import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  BarChart3,
  BookOpen,
  Brain,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Flame,
  GraduationCap,
  Home,
  Instagram,
  KeyRound,
  Library,
  LineChart as LineIcon,
  Lock,
  Mail,
  Menu,
  MessageSquare,
  Music,
  Network,
  Plus,
  Pencil,
  Radar,
  RefreshCw,
  Search,
  Save,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  TimerReset,
  Trash2,
  Trophy,
  User,
  X,
  Youtube
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar as RadarSeries,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { api, deleteJson, getStudentSession, postJson, setStudentSession } from './services/api';
import francisneyJornadaConcursos from './assets/francisney-jornada-concursos.jpg';
import PlanWizard from './components/plan/PlanWizard';
import { studyTechniques, studyTechniqueCategories } from './data/studyTechniques';
import { ALL_SUBJECTS_LABEL, type GeneratedStudyBlock, type PlanConfiguration, type PlanSubject, type PreviousSubjectPerformance, type SavedPlanVersion } from './types/plan';
import { calculateDisciplinePriorities } from './utils/disciplinePriority';

const AdminPanel = lazy(() => import('./components/AdminPanel'));
import {
  diagnosisQuestions,
  diagnosisResult,
  lifeWheelAreas,
  strategicQuestions,
  strategicScore
} from './utils/calculations';

const PROJECT_URL = 'https://francisneyliberato-estudante.pages.dev/';
const OFFICIAL_SITE_URL = 'https://www.francisney.com.br';
const WHATSAPP_URL =
  'https://wa.me/5565999031061?text=Ol%C3%A1%2C%20Francisney!%20Conheci%20a%20plataforma%20Estudante%20em%20Alta%20Performance%20e%20gostaria%20de%20saber%20mais.';
const STORAGE_PREFIX = 'francisneyliberato-estudante:';
const studyGoalOptions = [
  'Concurso Público',
  'ENEM',
  'OAB',
  'CFC',
  'Vestibular',
  'Graduação',
  'Pós-graduação',
  'Mestrado',
  'Doutorado',
  'Processo Seletivo',
  'Residência',
  'Certificação Profissional',
  'Curso Técnico',
  'Idiomas',
  'Reforço Escolar',
  'Desenvolvimento Profissional',
  'Atualização Profissional',
  'Outros'
];

type PdfMeta = {
  title: string;
  subtitle: string;
  fileName: string;
  orientation?: 'p' | 'l';
  participantName?: string;
  participantEmail?: string;
  participantWhatsapp?: string;
};

type Subject = {
  id: string;
  name: string;
  weight: number;
  questions: number;
  size: string;
  difficulty: string;
  current_mastery: number;
  target_mastery: number;
  priority?: number;
  recommended_percent?: number;
  recommended_hours?: number;
  color: string;
  notes?: string;
};

type TimeCycle = {
  study: number;
  rest: number;
};

type TimerNotice = {
  title: string;
  detail: string;
};

type ExamBoardListItem = {
  id: string;
  name: string;
  acronym: string;
  institution: string;
  scope: string;
  focus: string;
  updated_at: string;
  search_text?: string;
};

type ExamBoardProfile = {
  id: string;
  name: string;
  acronym: string;
  institution: string;
  officialSite: string;
  scope: string;
  operatesSince: string;
  focus: string;
  updatedAt: string;
  overview: string;
  evaluation: string[];
  questionFormat: string[];
  scoring: string[];
  style: string[];
  attentionPoints: string[];
  timeStrategy: string[];
  studyStrategy: string[];
  examDayStrategy: string[];
  sources: string[];
  disclaimer: string;
  officialSource?: {
    last_checked_at: string;
    last_success_at: string | null;
    status_code: number | null;
    page_title: string | null;
    page_description: string | null;
    changed_at: string | null;
  } | null;
};

function readStored<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

function readStoredValue<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: unknown) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
}

const TEST_DATA_STORAGE_KEYS = [
  'accessCode',
  'answers',
  'strategicAnswers',
  'selfActionPlan',
  'lifeActionPlan',
  'lifeValues',
  'matrixSwot',
  'matrixLossGain',
  'swotActionPlan',
  'lossGainActionPlan',
  'subjectForm',
  'disciplineWeights',
  'disciplineWeightForm',
  'weeklyPlans',
  'planForm',
  'planView',
  'weekStartDate',
  'weekEndDate',
  'planAssistant',
  'lastConfirmedPlanWeek',
  'studyWeeksTarget',
  'logForm',
  'studyLogs',
  'activeStudyBlockId',
  'studyElapsedSeconds',
  'studyStartedAt',
  'finishForm',
  'dashboardStartDate',
  'dashboardEndDate',
  'dashboardStartDateV2',
  'dashboardEndDateV2',
  'studyScheduleStartDate',
  'studyScheduleEndDate',
  'consultWhatsapp'
];

function clearTestDataStorage() {
  if (typeof window === 'undefined') return;
  TEST_DATA_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(`${STORAGE_PREFIX}${key}`));
}

function dateToIso(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(date: Date, daysToAdd: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + daysToAdd);
  return next;
}

function startOfCurrentWeek() {
  const today = new Date();
  return addDays(today, -today.getDay());
}

function calendarWeekKey(dateValue?: string) {
  const date = String(dateValue || '').slice(0, 10);
  if (!date) return '';
  const localDate = new Date(`${date}T12:00:00`);
  return dateToIso(addDays(localDate, -localDate.getDay()));
}

function eachDateInRange(startDate: Date, endDate: Date) {
  const dates: Date[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return dates;
  const safeEnd = end < start ? start : end;
  for (let date = start; date <= safeEnd; date = addDays(date, 1)) {
    dates.push(date);
    if (dates.length >= 31) break;
  }
  return dates;
}

function formatDateBr(dateLike: string | Date) {
  const date = typeof dateLike === 'string' ? new Date(`${dateLike}T12:00:00`) : dateLike;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatFullDateBr(dateLike: string | Date) {
  const date = typeof dateLike === 'string' ? new Date(`${dateLike}T12:00:00`) : dateLike;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function planEndTime(plan: WeeklyPlan) {
  if (plan.end_time) return plan.end_time;
  const [hours, minutes] = String(plan.start_time || '00:00').split(':').map(Number);
  const totalMinutes = Math.max(0, hours * 60 + minutes + Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0));
  return `${String(Math.floor((totalMinutes % 1440) / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

function normalizedActivityType(activityType?: string) {
  return String(activityType || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function activityRequiresQuestions(activityType?: string) {
  const normalized = normalizedActivityType(activityType);
  return normalized.startsWith('exercicio') || normalized.startsWith('simulado');
}

function isWritingActivity(activityType?: string) {
  const normalized = normalizedActivityType(activityType);
  return normalized === 'redacao' || normalized === 'redacao ou prova dissertativa';
}

function isCrossSubjectActivity(activityType?: string) {
  const normalized = normalizedActivityType(activityType);
  return normalized === 'simulado';
}

function planIsExpired(plan: WeeklyPlan, date: string, todayIso: string) {
  if (date < todayIso) return true;
  if (date > todayIso) return false;
  return new Date().getTime() > new Date(`${date}T${planEndTime(plan)}:00`).getTime();
}

function canStartScheduledBlock(plan: WeeklyPlan, todayIso: string) {
  const scheduledDate = String(plan.scheduled_date || todayIso).slice(0, 10);
  return !planIsExpired(plan, scheduledDate, todayIso);
}

function formatHoursFromMinutes(minutes: number) {
  const safe = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  if (!hours) return `${rest}min`;
  if (!rest) return `${hours}h`;
  return `${hours}h${String(rest).padStart(2, '0')}`;
}

function studyLogElapsedSeconds(log?: Pick<StudyLog, 'actual_seconds' | 'actual_minutes' | 'completed_hours'> | null) {
  const seconds = Math.round(Number(log?.actual_seconds || 0));
  if (seconds > 0) return seconds;
  const minutes = Number(log?.actual_minutes || 0);
  if (minutes > 0) return Math.round(minutes * 60);
  return Math.max(0, Math.round(Number(log?.completed_hours || 0) * 3600));
}

function formatStudyElapsed(seconds: number) {
  const safe = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const rest = safe % 60;
  if (hours) return `${hours}h${String(minutes).padStart(2, '0')}min${rest ? `${String(rest).padStart(2, '0')}s` : ''}`;
  if (minutes) return `${minutes}min${rest ? `${String(rest).padStart(2, '0')}s` : ''}`;
  return `${rest}s`;
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

type ChartTooltipEntry = { name?: string; dataKey?: string; value?: number | string; color?: string };

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipEntry[]; label?: string | number }) {
  if (!active || !payload?.length) return null;
  const labels: Record<string, string> = {
    hours: 'Horas estudadas',
    planejado: 'Planejado',
    executado: 'Executado',
    acerto: 'Percentual de acertos',
    accuracy: 'Percentual de acertos',
    foco: 'Foco',
    energia: 'Energia',
    value: 'Resultado'
  };
  const formatValue = (entry: ChartTooltipEntry) => {
    const key = String(entry.dataKey || entry.name || '').toLowerCase();
    const value = Number(entry.value || 0);
    if (key === 'hours') return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h`;
    if (key === 'acerto' || key === 'accuracy') return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  };
  return (
    <div className="max-w-[240px] rounded-lg border border-cyan-200/70 bg-slate-950 px-3 py-2.5 shadow-xl">
      {label !== undefined && label !== null && <p className="mb-1.5 break-words text-sm font-black text-white">{String(label)}</p>}
      <div className="space-y-1">
        {payload.map((entry, index) => {
          const key = String(entry.dataKey || entry.name || 'value');
          return <p key={`${key}-${index}`} className="text-sm font-semibold" style={{ color: entry.color || '#e2e8f0' }}>{labels[key] || entry.name || 'Resultado'}: {formatValue(entry)}</p>;
        })}
      </div>
    </div>
  );
}

function ChartSummary({ children }: { children: ReactNode }) {
  return <p className="chart-summary"><strong>Resumo do gráfico:</strong> {children}</p>;
}

function hoursTrendSummary(data: Array<{ date: string; hours: number }>) {
  if (!data.length) return 'Ainda não há horas registradas neste período.';
  if (data.length === 1) {
    const hours = data[0].hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return `Foram registradas ${hours} ${Number(data[0].hours) === 1 ? 'hora' : 'horas'} em ${data[0].date}.`;
  }
  const first = data[0];
  const last = data[data.length - 1];
  const movement = last.hours > first.hours ? 'cresceram' : last.hours < first.hours ? 'diminuíram' : 'permaneceram estáveis';
  return `No período exibido, as horas ${movement} de ${first.hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} para ${last.hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} horas entre ${first.date} e ${last.date}.`;
}

function leadingHoursSummary(data: Array<{ name: string; hours: number }>, noun = 'disciplina') {
  if (!data.length) return `Ainda não há ${noun === 'disciplina' ? 'horas por disciplina' : 'horas por atividade'} registradas neste período.`;
  const leader = [...data].sort((first, second) => second.hours - first.hours)[0];
  return `${leader.name} concentra ${leader.hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} hora${leader.hours === 1 ? '' : 's'} registradas e lidera este recorte.`;
}

function executionSummary(data: Array<{ planejado: number; executado: number }>) {
  const planned = data.reduce((sum, item) => sum + Number(item.planejado || 0), 0);
  const executed = data.reduce((sum, item) => sum + Number(item.executado || 0), 0);
  const percentage = planned ? Math.round((executed / planned) * 100) : 0;
  return `Foram planejadas ${planned} questões e executadas ${executed}, o que representa ${percentage}% da meta.`;
}

function accuracySummary(data: Array<{ name: string; acerto: number }>) {
  if (!data.length) return 'Ainda não há questões registradas para calcular o percentual de acertos.';
  const leader = [...data].sort((first, second) => second.acerto - first.acerto)[0];
  return `O melhor percentual atual é de ${leader.acerto}% em ${leader.name}.`;
}

function focusEnergySummary(data: Array<{ foco: number; energia: number }>) {
  if (!data.length) return 'Registre foco e energia ao concluir um estudo para acompanhar seu ritmo.';
  const focus = Math.round(data.reduce((sum, item) => sum + item.foco, 0) / data.length * 10) / 10;
  const energy = Math.round(data.reduce((sum, item) => sum + item.energia, 0) / data.length * 10) / 10;
  return `A média registrada é de ${focus}/10 em foco e ${energy}/10 em energia.`;
}

function dayNameFromDate(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' }).replace(/^\w/, (letter) => letter.toUpperCase());
}

function drawPdfBranding(pdf: jsPDF, pageNumber: number, totalPages: number, meta: PdfMeta) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString('pt-BR');

  pdf.setFillColor(7, 17, 31);
  pdf.rect(0, 0, pageWidth, 27, 'F');
  pdf.setDrawColor(250, 204, 21);
  pdf.setLineWidth(0.7);
  pdf.line(12, 27, pageWidth - 12, 27);

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('RELATÓRIO PERSONALIZADO DO ESTUDANTE', 12, 10);
  pdf.setFontSize(16);
  pdf.text(meta.title, 12, 18);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(203, 213, 225);
  pdf.text(`por Francisney Liberato | ${meta.subtitle}`, 12, 24);

  const participantLines = [
    meta.participantName ? `Participante: ${meta.participantName}` : '',
    meta.participantWhatsapp ? `WhatsApp informado: ${meta.participantWhatsapp}` : '',
    meta.participantEmail ? `E-mail: ${meta.participantEmail}` : '',
    `Gerado em: ${generatedAt}`
  ].filter(Boolean);

  pdf.setFontSize(7);
  participantLines.slice(0, 4).forEach((line, index) => {
    pdf.text(line, pageWidth - 12, 9 + index * 4, { align: 'right' });
  });

  pdf.setFillColor(7, 17, 31);
  pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  pdf.setDrawColor(250, 204, 21);
  pdf.line(12, pageHeight - 15, pageWidth - 12, pageHeight - 15);

  pdf.setFontSize(8);
  pdf.setTextColor(203, 213, 225);
  pdf.text(`Página ${pageNumber}/${totalPages}`, 12, pageHeight - 7);

  const footerY = pageHeight - 7;
  const footerLinks = [
    { text: 'francisneyliberato-estudante.pages.dev', url: PROJECT_URL },
    { text: 'www.francisney.com.br', url: OFFICIAL_SITE_URL },
    { text: '(65) 99903-1061', url: WHATSAPP_URL }
  ];

  pdf.setTextColor(56, 189, 248);
  let footerCursor = pageWidth - 12;
  [...footerLinks].reverse().forEach((link) => {
    const width = pdf.getTextWidth(link.text);
    pdf.text(link.text, footerCursor, footerY, { align: 'right' });
    pdf.link(footerCursor - width, footerY - 4, width, 5, { url: link.url });
    footerCursor -= width + 7;
  });
}

async function createBrandedPdfFromElement(element: HTMLElement, meta: PdfMeta) {
  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#07111f',
    useCORS: true,
    logging: false
  });
  const pdf = new jsPDF('p', 'mm', 'a4');
  pdf.setProperties({
    title: meta.title,
    subject: meta.subtitle,
    author: 'Francisney Liberato',
    creator: 'Francisney Liberato - Estudante em Alta Performance'
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 12;
  const headerHeight = 33;
  const footerHeight = 20;
  const contentWidth = pageWidth - marginX * 2;
  const contentHeight = pageHeight - headerHeight - footerHeight;
  const pxPerMm = canvas.width / contentWidth;
  const sliceHeightPx = Math.floor(contentHeight * pxPerMm);
  const rootBounds = element.getBoundingClientRect();
  const scaleY = rootBounds.height ? canvas.height / rootBounds.height : 1;
  const naturalBreaks = Array.from(element.children)
    .map((child) => Math.round((child.getBoundingClientRect().bottom - rootBounds.top) * scaleY))
    .filter((position) => position > 0 && position < canvas.height)
    .sort((first, second) => first - second);

  // Quebra as páginas entre os painéis principais do HTML, sem recortar cartões ou gráficos ao meio.
  const slices: Array<{ sourceY: number; height: number }> = [];
  let sourceY = 0;
  while (sourceY < canvas.height) {
    const maximumY = Math.min(sourceY + sliceHeightPx, canvas.height);
    const minimumUsefulY = sourceY + Math.min(sliceHeightPx * 0.45, canvas.height - sourceY);
    const pageBreak = naturalBreaks.filter((position) => position >= minimumUsefulY && position <= maximumY).pop();
    const nextY = pageBreak && pageBreak > sourceY ? pageBreak : maximumY;
    slices.push({ sourceY, height: Math.min(nextY - sourceY, canvas.height - sourceY) });
    sourceY = nextY;
  }
  const totalPages = slices.length;

  for (let page = 0; page < slices.length; page += 1) {
    if (page > 0) pdf.addPage();
    const { sourceY: sliceY, height: currentSliceHeight } = slices[page];
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = currentSliceHeight;
    const context = sliceCanvas.getContext('2d');
    if (!context) continue;
    context.drawImage(canvas, 0, sliceY, canvas.width, currentSliceHeight, 0, 0, canvas.width, currentSliceHeight);
    const imageHeightMm = currentSliceHeight / pxPerMm;
    pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', marginX, headerHeight, contentWidth, imageHeightMm);
    drawPdfBranding(pdf, page + 1, totalPages, meta);
  }

  return pdf;
}

async function saveBrandedPdfFromElement(element: HTMLElement, meta: PdfMeta) {
  const pdf = await createBrandedPdfFromElement(element, meta);
  pdf.save(meta.fileName);
}

type PdfReportSection = {
  title: string;
  text?: string;
  metrics?: Array<{ label: string; value: string }>;
  chart?: {
    title: string;
    color: [number, number, number];
    unit?: 'hours' | 'percent' | 'number';
    data: Array<{ label: string; value: number }>;
  };
  radar?: {
    title: string;
    color: [number, number, number];
    data: Array<{ label: string; value: number }>;
  };
  headers?: string[];
  widths?: number[];
  rows?: string[][];
};

function createProfessionalPdf(meta: PdfMeta, sections: PdfReportSection[]) {
  const pdf = new jsPDF(meta.orientation || 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 12;
  const contentWidth = pageWidth - marginX * 2;
  const contentBottom = pageHeight - 22;
  let y = 37;

  pdf.setProperties({ title: meta.title, subject: meta.subtitle, author: 'Francisney Liberato' });

  const nextPage = () => {
    pdf.addPage();
    y = 37;
  };
  const ensureSpace = (height: number) => {
    if (y + height > contentBottom) nextPage();
  };
  const drawSectionTitle = (title: string) => {
    ensureSpace(12);
    pdf.setFillColor(15, 35, 57);
    pdf.roundedRect(marginX, y, contentWidth, 9, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text(title, marginX + 4, y + 5.8);
    y += 12;
  };

  sections.forEach((section) => {
    const textLines = section.text
      ? pdf.splitTextToSize(section.text, contentWidth - 8) as string[]
      : [];
    const textHeight = textLines.length ? textLines.length * 4.5 + 7 : 0;
    // Mantem o cabecalho visual com o inicio do respectivo conteudo. Sem essa
    // reserva, um titulo poderia ficar no fim da pagina e o grafico ou tabela
    // iniciar apenas na pagina seguinte.
    const firstContentHeight = section.radar
      ? 94
      : section.chart
      ? 63
      : section.headers && section.widths && section.rows
        ? 10
        : section.metrics?.length
          ? 21
          : textHeight;
    ensureSpace(12 + textHeight + firstContentHeight);
    drawSectionTitle(section.title);

    if (section.text) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(51, 65, 85);
      ensureSpace(textHeight + 1);
      pdf.text(textLines, marginX + 4, y + 2);
      y += textHeight;
    }

    if (section.metrics?.length) {
      const cardGap = 4;
      const cardWidth = (contentWidth - cardGap) / 2;
      section.metrics.forEach((metric, index) => {
        if (index % 2 === 0) ensureSpace(20);
        const x = marginX + (index % 2) * (cardWidth + cardGap);
        pdf.setFillColor(241, 245, 249);
        pdf.setDrawColor(203, 213, 225);
        pdf.roundedRect(x, y, cardWidth, 17, 2, 2, 'FD');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(71, 85, 105);
        pdf.text(metric.label.toUpperCase(), x + 3, y + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(15, 35, 57);
        const valueLines = pdf.splitTextToSize(metric.value, cardWidth - 6) as string[];
        pdf.text(valueLines.slice(0, 2), x + 3, y + 11);
        if (index % 2 === 1 || index === section.metrics!.length - 1) y += 21;
      });
    }

    if (section.chart) {
      const chart = section.chart;
      const chartData = chart.data.filter((item) => Number.isFinite(item.value) && item.value >= 0).slice(0, 8);
      if (!chartData.length) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(9);
        pdf.setTextColor(71, 85, 105);
        pdf.text('Ainda não há dados suficientes para exibir este gráfico.', marginX + 4, y + 4);
        y += 10;
      } else {
        const chartHeight = 58;
        ensureSpace(chartHeight + 9);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8.5);
        pdf.setTextColor(15, 35, 57);
        pdf.text(chart.title, marginX + 3, y + 4);
        const chartX = marginX + 8;
        const chartY = y + 10;
        const chartWidth = contentWidth - 16;
        const plotHeight = 33;
        const maxValue = Math.max(...chartData.map((item) => item.value), 1);
        const barGap = 4;
        const barWidth = Math.max(8, (chartWidth - barGap * (chartData.length - 1)) / chartData.length);
        pdf.setDrawColor(203, 213, 225);
        pdf.setLineWidth(0.25);
        pdf.line(chartX, chartY + plotHeight, chartX + chartWidth, chartY + plotHeight);
        pdf.setDrawColor(226, 232, 240);
        [0.25, 0.5, 0.75].forEach((ratio) => pdf.line(chartX, chartY + plotHeight * (1 - ratio), chartX + chartWidth, chartY + plotHeight * (1 - ratio)));
        chartData.forEach((item, index) => {
          const height = Math.max(1, (item.value / maxValue) * plotHeight);
          const x = chartX + index * (barWidth + barGap);
          const valueText = chart.unit === 'hours'
            ? `${item.value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h`
            : chart.unit === 'percent'
              ? `${item.value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
              : item.value.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
          pdf.setFillColor(...chart.color);
          pdf.roundedRect(x, chartY + plotHeight - height, barWidth, height, 1.5, 1.5, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(6.5);
          pdf.setTextColor(15, 35, 57);
          pdf.text(valueText, x + barWidth / 2, chartY + plotHeight - height - 2, { align: 'center' });
          const label = item.label.length > 15 ? `${item.label.slice(0, 14)}…` : item.label;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6.4);
          pdf.setTextColor(51, 65, 85);
          const labelLines = pdf.splitTextToSize(label, barWidth + 4) as string[];
          pdf.text(labelLines.slice(0, 2), x + barWidth / 2, chartY + plotHeight + 4, { align: 'center' });
        });
        y += chartHeight + 5;
      }
    }

    if (section.radar) {
      const radar = section.radar;
      const radarData = radar.data.filter((item) => Number.isFinite(item.value)).slice(0, 12);
      if (!radarData.length) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(9);
        pdf.setTextColor(71, 85, 105);
        pdf.text('Ainda não há notas suficientes para exibir a Roda da Vida.', marginX + 4, y + 4);
        y += 10;
      } else {
        const chartHeight = 91;
        ensureSpace(chartHeight + 9);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8.5);
        pdf.setTextColor(15, 35, 57);
        pdf.text(radar.title, marginX + 3, y + 4);
        const centerX = marginX + contentWidth / 2;
        const centerY = y + 46;
        const radius = 31;
        const angleFor = (index: number) => -Math.PI / 2 + (index * Math.PI * 2) / radarData.length;
        const pointFor = (index: number, value: number) => {
          const angle = angleFor(index);
          const normalizedValue = Math.max(0, Math.min(10, value)) / 10;
          return { x: centerX + Math.cos(angle) * radius * normalizedValue, y: centerY + Math.sin(angle) * radius * normalizedValue };
        };

        pdf.setDrawColor(203, 213, 225);
        pdf.setLineWidth(0.25);
        [0.2, 0.4, 0.6, 0.8, 1].forEach((ratio) => pdf.circle(centerX, centerY, radius * ratio, 'S'));
        radarData.forEach((item, index) => {
          const outer = pointFor(index, 10);
          pdf.setDrawColor(203, 213, 225);
          pdf.line(centerX, centerY, outer.x, outer.y);
          const angle = angleFor(index);
          const labelX = centerX + Math.cos(angle) * (radius + 7);
          const labelY = centerY + Math.sin(angle) * (radius + 7) + 1.5;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6.3);
          pdf.setTextColor(51, 65, 85);
          pdf.text(item.label, labelX, labelY, { align: Math.cos(angle) > 0.25 ? 'left' : Math.cos(angle) < -0.25 ? 'right' : 'center' });
        });

        const points = radarData.map((item, index) => pointFor(index, item.value));
        pdf.setDrawColor(...radar.color);
        pdf.setLineWidth(0.8);
        points.forEach((point, index) => {
          const next = points[(index + 1) % points.length];
          pdf.line(point.x, point.y, next.x, next.y);
          pdf.setFillColor(...radar.color);
          pdf.circle(point.x, point.y, 1.25, 'F');
        });
        pdf.setFillColor(15, 35, 57);
        pdf.circle(centerX, centerY, 1.2, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(6.5);
        pdf.setTextColor(15, 35, 57);
        pdf.text('0', centerX + 2, centerY + 2);
        pdf.text('10', centerX + 2, centerY - radius + 2);
        y += chartHeight + 5;
      }
    }

    if (section.headers && section.widths && section.rows) {
      const widthsTotal = section.widths.reduce((sum, width) => sum + width, 0);
      const widths = section.widths.map((width) => (width / widthsTotal) * contentWidth);
      const drawTableHeader = () => {
        ensureSpace(10);
        let x = marginX;
        pdf.setFillColor(250, 204, 21);
        pdf.rect(marginX, y, contentWidth, 8, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(15, 23, 42);
        section.headers!.forEach((header, index) => {
          pdf.text(header, x + 2, y + 5);
          x += widths[index];
        });
        y += 8;
      };
      drawTableHeader();
      section.rows.forEach((row, rowIndex) => {
        const cells = row.map((value, index) => pdf.splitTextToSize(String(value || '-'), widths[index] - 4) as string[]);
        const rowHeight = Math.max(7, ...cells.map((lines) => lines.length * 3.4 + 3));
        if (y + rowHeight > contentBottom) {
          nextPage();
          drawTableHeader();
        }
        pdf.setFillColor(rowIndex % 2 === 0 ? 248 : 241, rowIndex % 2 === 0 ? 250 : 245, rowIndex % 2 === 0 ? 252 : 249);
        pdf.setDrawColor(203, 213, 225);
        pdf.rect(marginX, y, contentWidth, rowHeight, 'FD');
        let x = marginX;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        cells.forEach((lines, index) => {
          pdf.text(lines, x + 2, y + 4.3);
          x += widths[index];
        });
        y += rowHeight;
      });
      y += 5;
    }
  });

  const totalPages = pdf.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    drawPdfBranding(pdf, page, totalPages, meta);
  }
  return pdf;
}

type StudyLog = {
  id?: string;
  study_block_id?: string | null;
  plan_week_id?: string | null;
  date: string;
  subject_name: string;
  content: string;
  activity_type: string;
  planned_hours: number;
  completed_hours: number;
  planned_questions: number;
  completed_questions: number;
  correct_answers: number;
  actual_minutes?: number;
  actual_seconds?: number;
  questions_done?: number;
  accuracy_percentage?: number;
  perceived_difficulty?: string;
  is_extra?: boolean;
  started_at?: string;
  started_on_time?: boolean;
  finished_at?: string;
  review_done: boolean;
  simulation_done: boolean;
  essay_done: boolean;
  energy: number;
  focus: number;
  notes: string;
};

type WeeklyPlan = {
  id?: string;
  week_label: string;
  day: string;
  start_time: string;
  duration_hours: number;
  activity_type: string;
  subject_name: string;
  status: string;
  scheduled_date?: string;
  content?: string;
  planned_minutes?: number;
  planned_questions?: number;
  subject_weight?: number;
  notes?: string;
  recurrence?: string;
  priority?: string;
  rescheduled_from_id?: string;
  completed_at?: string;
  study_plan_id?: string;
  plan_week_id?: string;
  week_number?: number;
  end_time?: string;
  generation_source?: string;
  generation_key?: string;
  draft_status?: string;
  confirmed_at?: string;
};

type DisciplineWeight = {
  id: string;
  subject_name: string;
  exam_weight: number;
  question_weight: number;
  discipline_size: number;
  difficulty_level: number;
};

type SelfActionPlanItem = {
  id: string;
  task: string;
  activity: string;
  deadline: string;
  completed: boolean;
  focus_area?: string;
};

type Dashboard = {
  cards: Record<string, number | string>;
  charts: {
    hours_by_day: Array<{ date: string; hours: number }>;
    hours_by_subject: Array<{ name: string; hours: number }>;
  };
};

const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const calendarDays = ['DOMINGO', 'SEGUNDA', 'TERÇA', 'QUARTA', 'QUINTA', 'SEXTA', 'SÁBADO'];
const calendarHours = Array.from({ length: 18 }, (_, index) => {
  const start = index + 6;
  return `${start} / ${start + 1} h`;
});
const activities = ['Estudo', 'Exercício', 'Memorização/Revisão', 'Outros', 'Redação ou Prova Dissertativa', 'Simulado'];
const selfAssessmentScale = [
  { value: 0, label: 'Nunca', badge: '0' },
  { value: 1, label: 'Raramente', badge: '1' },
  { value: 2, label: 'Às vezes', badge: '2' },
  { value: 3, label: 'Muitas vezes', badge: '3' },
  { value: 4, label: 'Quase sempre', badge: '4' },
  { value: 5, label: 'Sempre', badge: '5' }
];
const statusOptions = ['Planejado', 'Feito', 'Parcialmente feito', 'Não feito'];
const timerSoundOptions = [
  { id: 'classico', label: 'Clássico', description: 'Toque limpo e objetivo para voltar ao foco.' },
  { id: 'energia', label: 'Energia', description: 'Som mais vibrante para despertar atenção.' },
  { id: 'suave', label: 'Suave', description: 'Aviso leve para quem prefere menos intensidade.' },
  { id: 'vitoria', label: 'Vitória', description: 'Sequência alegre para comemorar cada etapa.' }
];

const demoSubjects: Subject[] = [
  { id: 'demo-1', name: 'Direito Constitucional', weight: 1, questions: 15, size: 'alto', difficulty: 'alto', current_mastery: 35, target_mastery: 85, priority: 10.2, recommended_percent: 12.5, recommended_hours: 2.5, color: '#22d3ee' },
  { id: 'demo-2', name: 'AFO', weight: 1, questions: 15, size: 'alto', difficulty: 'medio', current_mastery: 30, target_mastery: 85, priority: 9.4, recommended_percent: 11.4, recommended_hours: 2.3, color: '#f59e0b' },
  { id: 'demo-3', name: 'Língua Portuguesa', weight: 1, questions: 14, size: 'medio', difficulty: 'medio', current_mastery: 55, target_mastery: 85, priority: 7.1, recommended_percent: 8.9, recommended_hours: 1.8, color: '#a78bfa' },
  { id: 'demo-4', name: 'Português', weight: 1, questions: 12, size: 'medio', difficulty: 'medio', current_mastery: 45, target_mastery: 85, priority: 8.6, recommended_percent: 10.2, recommended_hours: 2, color: '#38bdf8' },
  { id: 'demo-5', name: 'Matemática', weight: 1, questions: 12, size: 'alto', difficulty: 'alto', current_mastery: 32, target_mastery: 80, priority: 9.8, recommended_percent: 11.8, recommended_hours: 2.4, color: '#34d399' },
  { id: 'demo-6', name: 'Legislação', weight: 1, questions: 10, size: 'medio', difficulty: 'medio', current_mastery: 40, target_mastery: 80, priority: 8.2, recommended_percent: 9.5, recommended_hours: 1.9, color: '#facc15' },
  { id: 'demo-7', name: 'Contabilidade Pública', weight: 1, questions: 10, size: 'alto', difficulty: 'alto', current_mastery: 28, target_mastery: 80, priority: 9.6, recommended_percent: 11.2, recommended_hours: 2.2, color: '#fb7185' }
];

const socialLinks = [
  { name: 'Instagram', href: 'https://www.instagram.com/francisneyliberato/', color: '#fb7185' },
  { name: 'Facebook', href: 'https://www.facebook.com/people/Francisney-Liberato-Batista-Siqueira/pfbid09kXyi1eskgL3mQG65WebEUKXxbQUw1Qnh4Nuaap16FQ5HWJCy12J4LEzM2aHUyZ4l/', color: '#38bdf8' },
  { name: 'YouTube', href: 'https://www.youtube.com/results?search_query=Francisney+Liberato', color: '#ef4444' },
  { name: 'LinkedIn', href: 'https://br.linkedin.com/in/francisney-liberato-batista-siqueira-87871288', color: '#60a5fa' },
  { name: 'Kwai', href: 'https://www.kwai.com/search?keyword=Francisney%20Liberato', color: '#facc15' },
  { name: 'TikTok', href: 'https://www.tiktok.com/search?q=Francisney%20Liberato', color: '#34d399' },
  { name: 'WhatsApp', href: 'https://wa.me/5565999031061', color: '#22c55e' }
];

const navigationGroups = [
  {
    id: 'studies',
    label: 'ESTUDOS',
    markerClass: 'bg-cyan-300',
    titleClass: 'text-cyan-100',
    iconClass: 'text-cyan-200',
    activeClass: 'border-cyan-300 bg-cyan-300/15 text-white shadow-[inset_3px_0_0_rgba(34,211,238,0.95)]',
    primary: true
  },
  {
    id: 'development',
    label: 'DESENVOLVIMENTO',
    markerClass: 'bg-violet-300',
    titleClass: 'text-violet-100',
    iconClass: 'text-violet-200',
    activeClass: 'border-violet-300 bg-violet-300/15 text-white shadow-[inset_3px_0_0_rgba(196,181,253,0.95)]',
    primary: false
  },
  {
    id: 'about',
    label: 'CONHEÇA FRANCISNEY',
    markerClass: 'bg-amber-300',
    titleClass: 'text-amber-100',
    iconClass: 'text-amber-200',
    activeClass: 'border-amber-300 bg-amber-300/15 text-white shadow-[inset_3px_0_0_rgba(252,211,77,0.95)]',
    primary: false
  },
  {
    id: 'restricted',
    label: 'ÁREA RESTRITA',
    markerClass: 'bg-rose-300',
    titleClass: 'text-rose-100',
    iconClass: 'text-rose-200',
    activeClass: 'border-rose-300 bg-rose-300/15 text-white shadow-[inset_3px_0_0_rgba(253,164,175,0.95)]',
    primary: false
  }
] as const;

const navItems = [
  { id: 'inicio', label: 'Início', hash: '#inicio', icon: Home, group: 'studies' },
  { id: 'peso', label: 'Disciplinas e Prioridades', hash: '#peso', icon: Target, group: 'studies' },
  { id: 'plano', label: 'Plano de Estudos', hash: '#plano', icon: CalendarDays, group: 'studies' },
  { id: 'registro', label: 'Estudar Agora', hash: '#registro', icon: TimerReset, group: 'studies' },
  { id: 'dashboard', label: 'Meu Desempenho', hash: '#dashboard', icon: BarChart3, group: 'studies' },
  { id: 'geral', label: 'Painel da Comunidade', hash: '#geral', icon: Trophy, group: 'studies' },
  { id: 'consulta', label: 'Histórico de Estudos', hash: '#consulta', icon: FileText, group: 'studies' },
  { id: 'tempo', label: 'Gestão do Tempo', hash: '#tempo', icon: TimerReset, group: 'development' },
  { id: 'banca', label: 'Banca Examinadora', hash: '#banca', icon: Search, group: 'development' },
  { id: 'tecnicas', label: 'Técnicas de Estudo', hash: '#tecnicas', icon: BookOpen, group: 'development' },
  { id: 'autoavaliacao', label: 'Meu Diagnóstico', hash: '#autoavaliacao', icon: Brain, group: 'development' },
  { id: 'vida', label: 'Roda da Vida', hash: '#vida', icon: Radar, group: 'development' },
  { id: 'swot', label: 'SWOT / FOFA', hash: '#swot', icon: Network, group: 'development' },
  { id: 'ganhos', label: 'Matriz de Perdas e Ganhos', hash: '#ganhos', icon: Sparkles, group: 'development' },
  { id: 'livros', label: 'Livros', hash: '#livros', icon: Library, group: 'about' },
  { id: 'curriculo', label: 'Currículo', hash: '#curriculo', icon: User, group: 'about' },
  { id: 'redes', label: 'Redes Sociais', hash: '#redes', icon: Flame, group: 'about' },
  { id: 'ecos', label: 'Ecos da Graça', hash: '#ecos', icon: Music, group: 'about' },
  { id: 'editora', label: 'Editora Liberato', hash: '#editora', icon: Library, group: 'about' },
  { id: 'feedback', label: 'Sugestões de Melhoria', hash: '#feedback', icon: MessageSquare, group: 'about' },
  { id: 'admin', label: 'Administrador', hash: '#admin', icon: Lock, group: 'restricted' }
] as const;

const publicNavIds = new Set(['inicio', 'consulta', 'livros', 'curriculo', 'redes', 'ecos', 'editora', 'feedback', 'admin']);

function Field(props: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`grid gap-2 text-sm text-slate-300 ${props.className || ''}`}>
      <span>{props.label}</span>
      {props.children}
    </label>
  );
}

function inputClass() {
  return 'focus-ring w-full rounded-lg border border-slate-700 bg-slate-950/70 px-4 py-3 text-slate-100 placeholder:text-slate-500';
}

function Button(props: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'quiet' | 'danger' }) {
  const tone = props.tone || 'primary';
  const colors =
    tone === 'primary'
      ? 'bg-yellow-300 text-slate-950 hover:bg-yellow-200'
      : tone === 'danger'
        ? 'border border-rose-400/50 text-rose-100 hover:bg-rose-500/15'
        : 'border border-slate-500 bg-slate-900/80 text-slate-100 hover:bg-slate-800';
  return (
    <button
      {...props}
      className={`focus-ring inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-400 disabled:opacity-80 ${colors} ${props.className || ''}`}
    />
  );
}

function StatCard({ label, value, icon: Icon, color = '#22d3ee', className = '' }: { label: string; value: string | number; icon: typeof Home; color?: string; className?: string }) {
  return (
    <div className={`glass min-w-0 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-300">{label}</p>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <strong className="mt-2 block break-words text-2xl leading-tight text-white">{value}</strong>
    </div>
  );
}

function AutoSaveNote() {
  return null;
}

function ToolIntro({ title, text, examples = [] }: { title: string; text: string; examples?: string[] }) {
  return (
    <div className="mb-5 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4 text-slate-100">
      <h3 className="text-lg font-black text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-200">{text}</p>
      {examples.length > 0 && (
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {examples.map((example) => (
            <div key={example} className="rounded-lg border border-slate-700 bg-slate-950/55 p-3 text-sm leading-6 text-cyan-50">
              {example}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SabbathNote() {
  const references = [
    'Gênesis 2:2-3',
    'Êxodo 20:8-11',
    'Êxodo 31:13-17',
    'Deuteronômio 5:12-15',
    'Isaías 58:13-14',
    'Isaías 66:22-23',
    'Ezequiel 20:12',
    'Ezequiel 20:20',
    'Mateus 12:12',
    'Marcos 1:21',
    'Marcos 2:27-28',
    'Lucas 4:16',
    'Lucas 23:54-56',
    'Atos 13:14',
    'Atos 13:42-44',
    'Atos 16:13',
    'Atos 17:2',
    'Atos 18:4',
    'Apocalipse 7:2-3',
    'Apocalipse 9:4',
    'Apocalipse 12:17',
    'Apocalipse 13:16-17',
    'Apocalipse 14:1',
    'Apocalipse 14:12',
    'Apocalipse 20:4',
    'Apocalipse 22:14'
  ];
  return (
    <div className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-4 text-sm leading-6 text-yellow-50">
      <strong className="block text-base text-white">Nota sobre o sábado</strong>
      <p className="mt-2">
        A coluna de sábado fica livre para o estudante decidir se deseja estudar. Como sugestão espiritual, recomenda-se guardar o período de sexta ao pôr do sol até sábado ao pôr do sol como dia sagrado de Deus instituído na Bíblia.
      </p>
      <p className="mt-2 text-yellow-100">{references.join('; ')}.</p>
    </div>
  );
}

function StudyCalendarGrid({
  title,
  plans,
  logs,
  startDate
}: {
  title: string;
  plans: WeeklyPlan[];
  logs: StudyLog[];
  startDate: string;
}) {
  const weekStartDate = new Date(`${startDate}T12:00:00`);
  const dates = Array.from({ length: 7 }, (_, index) => dateToIso(addDays(weekStartDate, index)));
  const plannedByDayHour = plans.reduce<Record<string, WeeklyPlan[]>>((acc, plan) => {
    const date = String(plan.scheduled_date || '').slice(0, 10);
    const hour = Number(String(plan.start_time || '00:00').slice(0, 2));
    const key = `${date}-${Number.isFinite(hour) ? hour : 0}`;
    acc[key] = [...(acc[key] || []), plan];
    return acc;
  }, {});
  const plansById = new Map(plans.map((plan) => [String(plan.id || ''), plan]));
  const executedByDayHour = logs.reduce<Record<string, StudyLog[]>>((acc, log) => {
    const plannedBlock = plansById.get(String(log.study_block_id || ''));
    const date = String(plannedBlock?.scheduled_date || log.date || '').slice(0, 10);
    const hour = Number(plannedBlock?.start_time ? String(plannedBlock.start_time).slice(0, 2) : String(log.started_at || '').slice(11, 13) || 0);
    const key = `${date}-${Number.isFinite(hour) ? hour : 0}`;
    acc[key] = [...(acc[key] || []), log];
    return acc;
  }, {});

  return (
    <div className="grid min-w-0 gap-4">
      <div className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-4">
        <p className="text-sm font-black uppercase tracking-wider text-yellow-100">{title}</p>
        <p className="mt-1 text-sm leading-6 text-slate-200">
          Acompanhe cada dia em blocos verticais: horário, atividade, tempo planejado e tempo executado. Assim a leitura fica simples no celular, tablet e notebook.
        </p>
      </div>
      <div className="grid min-w-0 gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {dates.map((date, dayIndex) => {
          const dayEntries = calendarHours.map((label, hourIndex) => {
            const hour = hourIndex + 6;
            const plannedItems = plannedByDayHour[`${date}-${hour}`] || [];
            const executedItems = executedByDayHour[`${date}-${hour}`] || [];
            const plannedMinutes = plannedItems.reduce((sum, item) => sum + Number(item.planned_minutes || 0), 0);
            const executedSeconds = executedItems.reduce((sum, item) => sum + studyLogElapsedSeconds(item), 0);
            return { label, plannedItems, executedItems, plannedMinutes, executedSeconds };
          });
          const visibleEntries = dayEntries.filter((entry) => entry.plannedItems.length || entry.executedItems.length);
          const totalPlanned = dayEntries.reduce((sum, entry) => sum + entry.plannedMinutes, 0);
          const totalExecutedSeconds = dayEntries.reduce((sum, entry) => sum + entry.executedSeconds, 0);
          return (
            <div key={date} className="min-w-0 rounded-lg border border-slate-700 bg-slate-950/45 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-black uppercase tracking-wider text-yellow-100">{calendarDays[dayIndex]}</p>
                  <h4 className="mt-1 text-xl font-black text-white">{formatDateBr(new Date(`${date}T12:00:00`))}</h4>
                </div>
                <div className="text-right text-xs leading-5 text-slate-300">
                  <p><strong className="text-cyan-100">Planejado:</strong> {formatHoursFromMinutes(totalPlanned)}</p>
                  <p><strong className="text-emerald-100">Executado:</strong> {formatStudyElapsed(totalExecutedSeconds)}</p>
                </div>
              </div>
              <div className="mt-4 grid gap-2">
                {visibleEntries.length ? visibleEntries.map((entry) => (
                  <div key={entry.label} className="rounded-lg border border-slate-700 bg-slate-900/70 p-3">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400">{entry.label}</p>
                    <p className="mt-1 break-words text-sm font-bold text-white">
                      {entry.plannedItems.map((item) => item.subject_name || item.activity_type).join(', ') || 'Sem atividade planejada'}
                    </p>
                    <div className="mt-2 grid gap-2 text-xs text-slate-300 sm:grid-cols-2">
                      <span className="rounded-md bg-cyan-300/10 px-2 py-1 text-cyan-50">Planejado: {formatHoursFromMinutes(entry.plannedMinutes)}</span>
                      <span className="rounded-md bg-emerald-300/10 px-2 py-1 text-emerald-50">Executado: {formatStudyElapsed(entry.executedSeconds)}</span>
                    </div>
                  </div>
                )) : (
                  <p className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm text-slate-400">
                    Sem registros neste dia. Use esse espaço com intenção: descanso também pode fazer parte de um plano inteligente.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function App() {
  const defaultLead = { full_name: '', whatsapp: '', email: '', study_goal: '', study_goal_other: '', consent_lgpd: false };
  const defaultEditoraContact = { full_name: '', email: '', whatsapp: '', message: '', company: '', started_at: new Date().toISOString() };
  const defaultFeedbackContact = { full_name: '', email: '', whatsapp: '', message: '', company: '', started_at: new Date().toISOString() };
  const defaultLifeValues = Object.fromEntries(lifeWheelAreas.map((area) => [area, 6])) as Record<string, number>;
  const defaultSubjectForm: Omit<Subject, 'id'> = {
    name: '',
    weight: 1,
    questions: 10,
    size: 'medio',
    difficulty: 'medio',
    current_mastery: 40,
    target_mastery: 85,
    color: '#22d3ee',
    notes: ''
  };
  const defaultPlanForm: WeeklyPlan = {
    week_label: 'Semana atual',
    day: 'Segunda',
    start_time: '07:00',
    duration_hours: 1,
    activity_type: 'Estudo',
    subject_name: 'Direito Constitucional',
    status: 'Planejado',
    scheduled_date: dateToIso(new Date()),
    content: '',
    planned_minutes: 60,
    planned_questions: 20,
    subject_weight: 3,
    notes: '',
    recurrence: 'Não repetir',
    priority: 'Normal'
  };
  const defaultLogForm: StudyLog = {
    date: new Date().toISOString().slice(0, 10),
    subject_name: 'Direito Constitucional',
    content: '',
    activity_type: 'Exercício',
    planned_hours: 1,
    completed_hours: 1,
    planned_questions: 20,
    completed_questions: 20,
    correct_answers: 14,
    review_done: false,
    simulation_done: false,
    essay_done: false,
    energy: 7,
    focus: 7,
    notes: ''
  };
  const defaultFinishForm = { questions_done: 0, correct_answers: 0, perceived_difficulty: 'regular', notes: '', essay_score: '' };
  const defaultTimeCycles: TimeCycle[] = Array.from({ length: 4 }, () => ({ study: 25, rest: 5 }));
  const [active, setActive] = useState(() => readStoredValue('returningStudent', false) ? 'consulta' : 'inicio');
  const navigationReady = useRef(false);
  const restoringHistory = useRef(false);
  const shouldFocusPageTitle = useRef(false);
  const studyScheduleRef = useRef<HTMLDivElement>(null);
  const pageTitleRef = useRef<HTMLHeadingElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accessCode, setAccessCode] = useState(() => readStoredValue('accessCode', ''));
  const [leadPassword, setLeadPassword] = useState('');
  const [accessOk, setAccessOk] = useState(false);
  const [journeyRead, setJourneyRead] = useState(false);
  const [lead, setLead] = useState(() => readStored('lead', defaultLead));
  const [leadId, setLeadId] = useState(() => readStoredValue('leadId', ''));
  const [leadSaved, setLeadSaved] = useState(false);
  const [answers, setAnswers] = useState<number[]>(() => readStoredValue('answers', Array(10).fill(3)));
  const [strategicAnswers, setStrategicAnswers] = useState<string[]>(() => readStoredValue('strategicAnswers', Array(11).fill('Talvez')));
  const [selfActionPlan, setSelfActionPlan] = useState<SelfActionPlanItem[]>(() => readStoredValue('selfActionPlan', [
    { id: crypto.randomUUID(), task: 'Organizar minha rotina de estudos', activity: 'Definir dias, horários e blocos possíveis no Plano de Estudos.', deadline: dateToIso(addDays(new Date(), 3)), completed: false },
    { id: crypto.randomUUID(), task: 'Fortalecer minha disciplina prioritária', activity: 'Realizar um bloco de teoria e uma sequência de exercícios.', deadline: dateToIso(addDays(new Date(), 7)), completed: false },
    { id: crypto.randomUUID(), task: 'Revisar meu desempenho', activity: 'Registrar a execução e observar o Meu Desempenho para ajustar a semana.', deadline: dateToIso(addDays(new Date(), 10)), completed: false }
  ]));
  const [lifeActionPlan, setLifeActionPlan] = useState<SelfActionPlanItem[]>(() => readStoredValue('lifeActionPlan', [
    { id: crypto.randomUUID(), task: 'Cuidar da área com menor nota', activity: 'Escolher uma ação simples para elevar a área mais baixa da Roda da Vida.', deadline: dateToIso(addDays(new Date(), 7)), completed: false },
    { id: crypto.randomUUID(), task: 'Proteger minha energia de estudo', activity: 'Definir horário de sono, pausa e início do estudo para a semana.', deadline: dateToIso(addDays(new Date(), 5)), completed: false },
    { id: crypto.randomUUID(), task: 'Revisar meu equilíbrio semanal', activity: 'Atualizar a Roda da Vida e observar o que melhorou.', deadline: dateToIso(addDays(new Date(), 14)), completed: false }
  ]));
  const [subjects, setSubjects] = useState<Subject[]>(demoSubjects);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [weeklyPlans, setWeeklyPlans] = useState<WeeklyPlan[]>(() => readStoredValue('weeklyPlans', []));
  const [studyLogs, setStudyLogs] = useState<StudyLog[]>(() => readStoredValue('studyLogs', []));
  const [lifeValues, setLifeValues] = useState<Record<string, number>>(() => readStored('lifeValues', defaultLifeValues));
  const [consultWhatsapp, setConsultWhatsapp] = useState(() => readStoredValue('consultWhatsapp', ''));
  const [consultPassword, setConsultPassword] = useState('');
  const [consulting, setConsulting] = useState(false);
  const [consultFound, setConsultFound] = useState(false);
  const [editoraContact, setEditoraContact] = useState(() => readStored('editoraContact', defaultEditoraContact));
  const [feedbackContact, setFeedbackContact] = useState(() => readStored('feedbackContact', defaultFeedbackContact));
  const [editoraSending, setEditoraSending] = useState(false);
  const [editoraStatus, setEditoraStatus] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });
  const [editoraLastSignature, setEditoraLastSignature] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });
  const [feedbackLastSignature, setFeedbackLastSignature] = useState('');
  const [planView, setPlanView] = useState<'list' | 'calendar'>(() => readStoredValue('planView', 'list'));
  const [planAssistantSnapshot, setPlanAssistantSnapshot] = useState<PlanConfiguration | null>(() => readStoredValue('planAssistant', null));
  const [savedPlanVersions, setSavedPlanVersions] = useState<SavedPlanVersion[]>([]);
  const [studyWeeksTarget, setStudyWeeksTarget] = useState(() => readStoredValue('studyWeeksTarget', 12));
  const [dashboardStartDate, setDashboardStartDate] = useState(() => readStoredValue('dashboardStartDateV2', ''));
  const [dashboardEndDate, setDashboardEndDate] = useState(() => readStoredValue('dashboardEndDateV2', ''));
  const [studyScheduleStartDate, setStudyScheduleStartDate] = useState(() => readStoredValue('studyScheduleStartDate', ''));
  const [studyScheduleEndDate, setStudyScheduleEndDate] = useState(() => readStoredValue('studyScheduleEndDate', ''));
  const [disciplineWeights, setDisciplineWeights] = useState<DisciplineWeight[]>(() => readStoredValue('disciplineWeights', []));
  const [disciplineWeightForm, setDisciplineWeightForm] = useState<DisciplineWeight>(() => {
    const saved = readStored<DisciplineWeight>('disciplineWeightForm', {
      id: '', subject_name: '', exam_weight: 0, question_weight: 0, discipline_size: 1, difficulty_level: 1
    });
    return saved.subject_name.trim() ? saved : { ...saved, exam_weight: 0, question_weight: 0 };
  });
  const [weekStartDate, setWeekStartDate] = useState(() => readStoredValue('weekStartDate', dateToIso(startOfCurrentWeek())));
  const [weekEndDate, setWeekEndDate] = useState(() => readStoredValue('weekEndDate', dateToIso(addDays(startOfCurrentWeek(), 6))));
  const [activeStudyBlockId, setActiveStudyBlockId] = useState<string | null>(() => readStoredValue('activeStudyBlockId', null));
  const [studyTimerRunning, setStudyTimerRunning] = useState(false);
  const [studyElapsedSeconds, setStudyElapsedSeconds] = useState(() => readStoredValue('studyElapsedSeconds', 0));
  const [studyStartedAt, setStudyStartedAt] = useState<string | null>(() => readStoredValue('studyStartedAt', null));
  const [finishForm, setFinishForm] = useState(() => readStored('finishForm', defaultFinishForm));
  const [showExtraStudy, setShowExtraStudy] = useState(false);
  const [pendingRescheduleSlots, setPendingRescheduleSlots] = useState<Record<string, { date: string; time: string }>>({});
  const [retroactiveExecutionDrafts, setRetroactiveExecutionDrafts] = useState<Record<string, { minutes: number; questions: number; correct: number }>>({});
  const [studyViewDate, setStudyViewDate] = useState(() => dateToIso(new Date()));
  const [timeCycles, setTimeCycles] = useState<TimeCycle[]>(() => readStoredValue('timeCycles', defaultTimeCycles));
  const [timeStep, setTimeStep] = useState(0);
  const [timeRunning, setTimeRunning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(() => defaultTimeCycles[0].study * 60);
  const [timeEndsAt, setTimeEndsAt] = useState<number | null>(null);
  const [timeCompleted, setTimeCompleted] = useState(0);
  const [timerSound, setTimerSound] = useState(() => readStoredValue('timerSound', 'energia'));
  const [timerNotice, setTimerNotice] = useState<TimerNotice | null>(null);
  const [examBoards, setExamBoards] = useState<ExamBoardListItem[]>([]);
  const [examBoardSearch, setExamBoardSearch] = useState('');
  const [selectedExamBoard, setSelectedExamBoard] = useState<ExamBoardProfile | null>(null);
  const [examBoardLoading, setExamBoardLoading] = useState(false);
  const [examBoardError, setExamBoardError] = useState('');
  const [message, setMessage] = useState('');
  const returningStudentNotice = 'Cadastro localizado. Consulte seus planos e registros antes de escolher como deseja continuar.';
  const resultRef = useRef<HTMLDivElement>(null);
  const lifeRef = useRef<HTMLDivElement>(null);
  const planPdfRef = useRef<HTMLDivElement>(null);
  const progressPdfRef = useRef<HTMLDivElement>(null);
  const timerAudioRef = useRef<AudioContext | null>(null);
  const timerHandledStepRef = useRef<number | null>(null);
  const timerSoundStopsRef = useRef<Array<() => void>>([]);
  const studyRuntimeRef = useRef<{ block: WeeklyPlan | null; elapsedSeconds: number; running: boolean; startedAt: string | null; finish: typeof defaultFinishForm; capturedAt: number }>({
    block: null,
    elapsedSeconds: 0,
    running: false,
    startedAt: null,
    finish: defaultFinishForm,
    capturedAt: Date.now()
  });
  const autoSavingStudyRef = useRef(false);

  useEffect(() => {
    if (active !== 'consulta' && message === returningStudentNotice) {
      setMessage('');
    }
  }, [active, message, returningStudentNotice]);

  const result = useMemo(() => diagnosisResult(answers), [answers]);
  const strategicTotal = useMemo(() => strategicScore(strategicAnswers), [strategicAnswers]);
  const lifeLabelMap: Record<string, string> = {
    'Saúde e disposição': 'Saúde',
    'Desenvolvimento intelectual': 'Intelecto',
    'Equilíbrio emocional': 'Emocional',
    'Realização e propósito': 'Propósito',
    'Recursos financeiros': 'Recursos',
    'Contribuição social': 'Contribuição',
    'Família': 'Família',
    'Desenvolvimento amoroso': 'Amor',
    'Vida social': 'Social',
    'Criatividade, hobbies e diversão': 'Diversão',
    'Plenitude e felicidade': 'Felicidade',
    'Espiritualidade': 'Espiritual'
  };
  const lifeData = useMemo(() => lifeWheelAreas.map((area) => ({ area, label: lifeLabelMap[area] || area, nota: Number(lifeValues[area] || 0) })), [lifeValues]);
  const lifePriorityAreas = useMemo(
    () => [...lifeData].sort((first, second) => first.nota - second.nota || first.area.localeCompare(second.area, 'pt-BR')),
    [lifeData]
  );
  const latestSavedPlanSnapshot = useMemo(() => {
    const confirmedVersion = savedPlanVersions.find((version) => version.status === 'confirmed' && Array.isArray(version.snapshot?.blocks));
    if (confirmedVersion?.snapshot) return confirmedVersion.snapshot;
    return planAssistantSnapshot?.status === 'confirmed' ? { config: planAssistantSnapshot, blocks: [] } : undefined;
  }, [savedPlanVersions, planAssistantSnapshot]);
  const currentPlanSubjects = useMemo(() => {
    const configured = latestSavedPlanSnapshot?.config?.subjects || planAssistantSnapshot?.subjects;
    if (Array.isArray(configured)) {
      const selected = configured
        .filter((subject): subject is Record<string, unknown> => Boolean(subject) && typeof subject === 'object')
        .filter((subject) => subject.selected !== false && typeof subject.name === 'string' && subject.name.trim())
        .map((subject) => ({
          name: String(subject.name),
          percent: Number(subject.percent || 0),
          current_mastery: Number(subject.previousAccuracy || 0)
        }));
      if (selected.length) return selected.sort((first, second) => second.percent - first.percent || first.name.localeCompare(second.name));
    }
    const blocks = latestSavedPlanSnapshot?.blocks || [];
    const names = [...new Set(blocks.map((block) => String(block.subject_name || block.subjectName || '')).filter(Boolean))];
    if (names.length) return names.map((name) => ({ name, percent: 0, current_mastery: 0 }));
    return calculateDisciplinePriorities(disciplineWeights)
      .sort((first, second) => second.total - first.total || first.subject_name.localeCompare(second.subject_name))
      .map((subject) => ({ name: subject.subject_name, percent: subject.percent, current_mastery: 0 }));
  }, [latestSavedPlanSnapshot, planAssistantSnapshot, disciplineWeights]);
  const coachSuggestions = useMemo(() => {
    const cards = dashboard?.cards || {};
    const accuracy = Number(cards.accuracy || 0);
    const potential = Number(cards.potential || 0);
    const planned = Number(cards.planned_hours || 0);
    const completed = Number(cards.completed_hours || 0);
    return [
      accuracy < 70
        ? 'Acertos abaixo de 70%: reduza a leitura passiva e aumente a correção comentada de questões.'
        : 'Acertos em boa rota: mantenha questões cronometradas e suba a dificuldade aos poucos.',
      completed < planned
        ? 'Execução abaixo do planejado: reprograme blocos menores e proteja os horários de maior energia.'
        : 'Execução aderente ao plano: avance para revisões espaçadas e simulados semanais.',
      potential < 75
        ? 'Potencial ainda instável: escolha uma disciplina prioritária e conclua uma Missão de Estudo com 30 questões hoje.'
        : 'Potencial consistente: use o Meu Desempenho para decidir onde investir a próxima hora de estudo.'
    ];
  }, [dashboard]);
  const disciplineWeightResults = useMemo(() => calculateDisciplinePriorities(disciplineWeights)
    .sort((a, b) => b.total - a.total || a.subject_name.localeCompare(b.subject_name)), [disciplineWeights]);
  const hasDisciplineWeightSetup = disciplineWeights.length > 0;
  const planAssistantSubjects = useMemo<PlanSubject[]>(() => disciplineWeightResults.map((item, index) => {
    const subjectLogs = studyLogs.filter((log) => log.subject_name === item.subject_name);
    const questions = subjectLogs.reduce((sum, log) => sum + Number(log.questions_done ?? log.completed_questions ?? 0), 0);
    const correct = subjectLogs.reduce((sum, log) => sum + Number(log.correct_answers || 0), 0);
    return {
      id: item.id,
      name: item.subject_name,
      weight: item.total,
      percent: item.percent,
      suggestedHours: item.cycle_hours,
      color: ['#22d3ee', '#34d399', '#facc15', '#fb7185', '#a78bfa', '#38bdf8'][index % 6],
      selected: true,
      previousAccuracy: questions ? Math.round((correct / questions) * 100) : undefined
    };
  }), [disciplineWeightResults, studyLogs]);
  const planPreviousPerformance = useMemo<PreviousSubjectPerformance[]>(() => planAssistantSubjects.map((subject) => {
    const logs = studyLogs.filter((log) => log.subject_name === subject.name);
    const plans = weeklyPlans.filter((plan) => plan.subject_name === subject.name);
    const questions = logs.reduce((sum, log) => sum + Number(log.questions_done ?? log.completed_questions ?? 0), 0);
    const correct = logs.reduce((sum, log) => sum + Number(log.correct_answers || 0), 0);
    const completed = plans.filter((plan) => ['concluído', 'parcial'].includes(String(plan.status).toLowerCase())).length;
    const lastDate = logs.map((log) => String(log.date || '')).filter(Boolean).sort().pop();
    return {
      subjectName: subject.name,
      accuracy: questions ? Math.round((correct / questions) * 100) : 70,
      completion: plans.length ? Math.round((completed / plans.length) * 100) : 80,
      pendingEssential: plans.filter((plan) => ['não realizado', 'Planejado'].includes(String(plan.status))).length,
      daysSinceLastStudy: lastDate ? Math.max(0, Math.round((new Date().getTime() - new Date(`${lastDate}T12:00:00`).getTime()) / 86400000)) : 30
    };
  }), [planAssistantSubjects, studyLogs, weeklyPlans]);
  const [subjectForm, setSubjectForm] = useState<Omit<Subject, 'id'>>(() => readStored('subjectForm', defaultSubjectForm));
  const [planForm, setPlanForm] = useState<WeeklyPlan>(() => readStored('planForm', defaultPlanForm));
  const [logForm, setLogForm] = useState<StudyLog>(() => readStored('logForm', defaultLogForm));
  const studyGoalReady = Boolean(lead.study_goal && (lead.study_goal !== 'Outros' || lead.study_goal_other.trim().length >= 3));
  const consultMatchesLead = consultWhatsapp.replace(/\D/g, '').length >= 8 && lead.whatsapp.replace(/\D/g, '') === consultWhatsapp.replace(/\D/g, '');
  const savedSummary = useMemo(() => ({
    lifeFilled: Object.values(lifeValues).filter((value) => Number(value) !== 6).length,
    plans: weeklyPlans.length + (planForm.subject_name || planForm.week_label ? 1 : 0),
    logs: studyLogs.length + (logForm.subject_name || logForm.content ? 1 : 0),
    subjectReady: Boolean(subjectForm.name || subjectForm.questions),
    leadReady: Boolean(lead.full_name || lead.whatsapp || lead.email || lead.study_goal)
  }), [lifeValues, weeklyPlans.length, planForm, studyLogs.length, logForm, subjectForm, lead]);
  const currentCycleIndex = Math.floor(timeStep / 2);
  const currentMode = timeStep % 2 === 0 ? 'study' : 'rest';
  const currentCycle = timeCycles[currentCycleIndex] || timeCycles[0] || { study: 25, rest: 5 };
  const totalTimeSeconds = Math.max(1, Number(currentCycle[currentMode] || 1)) * 60;
  const timerProgress = Math.min(100, Math.max(0, ((totalTimeSeconds - timeRemaining) / totalTimeSeconds) * 100));
  const formatTimer = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  };
  const todayIso = dateToIso(new Date());
  const weekStart = new Date(`${weekStartDate || todayIso}T12:00:00`);
  const weekEnd = new Date(`${weekEndDate || weekStartDate || todayIso}T12:00:00`);
  const effectiveWeekEnd = weekEnd < weekStart ? weekStart : weekEnd;
  const selectedPeriodDates = eachDateInRange(weekStart, effectiveWeekEnd);
  const weekLabel = `${formatDateBr(weekStart)} a ${formatDateBr(effectiveWeekEnd)}`;
  const selectedWeekNumber = Math.min(Math.max(1, Math.ceil(selectedPeriodDates.length / 7)), Math.max(1, Number(studyWeeksTarget || 1)));
  const normalizedPlans = weeklyPlans.map((plan) => ({
    ...plan,
    scheduled_date: plan.scheduled_date || todayIso,
    planned_minutes: Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 60),
    content: plan.content || '',
    planned_questions: Number(plan.planned_questions || 0)
  }));
  // As versões em PDF são históricas. A agenda considera todas as semanas confirmadas
  // do plano ativo, inclusive em consultas a períodos passados ou futuros.
  const latestConfirmedPlanVersion = savedPlanVersions.find((version) => version.status === 'confirmed' && Array.isArray(version.snapshot?.blocks));
  const activeStudyPlanId = String(latestConfirmedPlanVersion?.studyPlanId || (planAssistantSnapshot?.status === 'confirmed' ? planAssistantSnapshot.id : '') || '');
  const latestConfirmationByWeek = normalizedPlans.reduce((latest, plan) => {
    if (plan.draft_status === 'draft') return latest;
    const planKey = String(plan.study_plan_id || 'legado');
    // A semana de calendário evita que IDs técnicos de gerações diferentes
    // misturem ou ocultem blocos ao filtrar o Meu Desempenho por datas.
    const weekKey = String(calendarWeekKey(plan.scheduled_date) || plan.plan_week_id || plan.week_number || plan.week_label || 'semana');
    const key = `${planKey}:${weekKey}`;
    const currentTime = String(plan.confirmed_at || (plan as WeeklyPlan & { updated_at?: string }).updated_at || '');
    const generationKey = String(plan.generation_key || currentTime);
    const previous = latest.get(key);
    if (!previous || currentTime >= previous.confirmedAt) latest.set(key, { confirmedAt: currentTime, generationKey });
    return latest;
  }, new Map<string, { confirmedAt: string; generationKey: string }>());
  const allConfirmedPlans = normalizedPlans.filter((plan) => {
    if (plan.draft_status === 'draft') return false;
    const planKey = String(plan.study_plan_id || 'legado');
    const weekKey = String(calendarWeekKey(plan.scheduled_date) || plan.plan_week_id || plan.week_number || plan.week_label || 'semana');
    const confirmedAt = String(plan.confirmed_at || (plan as WeeklyPlan & { updated_at?: string }).updated_at || '');
    const latest = latestConfirmationByWeek.get(`${planKey}:${weekKey}`);
    return latest?.generationKey === String(plan.generation_key || confirmedAt);
  });
  // A versao ativa e a referencia principal. Se um salvamento antigo nao trouxer
  // o mesmo identificador dos blocos ja confirmados, preservamos a agenda em vez
  // de ocultar estudos e execucoes que continuam validos para o estudante.
  const activeConfirmedPlans = allConfirmedPlans.filter((plan) => !activeStudyPlanId || String(plan.study_plan_id || '') === activeStudyPlanId);
  const recoverablePlans = normalizedPlans.filter((plan) => !['rascunho', 'cancelado', 'não realizado'].includes(String(plan.status || '').toLowerCase()));
  const confirmedPlans = activeConfirmedPlans.length
    ? activeConfirmedPlans
    : allConfirmedPlans.length
      ? allConfirmedPlans
      : recoverablePlans;
  const plansForPerformance = allConfirmedPlans.length ? allConfirmedPlans : recoverablePlans;
  const planAssistantExistingBlocks = normalizedPlans
    .filter((plan) => {
      if (!plan.generation_key || (!planAssistantSnapshot || Number(plan.week_number || 1) !== planAssistantSnapshot.currentWeek)) return false;
      return !activeStudyPlanId || String(plan.study_plan_id || '') === activeStudyPlanId;
    })
    .map<GeneratedStudyBlock>((plan) => {
      const activity = plan.activity_type === 'Memorização/Revisão' ? 'review' : plan.activity_type === 'Exercício' ? 'exercises' : plan.activity_type === 'Simulado' ? 'simulation' : isWritingActivity(plan.activity_type) ? 'writing' : 'theory';
      return {
        id: String(plan.id),
        planId: String(plan.study_plan_id || ''),
        weekNumber: Number(plan.week_number || 1),
        scheduledDate: String(plan.scheduled_date || todayIso),
        weekday: new Date(`${plan.scheduled_date || todayIso}T12:00:00`).getDay(),
        startTime: plan.start_time,
        endTime: plan.end_time || plan.start_time,
        durationMinutes: Number(plan.planned_minutes || 0),
        activity,
        activityLabel: plan.activity_type,
        subjectId: undefined,
        subjectName: plan.subject_name,
        priority: plan.priority || 'Média',
        estimatedQuestions: Number(plan.planned_questions || 0),
        status: plan.draft_status === 'draft' ? 'Rascunho' : 'Planejado',
        source: plan.generation_source === 'recalculated' ? 'recalculated' : plan.generation_source === 'copied' ? 'copied' : plan.generation_source === 'manual' ? 'manual' : 'generated',
        generationKey: String(plan.generation_key)
      };
    });
  const normalizedPlansInPeriod = normalizedPlans.filter((plan) => selectedPeriodDates.some((date) => dateToIso(date) === plan.scheduled_date));
  const studyLogsInSelectedPeriod = studyLogs.filter((log) => selectedPeriodDates.some((date) => dateToIso(date) === String(log.date || '').slice(0, 10)));
  const plannedMinutesWeek = normalizedPlansInPeriod.reduce((sum, plan) => sum + Number(plan.planned_minutes || 0), 0);
  const automaticWeekGoalLabel = plannedMinutesWeek ? formatHoursFromMinutes(plannedMinutesWeek) : 'Adicione blocos';
  const executedSecondsByBlock = studyLogs.reduce<Map<string, number>>((totals, log) => {
    const blockId = String(log.study_block_id || '');
    if (blockId) totals.set(blockId, studyLogElapsedSeconds(log));
    return totals;
  }, new Map());
  const completedStudyBlockIds = new Set(normalizedPlans
    .filter((plan) => (executedSecondsByBlock.get(String(plan.id || '')) || 0) >= Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0) * 60)
    .map((plan) => String(plan.id || ''))
    .filter(Boolean));
  const pendingStudyPlans = confirmedPlans
    .filter((plan) => {
      const scheduledDate = String(plan.scheduled_date || '').slice(0, 10);
      const status = String(plan.status || '').toLowerCase();
      const plannedSeconds = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0) * 60;
      const executedSeconds = executedSecondsByBlock.get(String(plan.id || '')) || 0;
      return Boolean(scheduledDate) && scheduledDate < todayIso && executedSeconds < plannedSeconds && !['rascunho', 'concluído', 'reagendado', 'cancelado'].includes(status);
    })
    .sort((first, second) => `${first.scheduled_date || ''}${first.start_time || ''}`.localeCompare(`${second.scheduled_date || ''}${second.start_time || ''}`));
  const pendingStudyMinutes = pendingStudyPlans.reduce((sum, plan) => {
    const plannedSeconds = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0) * 60;
    const executedSeconds = executedSecondsByBlock.get(String(plan.id || '')) || 0;
    return sum + Math.max(0, Math.ceil((plannedSeconds - executedSeconds) / 60));
  }, 0);
  const selectedStudyPlans = confirmedPlans
    .filter((plan) => String(plan.scheduled_date || '').slice(0, 10) === studyViewDate && !['rascunho', 'reagendado', 'cancelado', 'não realizado'].includes(String(plan.status || '').toLowerCase()))
    .sort((first, second) => String(first.start_time || '').localeCompare(String(second.start_time || '')));
  const selectedStudyLogs = studyLogs.filter((log) => String(log.date || '').slice(0, 10) === studyViewDate || selectedStudyPlans.some((plan) => String(plan.id || '') === String(log.study_block_id || '')));
  const selectedStudySchedule = selectedStudyPlans.map((plan) => {
    const execution = selectedStudyLogs.find((log) => String(log.study_block_id || '') === String(plan.id || ''));
    const plannedMinutes = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0);
    const actualSeconds = studyLogElapsedSeconds(execution);
    const executionPercent = plannedMinutes ? Math.min(100, Math.round((actualSeconds / (plannedMinutes * 60)) * 100)) : 0;
    const completed = actualSeconds >= plannedMinutes * 60;
    const partial = Boolean(execution) && !completed;
    return { plan, execution, plannedMinutes, actualSeconds, executionPercent, completed, partial, expired: planIsExpired(plan, studyViewDate, todayIso) };
  });
  const selectedExtraStudyLogs = selectedStudyLogs.filter((log) => !log.study_block_id || !selectedStudyPlans.some((plan) => String(plan.id || '') === String(log.study_block_id || '')));
  const selectStudyDay = (date: string) => {
    setStudyViewDate(date);
    window.setTimeout(() => studyScheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };
  const currentStudyWeekStart = startOfCurrentWeek();
  const defaultStudyScheduleStart = dateToIso(currentStudyWeekStart);
  const defaultStudyScheduleEnd = dateToIso(addDays(currentStudyWeekStart, 6));
  const hasStudyScheduleFilter = Boolean(studyScheduleStartDate || studyScheduleEndDate);
  const studyScheduleStart = studyScheduleStartDate || (studyScheduleEndDate ? dateToIso(addDays(new Date(`${studyScheduleEndDate}T12:00:00`), -6)) : defaultStudyScheduleStart);
  const studyScheduleEnd = studyScheduleEndDate || (studyScheduleStartDate ? dateToIso(addDays(new Date(`${studyScheduleStartDate}T12:00:00`), 6)) : defaultStudyScheduleEnd);
  const safeStudyScheduleEnd = studyScheduleEnd < studyScheduleStart ? studyScheduleStart : studyScheduleEnd;
  const studyScheduleDates = eachDateInRange(new Date(`${studyScheduleStart}T12:00:00`), new Date(`${safeStudyScheduleEnd}T12:00:00`));
  useEffect(() => {
    if (active === 'registro' && !hasStudyScheduleFilter) setStudyViewDate(todayIso);
  }, [active, hasStudyScheduleFilter, todayIso]);
  const studyWeekOverview = studyScheduleDates.map((scheduleDate) => {
    const date = dateToIso(scheduleDate);
    const plans = confirmedPlans.filter((plan) => String(plan.scheduled_date || '').slice(0, 10) === date && !['rascunho', 'reagendado', 'cancelado'].includes(String(plan.status || '').toLowerCase()));
    const planIds = new Set(plans.map((plan) => String(plan.id || '')).filter(Boolean));
    const logs = studyLogs.filter((log) => String(log.date || '').slice(0, 10) === date || planIds.has(String(log.study_block_id || '')));
    const plannedMinutes = plans.reduce((sum, plan) => sum + Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0), 0);
    const executedMinutes = logs.reduce((sum, log) => sum + studyLogElapsedSeconds(log) / 60, 0);
    return { date, plans: plans.length, plannedMinutes, executedMinutes, percent: plannedMinutes ? Math.min(100, Math.round((executedMinutes / plannedMinutes) * 100)) : 0 };
  });
  const historyReady = consultFound || leadSaved;
  const participantFirstName = lead.full_name.trim().split(/\s+/)[0] || 'participante';
  const greeting = new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite';
  const nextStudyMission = confirmedPlans
    .filter((plan) => !completedStudyBlockIds.has(String(plan.id || '')) && !['rascunho', 'reagendado', 'cancelado', 'concluído', 'concluido'].includes(String(plan.status || '').toLowerCase()) && String(plan.scheduled_date || '').slice(0, 10) >= todayIso)
    .sort((first, second) => `${first.scheduled_date || ''}${first.start_time || ''}`.localeCompare(`${second.scheduled_date || ''}${second.start_time || ''}`))[0];
  const historyWeeklyPlannedMinutes = studyWeekOverview.reduce((sum, day) => sum + day.plannedMinutes, 0);
  const historyWeeklyExecutedMinutes = studyWeekOverview.reduce((sum, day) => sum + day.executedMinutes, 0);
  const historyWeeklyCompletion = historyWeeklyPlannedMinutes ? Math.min(100, Math.round((historyWeeklyExecutedMinutes / historyWeeklyPlannedMinutes) * 100)) : 0;
  const activeStudyBlock = normalizedPlans.find((plan) => plan.id === activeStudyBlockId) || null;
  const questionsDoneTotal = studyLogsInSelectedPeriod.reduce((sum, log) => sum + Number(log.questions_done ?? log.completed_questions ?? 0), 0);
  const correctTotal = studyLogsInSelectedPeriod.reduce((sum, log) => sum + Number(log.correct_answers || 0), 0);
  const progressAccuracy = questionsDoneTotal ? Math.round((correctTotal / questionsDoneTotal) * 100) : 0;
  // Execucoes reais permanecem no desempenho mesmo quando o estudante cria uma
  // nova versao do plano. O vinculo do bloco serve para comparacao, nao para apagar o historico.
  const dashboardLogs = studyLogs.filter((log) => {
    const date = String(log.date || '').slice(0, 10);
    return (!dashboardStartDate || date >= dashboardStartDate) && (!dashboardEndDate || date <= dashboardEndDate);
  });
  const dashboardPlans = plansForPerformance.filter((plan) => {
    const date = String(plan.scheduled_date || '').slice(0, 10);
    return (!dashboardStartDate || date >= dashboardStartDate) && (!dashboardEndDate || date <= dashboardEndDate);
  });
  const dashboardPlannedMinutes = dashboardPlans.reduce((sum, plan) => sum + Number(plan.planned_minutes || 0), 0);
  const dashboardCompletedSeconds = dashboardLogs.reduce((sum, log) => sum + studyLogElapsedSeconds(log), 0);
  const dashboardCompletedMinutes = dashboardCompletedSeconds / 60;
  const dashboardExecutedSecondsByBlock = dashboardLogs.reduce<Map<string, number>>((totals, log) => {
    const blockId = String(log.study_block_id || '');
    if (blockId) totals.set(blockId, (totals.get(blockId) || 0) + studyLogElapsedSeconds(log));
    return totals;
  }, new Map());
  const dashboardTaskPlans = dashboardPlans.filter((plan) => !['rascunho', 'reagendado', 'cancelado', 'não realizado'].includes(String(plan.status || '').toLowerCase()));
  const dashboardCompletedTaskPlans = dashboardTaskPlans.filter((plan) => {
    const plannedSeconds = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0) * 60;
    return plannedSeconds > 0 && (dashboardExecutedSecondsByBlock.get(String(plan.id || '')) || 0) >= plannedSeconds;
  });
  const dashboardCompletedTaskIds = new Set(dashboardCompletedTaskPlans.map((plan) => String(plan.id || '')).filter(Boolean));
  const dashboardPartialTasks = dashboardTaskPlans.filter((plan) => {
    const executedSeconds = dashboardExecutedSecondsByBlock.get(String(plan.id || '')) || 0;
    const plannedSeconds = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0) * 60;
    return executedSeconds > 0 && executedSeconds < plannedSeconds;
  }).length;
  const dashboardTaskProgress = dashboardTaskPlans.length ? Math.round(dashboardCompletedTaskPlans.length * 100 / dashboardTaskPlans.length) : 0;
  const dashboardCompletedLogs = dashboardLogs.filter((log) => {
    const blockId = String(log.study_block_id || '');
    return blockId ? dashboardCompletedTaskIds.has(blockId) : studyLogElapsedSeconds(log) > 0;
  });
  const dashboardQuestionsDone = dashboardLogs.reduce((sum, log) => sum + Number(log.questions_done ?? log.completed_questions ?? 0), 0);
  const dashboardCorrectTotal = dashboardLogs.reduce((sum, log) => sum + Number(log.correct_answers || 0), 0);
  const dashboardAccuracy = dashboardQuestionsDone ? Math.round((dashboardCorrectTotal / dashboardQuestionsDone) * 100) : 0;
  const dashboardCompletedQuestions = dashboardCompletedLogs.reduce((sum, log) => sum + Number(log.questions_done ?? log.completed_questions ?? 0), 0);
  const dashboardCompletedCorrect = dashboardCompletedLogs.reduce((sum, log) => sum + Number(log.correct_answers || 0), 0);
  const dashboardCompletedAccuracy = dashboardCompletedQuestions ? Math.round((dashboardCompletedCorrect / dashboardCompletedQuestions) * 100) : 0;
  const dashboardProgress = dashboardPlannedMinutes ? Math.min(100, Math.round((dashboardCompletedMinutes / dashboardPlannedMinutes) * 100)) : 0;
  const dashboardPlannedDays = new Set(dashboardPlans.map((plan) => plan.scheduled_date).filter(Boolean)).size;
  const dashboardCompletedDays = new Set(dashboardCompletedTaskPlans.map((plan) => String(plan.scheduled_date || '').slice(0, 10)).filter(Boolean)).size;
  const dashboardDaysAdherence = dashboardPlannedDays ? Math.min(100, Math.round((dashboardCompletedDays / dashboardPlannedDays) * 100)) : 0;
  const dashboardRescheduled = dashboardPlans.filter((plan) => String(plan.status).toLowerCase() === 'reagendado' || Boolean(plan.rescheduled_from_id)).length;
  const dashboardRedistributedMinutes = dashboardPlans.filter((plan) => plan.generation_source === 'recalculated').reduce((sum, plan) => sum + Number(plan.planned_minutes || 0), 0);
  const dashboardSimulations = dashboardCompletedLogs.filter((log) => log.simulation_done || log.activity_type === 'Simulado').length;
  const dashboardEssays = dashboardCompletedLogs.filter((log) => log.essay_done || isWritingActivity(log.activity_type)).length;
  const totalGamificationXp = Number(dashboard?.cards?.xp || 0);
  const currentGamificationLevel = Math.max(1, Number(dashboard?.cards?.level || 1));
  const currentLevelStartXp = Math.pow(currentGamificationLevel - 1, 2) * 120;
  const nextLevelXp = Math.pow(currentGamificationLevel, 2) * 120;
  const xpToNextLevel = Math.max(0, nextLevelXp - totalGamificationXp);
  const levelProgress = Math.min(100, Math.max(0, Math.round((totalGamificationXp - currentLevelStartXp) * 100 / Math.max(1, nextLevelXp - currentLevelStartXp))));
  const dashboardMedals = [dashboardCompletedTaskPlans.length >= 1, dashboardCompletedQuestions >= 100, dashboardSimulations >= 1, dashboardCompletedQuestions > 0 && dashboardCompletedAccuracy >= 80, totalGamificationXp >= 3000].filter(Boolean).length;
  const dashboardPrioritySubject = currentPlanSubjects[0]?.name || disciplineWeightResults[0]?.subject_name || 'A definir';
  const subjectExecutionData = useMemo(() => {
    const sourceSubjects = currentPlanSubjects.length ? currentPlanSubjects : subjects;
    return sourceSubjects.slice(0, 8).map((subject) => {
      const logs = dashboardLogs.filter((log) => log.subject_name === subject.name);
      const plannedQuestions = dashboardPlans
        .filter((plan) => plan.subject_name === subject.name && activityRequiresQuestions(plan.activity_type))
        .reduce((sum, plan) => sum + Number(plan.planned_questions || 0), 0);
      const doneQuestions = logs.reduce((sum, log) => sum + Number(log.completed_questions || 0), 0);
      const correct = logs.reduce((sum, log) => sum + Number(log.correct_answers || 0), 0);
      return {
        name: subject.name.length > 22 ? `${subject.name.slice(0, 20)}...` : subject.name,
        planejado: plannedQuestions,
        executado: doneQuestions,
        acerto: doneQuestions ? Math.round((correct / doneQuestions) * 100) : Math.round(Number(subject.current_mastery || 0))
      };
    });
  }, [currentPlanSubjects, subjects, dashboardPlans, dashboardLogs]);
  const activityData = useMemo(() => {
    const totals = dashboardLogs.reduce<Record<string, number>>((acc, log) => {
      acc[log.activity_type] = (acc[log.activity_type] || 0) + studyLogElapsedSeconds(log) / 3600;
      return acc;
    }, {});
    return Object.entries(totals).map(([name, hours]) => ({ name, hours }));
  }, [dashboardLogs]);
  const focusEnergyData = useMemo(() => [...dashboardLogs]
    .sort((first, second) => String(first.date || '').localeCompare(String(second.date || '')))
    .slice(-10)
    .map((log) => ({
      date: formatFullDateBr(String(log.date).slice(0, 10)),
      foco: Number(log.focus || 0),
      energia: Number(log.energy || 0)
    })), [dashboardLogs]);
  const plansByDate = selectedPeriodDates.map((dateObject) => {
    const date = dateToIso(dateObject);
    return { day: dayNameFromDate(date), date, blocks: normalizedPlans.filter((plan) => plan.scheduled_date === date) };
  });
  const extraStudyNeedsQuestions = ['Exercício', 'Simulado'].includes(logForm.activity_type);
  const dashboardFilteredHoursByDay = Object.values(dashboardLogs.reduce<Record<string, { date: string; sortDate: string; hours: number }>>((acc, log) => {
    const date = String(log.date || '').slice(0, 10);
    if (!date) return acc;
    acc[date] = acc[date] || { date: formatFullDateBr(date), sortDate: date, hours: 0 };
    acc[date].hours += studyLogElapsedSeconds(log) / 3600;
    return acc;
  }, {}))
    .sort((first, second) => first.sortDate.localeCompare(second.sortDate))
    .map(({ sortDate: _sortDate, ...item }) => ({ ...item, hours: Math.round(item.hours * 10) / 10 }));
  const dashboardHoursByDay = dashboardFilteredHoursByDay;
  const dashboardHoursBySubject = Object.values(dashboardLogs.reduce<Record<string, { name: string; hours: number }>>((acc, log) => {
    const name = String(log.subject_name || 'Sem disciplina');
    acc[name] = acc[name] || { name, hours: 0 };
    acc[name].hours += studyLogElapsedSeconds(log) / 3600;
    return acc;
  }, {}))
    .map((item) => ({ ...item, hours: Math.round(item.hours * 10) / 10 }))
    .sort((first, second) => second.hours - first.hours || first.name.localeCompare(second.name));

  useEffect(() => {
    if (activeStudyBlockId && !activeStudyBlock) {
      setActiveStudyBlockId(null);
      setStudyTimerRunning(false);
      setStudyElapsedSeconds(0);
      setStudyStartedAt(null);
    }
  }, [activeStudyBlock, activeStudyBlockId]);

  useEffect(() => {
    studyRuntimeRef.current = {
      block: activeStudyBlock,
      elapsedSeconds: studyElapsedSeconds,
      running: studyTimerRunning,
      startedAt: studyStartedAt,
      finish: finishForm,
      capturedAt: Date.now()
    };
  }, [activeStudyBlock, finishForm, studyElapsedSeconds, studyStartedAt, studyTimerRunning]);

  async function refresh() {
    if (!getStudentSession()) {
      setSubjects([]);
      setWeeklyPlans([]);
      setStudyLogs([]);
      setDashboard(null);
      return;
    }
    try {
      const results = await Promise.allSettled([
        api<{ results: Subject[] }>('/api/subjects'),
        api<Dashboard>('/api/dashboard'),
        api<{ results: WeeklyPlan[] }>('/api/weekly-plan'),
        api<{ results: StudyLog[] }>('/api/study-logs'),
        api<{ results: DisciplineWeight[] }>('/api/discipline-weights'),
        api<{ results: SavedPlanVersion[] }>('/api/study-plan?history=1')
      ]);
      const payload = <T,>(index: number) => results[index].status === 'fulfilled'
        ? (results[index] as PromiseFulfilledResult<T>).value
        : null;
      const subjectPayload = payload<{ results: Subject[] }>(0);
      const dashboardPayload = payload<Dashboard>(1);
      const plansPayload = payload<{ results: WeeklyPlan[] }>(2);
      const logsPayload = payload<{ results: StudyLog[] }>(3);
      const weightsPayload = payload<{ results: DisciplineWeight[] }>(4);
      const versionsPayload = payload<{ results: SavedPlanVersion[] }>(5);
      if (subjectPayload) setSubjects(subjectPayload.results || []);
      if (dashboardPayload) setDashboard(dashboardPayload);
      if (weightsPayload) setDisciplineWeights(weightsPayload.results || []);
      if (versionsPayload) setSavedPlanVersions(versionsPayload.results || []);
      // A resposta vazia do banco também é um estado válido: ela precisa substituir
      // o rascunho local para não reaparecerem agendamentos ou execuções removidos.
      if (plansPayload && Array.isArray(plansPayload.results)) setWeeklyPlans(plansPayload.results);
      if (logsPayload && Array.isArray(logsPayload.results)) setStudyLogs(logsPayload.results);
      if (!plansPayload || !logsPayload) setMessage('Alguns dados ainda estão sendo sincronizados. Atualize a página em instantes para conferir seu plano e suas execuções.');
    } catch {
      setDashboard(null);
    }
  }

  async function loadExamBoardProfile(boardId: string) {
    if (!boardId) return;
    setExamBoardLoading(true);
    setExamBoardError('');
    try {
      const profile = await api<ExamBoardProfile>(`/api/exam-boards?id=${encodeURIComponent(boardId)}`);
      setSelectedExamBoard(profile);
      window.setTimeout(() => document.getElementById('perfil-banca-detalhes')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    } catch (error) {
      setSelectedExamBoard(null);
      setExamBoardError(error instanceof Error ? error.message : 'Não foi possível carregar o perfil desta banca agora.');
    } finally {
      setExamBoardLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    let activeRequest = true;
    const loadExamBoards = async () => {
      try {
        const payload = await api<{ results: ExamBoardListItem[] }>('/api/exam-boards');
        if (!activeRequest) return;
        setExamBoards(payload.results || []);
        if (payload.results?.length) await loadExamBoardProfile(payload.results[0].id);
      } catch (error) {
        if (!activeRequest) return;
        setExamBoardError(error instanceof Error ? error.message : 'Não foi possível carregar o catálogo de bancas agora.');
      }
    };
    void loadExamBoards();
    return () => { activeRequest = false; };
  }, []);

  useEffect(() => {
    const restoreTabFromHistory = () => {
      const tab = window.history.state?.studentTab || window.location.hash.replace('#', '');
      if (!navItems.some((item) => item.id === tab)) return;
      restoringHistory.current = true;
      setActive(tab);
    };
    window.addEventListener('popstate', restoreTabFromHistory);
    return () => window.removeEventListener('popstate', restoreTabFromHistory);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.hash = active;
    const state = { ...window.history.state, studentTab: active };
    if (!navigationReady.current) {
      window.history.replaceState(state, '', url);
      navigationReady.current = true;
      return;
    }
    if (restoringHistory.current) {
      restoringHistory.current = false;
      return;
    }
    window.history.pushState(state, '', url);
  }, [active]);

  useEffect(() => {
    if (!shouldFocusPageTitle.current) return;
    const frame = window.requestAnimationFrame(() => pageTitleRef.current?.focus());
    shouldFocusPageTitle.current = false;
    return () => window.cancelAnimationFrame(frame);
  }, [active]);

  useEffect(() => { writeStored('accessCode', accessCode); }, [accessCode]);
  useEffect(() => { writeStored('lead', lead); }, [lead]);
  useEffect(() => { writeStored('leadId', leadId); }, [leadId]);
  useEffect(() => { writeStored('answers', answers); }, [answers]);
  useEffect(() => { writeStored('strategicAnswers', strategicAnswers); }, [strategicAnswers]);
  useEffect(() => { writeStored('selfActionPlan', selfActionPlan); }, [selfActionPlan]);
  useEffect(() => { writeStored('lifeActionPlan', lifeActionPlan); }, [lifeActionPlan]);
  useEffect(() => { writeStored('lifeValues', lifeValues); }, [lifeValues]);
  useEffect(() => { writeStored('subjectForm', subjectForm); }, [subjectForm]);
  useEffect(() => { writeStored('planForm', planForm); }, [planForm]);
  useEffect(() => { writeStored('logForm', logForm); }, [logForm]);
  useEffect(() => { writeStored('weeklyPlans', weeklyPlans); }, [weeklyPlans]);
  useEffect(() => { writeStored('studyLogs', studyLogs); }, [studyLogs]);
  useEffect(() => { writeStored('consultWhatsapp', consultWhatsapp); }, [consultWhatsapp]);
  useEffect(() => { writeStored('editoraContact', editoraContact); }, [editoraContact]);
  useEffect(() => { writeStored('feedbackContact', feedbackContact); }, [feedbackContact]);
  useEffect(() => { writeStored('timeCycles', timeCycles); }, [timeCycles]);
  useEffect(() => { writeStored('timerSound', timerSound); }, [timerSound]);
  useEffect(() => { writeStored('planView', planView); }, [planView]);
  useEffect(() => { writeStored('studyWeeksTarget', studyWeeksTarget); }, [studyWeeksTarget]);
  useEffect(() => { writeStored('dashboardStartDateV2', dashboardStartDate); }, [dashboardStartDate]);
  useEffect(() => { writeStored('dashboardEndDateV2', dashboardEndDate); }, [dashboardEndDate]);
  useEffect(() => { writeStored('studyScheduleStartDate', studyScheduleStartDate); }, [studyScheduleStartDate]);
  useEffect(() => { writeStored('studyScheduleEndDate', studyScheduleEndDate); }, [studyScheduleEndDate]);
  useEffect(() => { writeStored('disciplineWeights', disciplineWeights); }, [disciplineWeights]);
  useEffect(() => { writeStored('disciplineWeightForm', disciplineWeightForm); }, [disciplineWeightForm]);
  useEffect(() => { writeStored('weekStartDate', weekStartDate); }, [weekStartDate]);
  useEffect(() => { writeStored('weekEndDate', weekEndDate); }, [weekEndDate]);
  useEffect(() => { writeStored('activeStudyBlockId', activeStudyBlockId); }, [activeStudyBlockId]);
  useEffect(() => { writeStored('studyElapsedSeconds', studyElapsedSeconds); }, [studyElapsedSeconds]);
  useEffect(() => { writeStored('studyStartedAt', studyStartedAt); }, [studyStartedAt]);
  useEffect(() => { writeStored('finishForm', finishForm); }, [finishForm]);

  useEffect(() => {
    if (!studyTimerRunning) return undefined;
    const interval = window.setInterval(() => setStudyElapsedSeconds((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(interval);
  }, [studyTimerRunning]);

  useEffect(() => {
    if (!leadSaved && !publicNavIds.has(active)) {
      setActive('inicio');
    }
  }, [active, leadSaved]);

  useEffect(() => {
    // Cada aba deve abrir no início para manter a navegação previsível em qualquer tela.
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    return () => window.cancelAnimationFrame(frame);
  }, [active]);

  useEffect(() => {
    if (active !== 'dashboard') return;
    // O desempenho começa sempre pela semana corrente. Os filtros continuam
    // disponíveis para consultas pontuais, mas um período antigo não fica
    // preso quando o estudante volta a esta aba.
    const currentWeekStart = startOfCurrentWeek();
    setDashboardStartDate(dateToIso(currentWeekStart));
    setDashboardEndDate(dateToIso(addDays(currentWeekStart, 6)));
  }, [active]);

  async function getTimerAudioContext() {
    const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!timerAudioRef.current) timerAudioRef.current = new AudioContextClass();
    if (timerAudioRef.current.state === 'suspended') await timerAudioRef.current.resume();
    return timerAudioRef.current;
  }

  async function unlockTimerAudio() {
    try {
      const audio = await getTimerAudioContext();
      if (!audio) return;
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      gain.gain.setValueAtTime(0.001, audio.currentTime);
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + 0.03);
    } catch {
      // O navegador pode bloquear áudio até a primeira interação; o timer continua.
    }
  }

  function stopTimerSound() {
    timerSoundStopsRef.current.forEach((stop) => stop());
    timerSoundStopsRef.current = [];
    const audio = timerAudioRef.current;
    if (audio && audio.state === 'running') {
      void audio.suspend().catch(() => undefined);
    }
  }

  async function playTimerSound() {
    try {
      const audio = await getTimerAudioContext();
      if (!audio) return;
      stopTimerSound();
      await getTimerAudioContext();
      const base = currentMode === 'study' ? 880 : 660;
      const patterns: Record<string, number[]> = {
        classico: [base],
        energia: [base, base * 1.25, base * 1.5],
        suave: [base * 0.75, base],
        vitoria: [660, 880, 990, 1320]
      };
      const notes = patterns[timerSound] || patterns.energia;
      const playPattern = () => {
        notes.forEach((frequency, index) => {
          const oscillator = audio.createOscillator();
          const gain = audio.createGain();
          const start = audio.currentTime + index * 0.2;
          oscillator.type = timerSound === 'suave' ? 'sine' : 'triangle';
          oscillator.frequency.value = frequency;
          gain.gain.setValueAtTime(0.001, start);
          gain.gain.exponentialRampToValueAtTime(timerSound === 'suave' ? 0.16 : 0.32, start + 0.03);
          gain.gain.exponentialRampToValueAtTime(0.001, start + 0.18);
          oscillator.connect(gain);
          gain.connect(audio.destination);
          oscillator.start(start);
          oscillator.stop(start + 0.2);
          timerSoundStopsRef.current.push(() => {
            try {
              gain.gain.cancelScheduledValues(audio.currentTime);
              gain.gain.setValueAtTime(0.0001, audio.currentTime);
              oscillator.stop();
            } catch {
              // Oscilador já finalizado.
            }
            try {
              oscillator.disconnect();
              gain.disconnect();
            } catch {
              // Nó de áudio já desconectado.
            }
          });
        });
      };
      playPattern();
      const interval = window.setInterval(playPattern, Math.max(900, notes.length * 220 + 650));
      timerSoundStopsRef.current.push(() => window.clearInterval(interval));
    } catch {
      // O som é um reforço; o fluxo do timer continua mesmo se o navegador bloquear áudio.
    }
  }

  function notifyTimerStep(title: string, detail: string) {
    setTimerNotice({ title, detail });
  }

  function resetTimer(step = 0) {
    const cycle = timeCycles[Math.floor(step / 2)] || timeCycles[0] || { study: 25, rest: 5 };
    const mode = step % 2 === 0 ? 'study' : 'rest';
    setTimeStep(step);
    setTimeRemaining(Math.max(1, Number(cycle[mode] || 1)) * 60);
    setTimeEndsAt(null);
    setTimeRunning(false);
    setTimeCompleted(step);
    timerHandledStepRef.current = null;
    stopTimerSound();
  }

  function startTimeManagement() {
    if (currentMode === 'rest' && Number(currentCycle.rest || 0) <= 0) {
      const nextStep = timeStep + 1 >= timeCycles.length * 2 ? 0 : timeStep + 1;
      const nextCycle = timeCycles[Math.floor(nextStep / 2)] || timeCycles[0] || { study: 25, rest: 5 };
      const nextSeconds = Math.max(1, Number(nextCycle.study || 1)) * 60;
      void unlockTimerAudio();
      timerHandledStepRef.current = null;
      setTimeStep(nextStep);
      setTimeRemaining(nextSeconds);
      setTimeEndsAt(Date.now() + nextSeconds * 1000);
      setTimeRunning(true);
      setMessage(`⏩ Descanso de 0 minuto escolhido. O ${Math.floor(nextStep / 2) + 1}º ciclo de estudo começou automaticamente.`);
      return;
    }
    const safeSeconds = Math.max(1, timeRemaining || totalTimeSeconds);
    void unlockTimerAudio();
    timerHandledStepRef.current = null;
    setTimeEndsAt(Date.now() + safeSeconds * 1000);
    setTimeRunning(true);
    setMessage('🚀 Timer iniciado. Você deu o passo mais importante: começou. Agora é foco com alegria!');
  }

  function pauseTimeManagement() {
    if (timeEndsAt) {
      setTimeRemaining(Math.max(0, Math.ceil((timeEndsAt - Date.now()) / 1000)));
    }
    setTimeEndsAt(null);
    setTimeRunning(false);
    stopTimerSound();
  }

  function stopTimeManagement() {
    resetTimer(0);
    setTimerNotice(null);
    setMessage('Timer encerrado. Quando quiser recomeçar, aperte Iniciar ciclo e avance no seu ritmo.');
  }

  useEffect(() => {
    if (!timeRunning || !timeEndsAt) return undefined;
    const syncRemaining = () => {
      setTimeRemaining(Math.max(0, Math.ceil((timeEndsAt - Date.now()) / 1000)));
    };
    syncRemaining();
    const timeout = window.setTimeout(syncRemaining, Math.max(0, timeEndsAt - Date.now()));
    const interval = window.setInterval(syncRemaining, 1000);
    window.addEventListener('focus', syncRemaining);
    document.addEventListener('visibilitychange', syncRemaining);
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      window.removeEventListener('focus', syncRemaining);
      document.removeEventListener('visibilitychange', syncRemaining);
    };
  }, [timeRunning, timeEndsAt]);

  useEffect(() => {
    if (!timeRunning || timeRemaining !== 0) return;
    if (timerHandledStepRef.current === timeStep) return;
    timerHandledStepRef.current = timeStep;
    void playTimerSound();
    const nextStep = timeStep + 1;
    const finishedMode = currentMode;
    const finishedCycle = Math.floor(timeStep / 2) + 1;
    setTimeCompleted(nextStep);
    const totalSteps = timeCycles.length * 2;
    let completedRound = nextStep >= totalSteps;
    let normalizedNextStep = completedRound ? 0 : nextStep;
    let nextCycle = timeCycles[Math.floor(normalizedNextStep / 2)] || timeCycles[0] || { study: 25, rest: 5 };
    let nextMode: keyof TimeCycle = normalizedNextStep % 2 === 0 ? 'study' : 'rest';
    const skippedRest = nextMode === 'rest' && Number(nextCycle.rest || 0) <= 0;
    if (skippedRest) {
      normalizedNextStep += 1;
      if (normalizedNextStep >= totalSteps) {
        normalizedNextStep = 0;
        completedRound = true;
      }
      nextCycle = timeCycles[Math.floor(normalizedNextStep / 2)] || timeCycles[0] || { study: 25, rest: 5 };
      nextMode = normalizedNextStep % 2 === 0 ? 'study' : 'rest';
    }
    const nextSeconds = Math.max(1, Number(nextCycle[nextMode] || 1)) * 60;
    setTimeStep(normalizedNextStep);
    setTimeCompleted(completedRound ? 0 : nextStep);
    timerHandledStepRef.current = null;
    setTimeRemaining(nextSeconds);
    setTimeEndsAt(Date.now() + nextSeconds * 1000);
    const nextMessage = completedRound
      ? '🏆 Você concluiu os 4 ciclos. O 1º ciclo foi reiniciado automaticamente para manter seu ritmo.'
      : skippedRest
      ? `⏩ Descanso de 0 minuto escolhido. O ${Math.floor(normalizedNextStep / 2) + 1}º ciclo de estudo começou automaticamente.`
      : nextMode === 'study'
      ? `🚀 ${Math.floor(nextStep / 2) + 1}º ciclo iniciado automaticamente. Vamos com foco, alegria e confiança!`
      : '☕ Descanso iniciado automaticamente. Respire, recarregue e volte ainda melhor.';
    setMessage(nextMessage);
    notifyTimerStep(
      completedRound ? '🏆 Quatro ciclos concluídos!' : finishedMode === 'study' ? `📚 Estudo do ${finishedCycle}º ciclo concluído!` : `☕ Descanso do ${finishedCycle}º ciclo concluído!`,
      completedRound
        ? 'O 1º ciclo de estudo já reiniciou automaticamente. Clique em OK para silenciar o aviso; o timer continua até você pausar ou reiniciar.'
        : skippedRest
        ? `Você escolheu não descansar neste ciclo. O ${Math.floor(normalizedNextStep / 2) + 1}º ciclo de estudo já começou automaticamente.`
        : nextMode === 'study'
        ? `O ${Math.floor(nextStep / 2) + 1}º ciclo de estudo já começou. Você está construindo aprovação minuto a minuto.`
        : 'Seu descanso já começou. Faça uma pausa consciente e volte com energia renovada.'
    );
  }, [timeRunning, timeRemaining, timeStep, timeCycles, currentMode, timerSound]);

  async function validateAccess() {
    const code = accessCode.trim();
    const completeLead = lead.full_name.trim().length >= 5 && lead.email.includes('@') && lead.whatsapp.replace(/\D/g, '').length >= 10 && studyGoalReady && lead.consent_lgpd && /^\d{6}$/.test(leadPassword);
    const afterValidCode = async () => {
      setAccessOk(true);
      if (!completeLead) {
        setMessage('Código validado. Complete os dados obrigatórios, crie sua senha de acesso e autorize a LGPD para iniciar sua jornada.');
        return;
      }
      if ((!leadSaved || !getStudentSession()) && !await saveLead({ silent: true })) return;
      await refresh();
      setConsultFound(true);
      setActive('peso');
      setMenuOpen(false);
      setMessage('Cadastro concluído. Agora configure suas disciplinas e prioridades para iniciar seu plano de estudos.');
    };
    try {
      const response = await postJson<{ success: boolean }>('/api/access-code/validate', { code });
      if (!response.success) throw new Error('Código inválido. Verifique o código de acesso informado.');
      await afterValidCode();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Código inválido. Verifique o código de acesso informado.');
    }
  }

  async function saveLead(options: { silent?: boolean; goToPriorities?: boolean } = {}) {
    try {
      const response = await postJson<{ id: string; existing?: boolean; student_session?: string }>('/api/leads', { ...lead, access_password: leadPassword });
      setLeadId(response.id);
      setStudentSession(response.student_session);
      setLeadSaved(true);
      writeStored('returningStudent', true);
      if (options.goToPriorities ?? !options.silent) {
        setActive('peso');
        setMenuOpen(false);
      }
      if (!options.silent) setMessage(response.existing ? 'Cadastro localizado e atualizado sem criar uma nova linha de lead.' : 'Cadastro concluído. Agora configure suas disciplinas e prioridades para iniciar seu plano de estudos.');
      return true;
    } catch (error) {
      setLeadSaved(false);
      setStudentSession();
      if (!options.silent) setMessage(error instanceof Error ? error.message : 'Não foi possível conectar seu cadastro ao servidor. Confira a conexão e valide novamente antes de salvar o plano de estudos.');
      return false;
    }
  }

  async function resumeStudiesByWhatsapp() {
    const digits = consultWhatsapp.replace(/\D/g, '');
    if (digits.length < 10 || !/^\d{6}$/.test(consultPassword)) {
      setMessage('Informe o WhatsApp usado no cadastro e sua senha de acesso com 6 números.');
      return;
    }
    setConsulting(true);
    try {
      const response = await postJson<{ found: boolean; student_session?: string; lead?: { id: string; full_name: string; whatsapp: string; email: string; study_goal?: string; study_goal_other?: string; consent_lgpd?: number | boolean } }>('/api/student-login', { whatsapp: consultWhatsapp, password: consultPassword });
      if (!response.found || !response.lead) {
        setMessage('Não localizamos esse WhatsApp. Use Liberar acesso para fazer seu primeiro cadastro.');
        return;
      }
      setLead({
        full_name: response.lead.full_name,
        whatsapp: response.lead.whatsapp,
        email: response.lead.email,
        study_goal: response.lead.study_goal || '',
        study_goal_other: response.lead.study_goal_other || '',
        consent_lgpd: Boolean(response.lead.consent_lgpd)
      });
      setLeadId(response.lead.id);
      setStudentSession(response.student_session);
      setConsultPassword('');
      setLeadSaved(true);
      setAccessOk(true);
      setConsultFound(true);
      writeStored('returningStudent', true);
      await refresh();
      setMessage(returningStudentNotice);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Não foi possível consultar o cadastro agora. Tente novamente em instantes.');
    } finally {
      setConsulting(false);
    }
  }

  async function saveAssessment() {
    try {
      await postJson('/api/assessments', { lead_id: leadId, answers });
      await postJson('/api/strategic-assessments', { lead_id: leadId, answers: strategicAnswers });
      setMessage('Resultado registrado com sucesso.');
      refresh();
    } catch {
      setMessage('Resultado calculado. A gravação será feita quando a API estiver ativa.');
    }
  }

  async function downloadPdf() {
    createAssessmentPdf().save(assessmentPdfMeta().fileName);
  }

  function assessmentPdfMeta(): PdfMeta {
    return {
      title: 'Estudante em Alta Performance',
      subtitle: 'Diagnóstico de desempenho estudantil',
      fileName: 'relatorio-francisneyliberato-estudante.pdf',
      participantName: lead.full_name,
      participantEmail: lead.email,
      participantWhatsapp: lead.whatsapp
    };
  }

  function createAssessmentPdf() {
    const scaleLabel = (value: number) => selfAssessmentScale.find((item) => item.value === value)?.label || `${value}/5`;
    const assessmentRows = diagnosisQuestions.map((question, index) => [`${index + 1}`, question, `${answers[index]}/5 - ${scaleLabel(answers[index])}`]);
    const strategicRows = strategicQuestions.map((question, index) => [`${index + 1}`, question, strategicAnswers[index] || '-']);
    const actionRows = selfActionPlan.map((item) => [item.task || '-', item.activity || '-', item.deadline ? formatDateBr(item.deadline) : '-', item.completed ? 'Concluída' : 'Em andamento']);
    return createProfessionalPdf(assessmentPdfMeta(), [
      {
        title: 'Síntese do diagnóstico',
        text: `${result.recommendation} Leitura estratégica: ${strategicTotal < 70 ? 'revise planejamento, método e fatores psicológicos antes de aumentar a carga.' : 'mantenha os ajustes progressivos e proteja sua constância.'}`,
        metrics: [
          { label: 'Potencial atual', value: `${result.percentage}%` },
          { label: 'Pontuação', value: `${result.total}/50` },
          { label: 'Pontuação estratégica', value: `${strategicTotal} pontos` },
          { label: 'Classificação', value: result.classification }
        ]
      },
      {
        title: 'Respostas da autoavaliação',
        headers: ['#', 'Questão', 'Resposta'],
        widths: [12, 126, 42],
        rows: assessmentRows
      },
      {
        title: 'Questionário estratégico',
        headers: ['#', 'Questão', 'Resposta'],
        widths: [12, 126, 42],
        rows: strategicRows
      },
      {
        title: 'Plano de ação',
        text: 'Transforme o diagnóstico em ações possíveis. Revise este plano semanalmente e ajuste o prazo quando necessário, sem abandonar a constância.',
        headers: ['Tarefa', 'Atividade prática', 'Prazo', 'Status'],
        widths: [42, 83, 28, 27],
        rows: actionRows.length ? actionRows : [['Defina sua primeira tarefa', 'Escolha uma ação pequena e prática para esta semana.', '-', 'Em andamento']]
      }
    ]);
  }

  async function shareCurrentPdf(element: HTMLElement | null, meta: PdfMeta) {
    if (!element) {
      setMessage('Gere o resultado antes de compartilhar o PDF.');
      return;
    }
    const text = `Segue o PDF em anexo e o link da plataforma: ${window.location.href}`;
    try {
      const pdf = await createBrandedPdfFromElement(element, meta);
      const file = new File([pdf.output('blob')], meta.fileName, { type: 'application/pdf' });
      const shareData = {
        title: meta.title,
        text,
        url: window.location.href,
        files: [file]
      };
      const nav = navigator as Navigator & {
        canShare?: (data: { files?: File[] }) => boolean;
        share?: (data: { title?: string; text?: string; url?: string; files?: File[] }) => Promise<void>;
      };
      if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
        await nav.share(shareData);
        setMessage('PDF e link compartilhados com sucesso.');
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      pdf.save(meta.fileName);
      setMessage('Este navegador não permite anexar PDF pelo compartilhamento. O PDF foi baixado e o link foi copiado para envio.');
    } catch {
      setMessage('Não foi possível abrir o compartilhamento agora. Baixe o PDF e envie pelo WhatsApp, e-mail ou aplicativo desejado.');
    }
  }

  async function sharePdfDocument(pdf: jsPDF, meta: PdfMeta) {
    const text = `Segue o PDF em anexo e o link da plataforma: ${window.location.href}`;
    try {
      const file = new File([pdf.output('blob')], meta.fileName, { type: 'application/pdf' });
      const nav = navigator as Navigator & {
        canShare?: (data: { files?: File[] }) => boolean;
        share?: (data: { title?: string; text?: string; url?: string; files?: File[] }) => Promise<void>;
      };
      if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
        await nav.share({ title: meta.title, text, url: window.location.href, files: [file] });
        setMessage('PDF e link compartilhados com sucesso.');
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      pdf.save(meta.fileName);
      setMessage('O PDF foi baixado e o link foi copiado para envio pelo aplicativo que você preferir.');
    } catch {
      setMessage('Não foi possível abrir o compartilhamento agora. Baixe o PDF e envie pelo WhatsApp, e-mail ou aplicativo desejado.');
    }
  }

  function planPdfMeta(): PdfMeta {
    return {
      title: 'Plano de Estudos',
      subtitle: `Planejamento de ${weekLabel}`,
      fileName: 'meu-plano-francisneyliberato-estudante.pdf',
      orientation: 'l',
      participantName: lead.full_name,
      participantEmail: lead.email,
      participantWhatsapp: lead.whatsapp
    };
  }

  function createPlanPdf() {
    return createPlanPdfFromBlocks(normalizedPlansInPeriod, weekLabel, automaticWeekGoalLabel, `${selectedWeekNumber}/${studyWeeksTarget || 1}`, planPdfMeta());
  }

  function queueInterruptedStudyLog(log: StudyLog) {
    const pending = readStoredValue<StudyLog[]>('pendingStudyLogSync', []);
    const next = [log, ...pending.filter((item) => item.id !== log.id)];
    writeStored('pendingStudyLogSync', next);
  }

  async function flushInterruptedStudyLogs() {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    const pending = readStoredValue<StudyLog[]>('pendingStudyLogSync', []);
    if (!pending.length) return;
    const remaining: StudyLog[] = [];
    for (const log of pending) {
      try {
        await postJson('/api/study-logs', log);
      } catch {
        remaining.push(log);
      }
    }
    writeStored('pendingStudyLogSync', remaining);
    if (remaining.length !== pending.length) void refresh();
  }

  function persistInterruptedStudy() {
    if (autoSavingStudyRef.current) return;
    const snapshot = studyRuntimeRef.current;
    if (!snapshot.block?.id || !snapshot.running) return;
    const elapsedSeconds = Math.max(0, snapshot.elapsedSeconds + Math.floor((Date.now() - snapshot.capturedAt) / 1000));
    if (!elapsedSeconds) return;

    autoSavingStudyRef.current = true;
    const block = snapshot.block;
    const previousExecution = studyLogs.find((item) => String(item.study_block_id || '') === String(block.id));
    const previousSeconds = studyLogElapsedSeconds(previousExecution);
    const actualSeconds = Math.max(previousSeconds, elapsedSeconds);
    const actualMinutes = Math.max(1, Math.ceil(actualSeconds / 60));
    const plannedMinutes = Number(block.planned_minutes || Math.round(Number(block.duration_hours || 0) * 60) || actualMinutes);
    const questions = Number(snapshot.finish.questions_done || previousExecution?.questions_done || previousExecution?.completed_questions || 0);
    const correct = Math.min(questions, Math.max(0, Number(snapshot.finish.correct_answers || previousExecution?.correct_answers || 0)));
    const status = actualSeconds >= plannedMinutes * 60 ? 'concluído' : 'parcial';
    const log: StudyLog = {
      ...defaultLogForm,
      id: previousExecution?.id || `interrupted-log-${Date.now()}-${crypto.randomUUID()}`,
      study_block_id: block.id,
      plan_week_id: block.plan_week_id || null,
      date: String(block.scheduled_date || todayIso).slice(0, 10),
      subject_name: block.subject_name,
      content: block.content || '',
      activity_type: block.activity_type,
      planned_hours: plannedMinutes / 60,
      completed_hours: actualSeconds / 3600,
      actual_minutes: actualMinutes,
      actual_seconds: actualSeconds,
      planned_questions: Number(block.planned_questions || 0),
      completed_questions: questions,
      questions_done: questions,
      correct_answers: correct,
      accuracy_percentage: questions ? Math.round(correct * 100 / questions) : 0,
      perceived_difficulty: snapshot.finish.perceived_difficulty || previousExecution?.perceived_difficulty || defaultFinishForm.perceived_difficulty,
      is_extra: false,
      started_at: previousExecution?.started_at || snapshot.startedAt || new Date().toISOString(),
      started_on_time: Boolean(previousExecution?.started_on_time),
      finished_at: new Date().toISOString(),
      review_done: block.activity_type === 'Memorização/Revisão',
      simulation_done: block.activity_type === 'Simulado',
      essay_done: isWritingActivity(block.activity_type),
      energy: Number(previousExecution?.energy || 0),
      focus: Number(previousExecution?.focus || 0),
      notes: snapshot.finish.notes || previousExecution?.notes || 'Tempo registrado automaticamente ao interromper a sessão.'
    };

    queueInterruptedStudyLog(log);
    setStudyLogs((logs) => [log, ...logs.filter((item) => String(item.study_block_id || '') !== String(block.id))]);
    setWeeklyPlans((plans) => plans.map((plan) => plan.id === block.id ? { ...plan, status, completed_at: log.finished_at } : plan));
    setActiveStudyBlockId(null);
    setStudyTimerRunning(false);
    setStudyElapsedSeconds(0);
    setStudyStartedAt(null);

    const session = getStudentSession();
    if (session && typeof navigator !== 'undefined' && navigator.onLine) {
      void fetch('/api/study-logs', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json', 'x-student-session': session },
        body: JSON.stringify(log)
      }).then((response) => {
        if (!response.ok) throw new Error('Falha ao registrar a execução interrompida.');
        const pending = readStoredValue<StudyLog[]>('pendingStudyLogSync', []);
        writeStored('pendingStudyLogSync', pending.filter((item) => item.id !== log.id));
      }).catch(() => undefined);
    }
  }

  useEffect(() => {
    void flushInterruptedStudyLogs();
    const syncPending = () => { void flushInterruptedStudyLogs(); };
    window.addEventListener('online', syncPending);
    return () => window.removeEventListener('online', syncPending);
  }, []);

  useEffect(() => {
    const saveBeforeExit = () => persistInterruptedStudy();
    window.addEventListener('pagehide', saveBeforeExit);
    window.addEventListener('offline', saveBeforeExit);
    return () => {
      window.removeEventListener('pagehide', saveBeforeExit);
      window.removeEventListener('offline', saveBeforeExit);
    };
  }, [activeStudyBlock, finishForm, studyElapsedSeconds, studyLogs, studyStartedAt, studyTimerRunning]);

  function createPlanPdfFromBlocks(blocks: WeeklyPlan[], period: string, plannedLoad: string, weekTarget: string, meta: PdfMeta) {
    const planRows = [...blocks]
      .sort((first, second) => `${first.scheduled_date || ''}${first.start_time || ''}`.localeCompare(`${second.scheduled_date || ''}${second.start_time || ''}`))
      .map((plan) => [
        plan.scheduled_date ? `${dayNameFromDate(plan.scheduled_date)}\n${formatDateBr(plan.scheduled_date)}` : plan.day,
        plan.start_time ? `${plan.start_time} às ${planEndTime(plan)}` : 'Horário livre',
        plan.activity_type,
        plan.subject_name || 'Atividade geral',
        formatHoursFromMinutes(Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60))),
        plan.planned_questions ? String(plan.planned_questions) : '-',
        plan.status || 'Planejado'
      ]);
    return createProfessionalPdf(meta, [
      {
        title: 'Visão geral do planejamento',
        text: 'Este relatório organiza a semana em ações claras. Use-o como seu roteiro: execute um bloco por vez, registre o que foi concluído e ajuste o próximo passo com tranquilidade.',
        metrics: [
          { label: 'Período', value: period },
          { label: 'Carga planejada', value: plannedLoad },
          { label: 'Meta de semanas', value: weekTarget }
        ]
      },
      {
        title: 'Blocos de estudo programados',
        headers: ['Dia', 'Horário', 'Atividade', 'Disciplina', 'Duração', 'Meta', 'Status'],
        widths: [34, 33, 52, 64, 26, 22, 30],
        rows: planRows.length ? planRows : [['-', '-', 'Nenhum bloco cadastrado no período selecionado.', '-', '-', '-', '-']]
      },
      {
        title: 'Orientação para a execução',
        text: 'Comece pelo bloco mais importante e proteja o horário definido. Ao terminar, registre sua execução no Estudar Agora. A constância nasce de metas possíveis, não de um plano perfeito.'
      }
    ]);
  }

  function downloadSavedPlanVersionPdf(version: SavedPlanVersion) {
    const blocks = Array.isArray(version.snapshot?.blocks) ? version.snapshot.blocks as unknown as WeeklyPlan[] : [];
    const config = version.snapshot?.config || {};
    const startDate = typeof config.startDate === 'string' ? config.startDate : '';
    const endDate = typeof config.endDate === 'string' ? config.endDate : '';
    const totalMinutes = blocks.reduce((sum, block) => sum + Number(block.planned_minutes || Math.round(Number(block.duration_hours || 0) * 60)), 0);
    const fileSlug = version.fileName.replace(/[^a-zA-Z0-9 -]/g, '').replace(/\s+/g, '-').toLowerCase();
    const meta: PdfMeta = {
      title: 'Plano de Estudos',
      subtitle: `Versão ${version.versionNumber} salva em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(version.savedAt))}`,
      fileName: `${fileSlug || `plano-de-estudos-versao-${version.versionNumber}`}.pdf`,
      orientation: 'l',
      participantName: lead.full_name,
      participantEmail: lead.email,
      participantWhatsapp: lead.whatsapp
    };
    const period = startDate && endDate ? `${formatDateBr(startDate)} a ${formatDateBr(endDate)}` : 'Período registrado nesta versão';
    createPlanPdfFromBlocks(blocks, period, formatHoursFromMinutes(totalMinutes), String(config.currentWeek || version.versionNumber), meta).save(meta.fileName);
  }

  async function deleteSavedPlanVersion(version: SavedPlanVersion) {
    const confirmed = window.confirm(`Apagar a versão ${version.versionNumber} do histórico? Esta ação não exclui os seus blocos de estudo nem as execuções já registradas.`);
    if (!confirmed) return;
    try {
      await api(`/api/study-plan/versions/${encodeURIComponent(version.id)}`, { method: 'DELETE' });
      setSavedPlanVersions((versions) => versions.filter((item) => item.id !== version.id));
      setMessage('Versão histórica apagada. Seu plano atual e seus registros de execução foram preservados.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Não foi possível apagar esta versão agora. Tente novamente.');
    }
  }

  function progressPdfMeta(): PdfMeta {
    const periodLabel = dashboardStartDate || dashboardEndDate
      ? `${dashboardStartDate ? formatFullDateBr(dashboardStartDate) : 'o início dos registros'} a ${dashboardEndDate ? formatFullDateBr(dashboardEndDate) : 'hoje'}`
      : 'todos os registros disponíveis';
    return {
      title: 'Meu Desempenho',
      subtitle: `Acompanhamento de ${periodLabel}`,
      fileName: 'meu-progresso-francisneyliberato-estudante.pdf',
      orientation: 'l',
      participantName: lead.full_name,
      participantEmail: lead.email,
      participantWhatsapp: lead.whatsapp
    };
  }

  function createProgressPdf() {
    const calendarRows = [...dashboardPlans]
      .sort((first, second) => `${first.scheduled_date || ''}${first.start_time || ''}`.localeCompare(`${second.scheduled_date || ''}${second.start_time || ''}`))
      .map((plan) => {
        const blockId = String(plan.id || '');
        const plannedMinutes = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0);
        const actualSeconds = dashboardExecutedSecondsByBlock.get(blockId) || 0;
        const completion = plannedMinutes > 0 ? Math.min(100, Math.round(actualSeconds * 100 / (plannedMinutes * 60))) : 0;
        return [
          plan.scheduled_date ? `${formatDateBr(String(plan.scheduled_date).slice(0, 10))}\n${dayNameFromDate(String(plan.scheduled_date).slice(0, 10))}` : '-',
          plan.start_time || '-',
          plan.subject_name || 'Atividade geral',
          plan.activity_type || '-',
          formatHoursFromMinutes(plannedMinutes),
          formatStudyElapsed(actualSeconds),
          `${completion}%`,
          plan.status || 'Planejado'
        ];
      });
    const missionRows = [
      ['Concluir 80% das tarefas planejadas', `${dashboardTaskProgress}%`, dashboardTaskProgress >= 80 ? 'Concluída' : 'Em andamento'],
      ['Estudar em todos os dias escolhidos', `${dashboardDaysAdherence}%`, dashboardDaysAdherence >= 100 ? 'Concluída' : 'Em andamento'],
      ['Concluir blocos de memorização ou revisão', dashboardCompletedLogs.some((log) => log.review_done) ? '100%' : '0%', dashboardCompletedLogs.some((log) => log.review_done) ? 'Concluída' : 'Em andamento'],
      ['Atingir a meta de questões', `${Math.min(100, Math.round(dashboardCompletedQuestions / 120 * 100))}%`, dashboardCompletedQuestions >= 120 ? 'Concluída' : 'Em andamento'],
      ['Realizar simulado', dashboardSimulations > 0 ? '100%' : '0%', dashboardSimulations > 0 ? 'Concluída' : 'Em andamento'],
      ['Realizar redação ou prova dissertativa', dashboardEssays > 0 ? '100%' : '0%', dashboardEssays > 0 ? 'Concluída' : 'Em andamento']
    ];
    const logRows = [...dashboardLogs]
      .sort((first, second) => String(first.date || '').localeCompare(String(second.date || '')))
      .map((log) => {
        const done = Number(log.questions_done ?? log.completed_questions ?? 0);
        const correct = Number(log.correct_answers || 0);
        const accuracy = done ? `${Math.round((correct / done) * 100)}%` : '-';
        return [
          log.date ? formatDateBr(String(log.date).slice(0, 10)) : '-',
          log.subject_name || 'Atividade geral',
          log.activity_type || '-',
          formatStudyElapsed(studyLogElapsedSeconds(log)),
          String(done || '-'),
          accuracy,
          `${log.focus ?? '-'}/10`,
          `${log.energy ?? '-'}/10`
        ];
      });
    return createProfessionalPdf(progressPdfMeta(), [
      {
        title: 'Painel de desempenho',
        text: 'O progresso real é construído registro por registro. Leia os indicadores com honestidade, celebre o que avançou e escolha uma melhoria prática para o próximo bloco.',
        metrics: [
          { label: 'Período analisado', value: dashboardStartDate || dashboardEndDate ? `${dashboardStartDate ? formatFullDateBr(dashboardStartDate) : 'Início dos registros'} a ${dashboardEndDate ? formatFullDateBr(dashboardEndDate) : 'Hoje'}` : 'Todos os registros disponíveis' },
          { label: 'Estudado no período', value: formatStudyElapsed(dashboardCompletedSeconds) },
          { label: 'Realizado x planejado', value: `${formatStudyElapsed(dashboardCompletedSeconds)} / ${formatHoursFromMinutes(dashboardPlannedMinutes)}` },
          { label: 'Progresso do período', value: `${dashboardProgress}%` },
          { label: 'Tarefas concluídas / planejadas', value: `${dashboardCompletedTaskPlans.length}/${dashboardTaskPlans.length}` },
          { label: 'Execuções parciais', value: String(dashboardPartialTasks) },
          { label: 'Questões realizadas', value: String(dashboardQuestionsDone) },
          { label: 'Percentual de acertos', value: `${dashboardAccuracy}%` },
          { label: 'Sequência de estudos', value: pluralize(Number(dashboard?.cards?.streak || 0), 'dia') },
          { label: 'Pontos de Evolução', value: `${totalGamificationXp} pontos` },
          { label: 'Nível de Preparação', value: String(currentGamificationLevel) },
          { label: 'Medalhas conquistadas', value: String(dashboardMedals) },
          { label: 'Disciplina prioritária', value: dashboardPrioritySubject }
        ]
      },
      {
        title: 'Gráfico: evolução diária de estudo',
        text: 'As barras mostram as horas efetivamente registradas em cada dia do período selecionado.',
        chart: {
          title: 'Horas executadas por dia',
          color: [34, 211, 238],
          unit: 'hours',
          data: dashboardHoursByDay.map((item) => ({ label: item.date, value: Number(item.hours || 0) }))
        }
      },
      {
        title: 'Gráfico: distribuição por disciplina e Simulado',
        text: 'Use esta leitura para verificar se as horas executadas acompanham as prioridades do seu plano de estudos. A Memorização/Revisão é registrada na disciplina revisada; o Simulado aparece como atividade geral, pois integra todas as disciplinas selecionadas.',
        chart: {
          title: 'Horas registradas por disciplina e Simulado',
          color: [52, 211, 153],
          unit: 'hours',
          data: dashboardHoursBySubject.map((item) => ({ label: item.name, value: Number(item.hours || 0) }))
        }
      },
      {
        title: 'Gráfico: exercícios planejados',
        text: 'Quantidade de questões planejadas por disciplina no período selecionado.',
        chart: {
          title: 'Questões planejadas',
          color: [250, 204, 21],
          unit: 'number',
          data: subjectExecutionData.map((item) => ({ label: item.name, value: Number(item.planejado || 0) }))
        }
      },
      {
        title: 'Gráfico: exercícios executados',
        text: 'Quantidade de questões efetivamente executadas por disciplina.',
        chart: {
          title: 'Questões executadas',
          color: [34, 211, 238],
          unit: 'number',
          data: subjectExecutionData.map((item) => ({ label: item.name, value: Number(item.executado || 0) }))
        }
      },
      {
        title: 'Gráfico: percentual de acertos por disciplina',
        text: 'Use o percentual de acertos para orientar revisões e ajustes no próximo ciclo.',
        chart: {
          title: 'Percentual de acertos',
          color: [52, 211, 153],
          unit: 'percent',
          data: subjectExecutionData.map((item) => ({ label: item.name, value: Number(item.acerto || 0) }))
        }
      },
      {
        title: 'Gráfico: distribuição por tipo de atividade',
        text: 'Mostra a distribuição das horas realizadas entre teoria, exercícios, revisões, simulados e atividades dissertativas.',
        chart: {
          title: 'Horas por tipo de atividade',
          color: [167, 139, 250],
          unit: 'hours',
          data: activityData.map((item) => ({ label: item.name, value: Number(item.hours || 0) }))
        }
      },
      {
        title: 'Gráfico: foco registrado',
        text: 'Evolução do foco informado ao concluir os estudos.',
        chart: {
          title: 'Foco por registro',
          color: [34, 211, 238],
          unit: 'number',
          data: focusEnergyData.map((item) => ({ label: item.date, value: Number(item.foco || 0) }))
        }
      },
      {
        title: 'Gráfico: energia registrada',
        text: 'Evolução da energia informada ao concluir os estudos.',
        chart: {
          title: 'Energia por registro',
          color: [250, 204, 21],
          unit: 'number',
          data: focusEnergyData.map((item) => ({ label: item.date, value: Number(item.energia || 0) }))
        }
      },
      {
        title: 'Calendário planejado x executado',
        text: 'Cada bloco mostra o tempo previsto, o tempo efetivamente registrado e o percentual executado no período analisado.',
        headers: ['Data', 'Horário', 'Disciplina', 'Atividade', 'Planejado', 'Realizado', 'Execução', 'Status'],
        widths: [27, 19, 37, 39, 24, 25, 22, 26],
        rows: calendarRows.length ? calendarRows : [['-', '-', '-', 'Nenhum bloco planejado no período selecionado.', '-', '-', '-', '-']]
      },
      {
        title: 'Registros de execução',
        headers: ['Data', 'Disciplina', 'Atividade', 'Tempo', 'Questões', '% de acertos', 'Foco', 'Energia'],
        widths: [18, 42, 28, 19, 20, 18, 17, 20],
        rows: logRows.length ? logRows : [['-', '-', 'Nenhuma execução registrada no período selecionado.', '-', '-', '-', '-', '-']]
      },
      {
        title: 'Missões de Estudo',
        text: 'Acompanhe estas metas sem transformar o estudo em cobrança excessiva. A consistência é construída com passos possíveis.',
        headers: ['Missão', 'Progresso', 'Situação'],
        widths: [118, 32, 40],
        rows: missionRows
      },
      {
        title: 'Estratégias para a próxima semana',
        text: coachSuggestions.map((suggestion, index) => `${index + 1}. ${suggestion}`).join('\n\n')
      }
    ]);
  }

  async function downloadPlanPdf() {
    createPlanPdf().save(planPdfMeta().fileName);
  }

  async function downloadProgressPdf() {
    createProgressPdf().save(progressPdfMeta().fileName);
  }

  async function downloadLifePdf() {
    createLifePdf().save(lifePdfMeta().fileName);
  }

  function lifePdfMeta(): PdfMeta {
    return {
      title: 'Roda da Vida do Estudante',
      subtitle: 'Diagnóstico de equilíbrio e desempenho',
      fileName: 'roda-da-vida-francisneyliberato-estudante.pdf',
      participantName: lead.full_name,
      participantEmail: lead.email,
      participantWhatsapp: lead.whatsapp
    };
  }

  function createLifePdf() {
    const average = lifeData.length ? Math.round((lifeData.reduce((sum, item) => sum + item.nota, 0) / lifeData.length) * 10) / 10 : 0;
    const priorityArea = [...lifeData].sort((first, second) => first.nota - second.nota)[0];
    return createProfessionalPdf(lifePdfMeta(), [
      {
        title: 'Síntese da Roda da Vida',
        text: `Use este diagnóstico para escolher pequenos ajustes que fortaleçam a sua rotina de estudos. A área prioritária atual é ${priorityArea?.area || 'a definir'}.`,
        metrics: [{ label: 'Média das áreas', value: `${average}/10` }, { label: 'Área prioritária', value: priorityArea?.area || 'A definir' }]
      },
      { title: 'Notas por área da vida', headers: ['Área', 'Nota'], widths: [140, 40], rows: lifeData.map((item) => [item.area, `${item.nota}/10`]) },
      { title: 'Gráfico da Roda da Vida', radar: { title: 'Distribuição das notas por área da vida', color: [34, 211, 238], data: lifeData.map((item) => ({ label: item.label, value: item.nota })) } },
      { title: 'Plano de ação', text: 'Defina ações possíveis e acompanhe seus avanços. A constância é mais importante do que a perfeição.', headers: ['Área prioritária', 'Nota', 'Tarefa', 'Atividade prática', 'Prazo', 'Status'], widths: [26, 12, 31, 61, 25, 20], rows: lifeActionPlan.map((item, index) => { const area = item.focus_area || lifePriorityAreas[index % lifePriorityAreas.length]?.area || '-'; const note = lifeData.find((lifeItem) => lifeItem.area === area)?.nota; return [area, Number.isFinite(note) ? `${note}/10` : '-', item.task || '-', item.activity || '-', item.deadline ? formatDateBr(item.deadline) : '-', item.completed ? 'Concluída' : 'Em andamento']; }) }
    ]);
  }

  async function addSubject() {
    try {
      await postJson('/api/subjects', subjectForm);
      setSubjectForm({ ...subjectForm, name: '' });
      await refresh();
      setMessage('Disciplina salva.');
    } catch {
      setSubjects([{ ...subjectForm, id: `local-${Date.now()}`, priority: 8, recommended_percent: 10, recommended_hours: 2 }, ...subjects]);
      setSubjectForm({ ...subjectForm, name: '' });
    }
  }

  async function addPlan() {
    if (!hasDisciplineWeightSetup) {
      setActive('peso');
      setMessage('Preencha primeiro a aba Disciplinas e Prioridades. Ela define as prioridades e alimenta o Plano de Estudos com disciplina e horas sugeridas.');
      return;
    }
    const usesDiscipline = !isCrossSubjectActivity(planForm.activity_type) && !isWritingActivity(planForm.activity_type) && planForm.activity_type !== 'Outros';
    const crossSubjectActivity = isCrossSubjectActivity(planForm.activity_type);
    const item: WeeklyPlan = {
      ...planForm,
      id: planForm.id || `plan-${Date.now()}`,
      subject_name: crossSubjectActivity ? ALL_SUBJECTS_LABEL : usesDiscipline ? planForm.subject_name : planForm.activity_type,
      scheduled_date: planForm.scheduled_date || todayIso,
      day: planForm.scheduled_date ? dayNameFromDate(planForm.scheduled_date) : planForm.day,
      planned_minutes: Number(planForm.planned_minutes || Math.round(Number(planForm.duration_hours || 1) * 60)),
      duration_hours: Number(planForm.duration_hours || Number(planForm.planned_minutes || 60) / 60),
      planned_questions: ['Exercício', 'Simulado'].includes(planForm.activity_type) ? Number(planForm.planned_questions || 0) : 0,
      recurrence: planForm.activity_type === 'Outros' ? 'Não repetir' : planForm.recurrence,
      status: planForm.status || 'Planejado'
    };
    setWeeklyPlans((plans) => {
      const exists = plans.some((plan) => plan.id === item.id);
      return exists ? plans.map((plan) => plan.id === item.id ? item : plan) : [item, ...plans];
    });
    setPlanForm({ ...defaultPlanForm, scheduled_date: todayIso });
    setMessage((item.scheduled_date || todayIso) < todayIso ? 'Bloco retroativo salvo. Registre a execução realizada para que ele não fique como pendência.' : '⭐ Bloco salvo! Você acabou de transformar intenção em missão de estudo.');
    try {
      await postJson('/api/weekly-plan', item);
      await refresh();
    } catch {
      // O bloco permanece localmente até que o estudante faça uma nova tentativa de salvamento.
    }
  }

  async function saveDisciplineWeight() {
    const subjectName = disciplineWeightForm.subject_name.trim();
    if (!subjectName) {
      setMessage('Informe o nome da disciplina para calcular o peso.');
      return;
    }
    if (Number(disciplineWeightForm.exam_weight) <= 0 || Number(disciplineWeightForm.question_weight) <= 0) {
      setMessage('Informe o Peso do edital e o Número de questões - NQ antes de calcular a prioridade.');
      return;
    }
    const item: DisciplineWeight = {
      ...disciplineWeightForm,
      id: disciplineWeightForm.id || `dw-${Date.now()}`,
      subject_name: subjectName,
      exam_weight: Math.max(0.1, Number(disciplineWeightForm.exam_weight)),
      question_weight: Math.max(1, Number(disciplineWeightForm.question_weight)),
      discipline_size: Math.min(3, Math.max(1, Number(disciplineWeightForm.discipline_size || 1))),
      difficulty_level: Math.min(3, Math.max(1, Number(disciplineWeightForm.difficulty_level || 1)))
    };
    setDisciplineWeights((items) => {
      const exists = items.some((current) => current.id === item.id);
      return exists ? items.map((current) => current.id === item.id ? item : current) : [item, ...items];
    });
    setDisciplineWeightForm({ id: '', subject_name: '', exam_weight: 0, question_weight: 0, discipline_size: 1, difficulty_level: 1 });
    try {
      await postJson('/api/discipline-weights', item);
      await refresh();
      setMessage('🏅 Disciplina calculada e salva com segurança. Mais clareza para estudar o que realmente aproxima você da aprovação.');
    } catch {
      setMessage('🏅 Disciplina calculada neste dispositivo, mas ainda não confirmada na nuvem. Tente salvar novamente para concluir o registro.');
    }
  }

  async function deleteDisciplineWeight(weightId: string) {
    setDisciplineWeights((items) => items.filter((item) => item.id !== weightId));
    try {
      await api(`/api/discipline-weights/${encodeURIComponent(weightId)}`, { method: 'DELETE' });
      setMessage('Disciplina removida do cálculo de prioridade.');
    } catch {
      setMessage('A remoção foi mantida neste dispositivo, mas ainda não foi confirmada na nuvem. Tente novamente para concluir a exclusão.');
    }
  }

  async function addLog() {
    try {
      const response = await postJson<{ xp: number; accuracy: number }>('/api/study-logs', logForm);
      await refresh();
      setMessage(`Registro salvo. Pontos de Evolução conquistados: ${response.xp}.`);
    } catch {
      setStudyLogs([{ ...logForm, id: `local-${Date.now()}` }, ...studyLogs]);
    }
  }

  function generateWeekPlan() {
    if (!hasDisciplineWeightSetup) {
      setActive('peso');
      setMessage('Preencha primeiro a aba Disciplinas e Prioridades para gerar sua semana com prioridades reais do seu edital.');
      return;
    }
    const subjectsForPlan = disciplineWeightResults.length
      ? disciplineWeightResults.slice(0, 7).map((item) => ({
        name: item.subject_name,
        recommended_hours: item.cycle_hours,
        questions: item.question_weight,
        subject_weight: Math.min(5, Math.max(1, Math.round(item.normalizedExamWeight || item.exam_weight)))
      }))
      : (subjects.length ? subjects : demoSubjects).slice(0, 7);
    const blocks = subjectsForPlan.map((subject, index) => {
      const date = dateToIso(addDays(new Date(`${todayIso}T00:00:00`), index));
      return {
        ...defaultPlanForm,
        id: `plan-${Date.now()}-${index}`,
        week_label: `Minha semana: ${weekLabel}`,
        scheduled_date: date,
        day: dayNameFromDate(date),
        start_time: index % 2 === 0 ? '19:00' : '07:00',
        activity_type: index % 3 === 0 ? 'Exercício' : 'Estudo',
        subject_name: subject.name,
        content: index % 3 === 0 ? 'Bateria de questões' : 'Estudo dirigido',
        planned_questions: index % 3 === 0 ? 20 : 0,
        subject_weight: Number('subject_weight' in subject ? subject.subject_weight : 3),
        planned_minutes: Math.round(Number(subject.recommended_hours || 1.5) * 60),
        duration_hours: Number(subject.recommended_hours || 1.5),
        status: 'Planejado'
      };
    });
    setWeeklyPlans((plans) => [...blocks, ...plans]);
    setMessage('✨ Semana gerada! Sua trilha de missões está pronta para virar execução.');
  }

  function convertAssistantBlocks(config: PlanConfiguration, blocks: GeneratedStudyBlock[], confirmed: boolean): WeeklyPlan[] {
    const activityMap = { review: 'Memorização/Revisão', theory: 'Estudo', exercises: 'Exercício', simulation: 'Simulado', writing: 'Redação ou Prova Dissertativa' } as const;
    const confirmedAt = confirmed ? new Date().toISOString() : undefined;
    return blocks.map((block) => ({
      id: block.id,
      week_label: `Semana ${block.weekNumber}`,
      day: dayNameFromDate(block.scheduledDate),
      start_time: block.startTime,
      end_time: block.endTime,
      duration_hours: block.durationMinutes / 60,
      activity_type: activityMap[block.activity],
      subject_name: block.subjectName,
      status: confirmed ? 'Planejado' : 'Rascunho',
      scheduled_date: block.scheduledDate,
      content: block.activityLabel,
      planned_minutes: block.durationMinutes,
      planned_questions: block.estimatedQuestions,
      priority: block.priority,
      study_plan_id: config.id,
      plan_week_id: `${config.id}-week-${block.weekNumber}`,
      week_number: block.weekNumber,
      generation_source: block.source,
      generation_key: block.generationKey,
      draft_status: confirmed ? 'confirmed' : 'draft',
      confirmed_at: confirmedAt
    }));
  }

  function versionFileMetadata() {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    const stamp = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}h${pad(now.getMinutes())}m${pad(now.getSeconds())}s`;
    return { fileName: `Plano de Estudos - ${stamp}`, savedAt: now.toISOString() };
  }

  async function syncPlanAssistant(config: PlanConfiguration, blocks: GeneratedStudyBlock[], confirmed: boolean, saveVersion = false): Promise<SavedPlanVersion | null> {
    // Só um cadastro vinculado à sessão do estudante pode alimentar Estudar Agora.
    // Isso evita que um plano local seja apresentado como salvo na nuvem.
    if (!getStudentSession()) {
      const leadConnected = await saveLead({ silent: true });
      if (!leadConnected || !getStudentSession()) {
        setMessage('Conecte seu cadastro antes de salvar o plano. Depois de validado, os blocos confirmados aparecerão automaticamente em Estudar Agora.');
        return null;
      }
    }
    const converted = convertAssistantBlocks(config, blocks, confirmed);
    setWeeklyPlans((plans) => [
      ...converted,
      ...plans.filter((plan) => !(plan.study_plan_id === config.id && Number(plan.week_number || 0) === config.currentWeek))
    ]);
    const weeklyMinutes = config.availability.reduce((sum, day) => sum + (day.enabled ? day.ranges.reduce((rangeSum, range) => {
      const [startHour, startMinute] = range.start.split(':').map(Number);
      const [endHour, endMinute] = range.end.split(':').map(Number);
      return rangeSum + Math.max(0, endHour * 60 + endMinute - startHour * 60 - startMinute - Number(range.breakMinutes || 0));
    }, 0) : 0), 0);
    try {
      const metadata = saveVersion ? versionFileMetadata() : null;
      await postJson<{ saved_version?: SavedPlanVersion }>('/api/study-plan', {
        ...config,
        status: confirmed ? 'confirmed' : 'draft',
        lead_id: leadId || null,
        weeklyMinutes: Math.min(weeklyMinutes, config.manualWeeklyMinutes || Number.MAX_SAFE_INTEGER),
        config,
        availability: config.availability,
        activities: config.activities,
        subjects: config.subjects,
        save_version: false,
        snapshot_file_name: undefined,
        snapshot_saved_at: undefined,
        blocks: converted
      });
      const weekResponse = await postJson<{ id: string }>('/api/plan-weeks', {
        id: `${config.id}-week-${config.currentWeek}`,
        plan_id: config.id,
        week_number: config.currentWeek,
        start_date: blocks[0]?.scheduledDate || config.startDate,
        end_date: blocks.length ? blocks[blocks.length - 1].scheduledDate : config.endDate,
        status: confirmed ? 'confirmed' : 'draft',
        target_minutes: converted.reduce((sum, plan) => sum + Number(plan.planned_minutes || 0), 0),
        generation_source: blocks.some((block) => block.source === 'recalculated') ? 'recalculated' : 'generated',
        generation_key: blocks[0]?.generationKey,
        confirmed_at: confirmed ? new Date().toISOString() : null,
        summary: { blocks: blocks.length, questions: blocks.reduce((sum, block) => sum + block.estimatedQuestions, 0) }
      });
      await Promise.all(converted.map((plan) => postJson('/api/weekly-plan', { ...plan, plan_week_id: weekResponse.id, skip_version: true })));
      if (confirmed) await postJson('/api/plan-confirm', {
        plan_id: config.id,
        plan_week_id: weekResponse.id,
        week_number: config.currentWeek,
        generation_key: blocks[0]?.generationKey,
        lead_id: leadId || null
      });
      // A versao historica so e criada depois que todos os blocos foram gravados.
      // Assim, o snapshot representa exatamente a semana que Estudar Agora deve usar.
      const savedPlan = saveVersion ? await postJson<{ saved_version?: SavedPlanVersion }>('/api/study-plan', {
        ...config,
        status: confirmed ? 'confirmed' : 'draft',
        lead_id: leadId || null,
        weeklyMinutes: Math.min(weeklyMinutes, config.manualWeeklyMinutes || Number.MAX_SAFE_INTEGER),
        config,
        availability: config.availability,
        activities: config.activities,
        subjects: config.subjects,
        save_version: true,
        snapshot_file_name: metadata?.fileName,
        snapshot_saved_at: metadata?.savedAt,
        blocks: converted
      }) : null;
      if (confirmed) await refresh();
      const savedVersion = savedPlan?.saved_version || null;
      if (savedVersion && !confirmed) setSavedPlanVersions((versions) => [savedVersion, ...versions]);
      return savedVersion;
    } catch {
      setMessage('Não foi possível concluir o salvamento na nuvem agora. O rascunho permanece neste dispositivo; tente salvar novamente para concluir a sincronização.');
      return null;
    }
  }

  function savePlanAssistantDraft(config: PlanConfiguration, blocks: GeneratedStudyBlock[]) {
    setPlanAssistantSnapshot(config);
    setWeekStartDate(blocks[0]?.scheduledDate || config.startDate);
    setWeekEndDate(blocks[blocks.length - 1]?.scheduledDate || config.endDate);
    void syncPlanAssistant(config, blocks, config.status === 'confirmed');
  }

  async function confirmPlanAssistant(config: PlanConfiguration, blocks: GeneratedStudyBlock[]) {
    setPlanAssistantSnapshot(config);
    setWeekStartDate(blocks[0]?.scheduledDate || config.startDate);
    setWeekEndDate(blocks[blocks.length - 1]?.scheduledDate || config.endDate);
    return syncPlanAssistant(config, blocks, true, true);
  }

  function createRecoveryPlan(plan: WeeklyPlan, scheduledDate: string, source: 'manual' | 'recalculated', startTime = plan.start_time) {
    const recoveryId = `recovery-${Date.now()}-${crypto.randomUUID()}`;
    const plannedSeconds = Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0) * 60;
    const executedSeconds = executedSecondsByBlock.get(String(plan.id || '')) || 0;
    const remainingMinutes = Math.max(1, Math.ceil((plannedSeconds - executedSeconds) / 60));
    const endTime = startTime ? planEndTime({ ...plan, start_time: startTime, end_time: undefined, planned_minutes: remainingMinutes, duration_hours: remainingMinutes / 60 }) : plan.end_time;
    return {
      ...plan,
      id: recoveryId,
      planned_minutes: remainingMinutes,
      duration_hours: remainingMinutes / 60,
      scheduled_date: scheduledDate,
      day: dayNameFromDate(scheduledDate),
      start_time: startTime,
      end_time: endTime,
      status: 'Planejado',
      rescheduled_from_id: plan.id,
      completed_at: undefined,
      generation_source: source,
      generation_key: `recovery:${plan.id || recoveryId}:${scheduledDate}:${startTime}`,
      confirmed_at: new Date().toISOString()
    };
  }

  function persistRecoveryPlans(plans: WeeklyPlan[]) {
    void (async () => {
      for (let index = 0; index < plans.length; index += 1) {
        await postJson('/api/weekly-plan', { ...plans[index], skip_version: index < plans.length - 1 });
      }
      await refresh();
    })().catch(() => {
      setMessage('Os ajustes foram preservados neste dispositivo, mas ainda não foram confirmados na nuvem. Tente novamente para concluir o salvamento.');
    });
  }

  function schedulePendingStudy(plan: WeeklyPlan, scheduledDate: string, startTime: string) {
    const originalId = String(plan.id || '');
    if (!originalId || scheduledDate < todayIso || !startTime) {
      setMessage('Informe uma data válida e o horário para recuperar esta pendência.');
      return;
    }
    const recovery = createRecoveryPlan(plan, scheduledDate, 'manual', startTime);
    const original = { ...plan, status: 'reagendado' };
    setWeeklyPlans((plans) => [recovery, ...plans.map((item) => item.id === originalId ? original : item)]);
    setPendingRescheduleSlots((slots) => {
      const next = { ...slots };
      delete next[originalId];
      return next;
    });
    persistRecoveryPlans([original, recovery]);
    setMessage(`Pendência de ${formatDateBr(String(plan.scheduled_date || todayIso))} agendada para ${formatDateBr(scheduledDate)} às ${startTime}. Você está retomando o controle do seu plano.`);
  }

  function recalculatePlanWithPendingStudies() {
    if (!pendingStudyPlans.length) {
      setMessage('Não há pendências anteriores para redistribuir. Continue firme no plano de hoje.');
      return;
    }
    const originalIds = new Set(pendingStudyPlans.map((plan) => String(plan.id || '')).filter(Boolean));
    const recoveries = pendingStudyPlans.map((plan, index) => createRecoveryPlan(plan, dateToIso(addDays(new Date(`${todayIso}T12:00:00`), index)), 'recalculated'));
    const originals = pendingStudyPlans.map((plan) => ({ ...plan, status: 'reagendado' }));
    setWeeklyPlans((plans) => [
      ...recoveries,
      ...plans.map((plan) => originalIds.has(String(plan.id || '')) ? { ...plan, status: 'reagendado' } : plan)
    ]);
    persistRecoveryPlans([...originals, ...recoveries]);
    setActive('plano');
      setMessage(`Plano de Estudos recalculado: ${pluralize(recoveries.length, 'pendência')} ${recoveries.length === 1 ? 'foi distribuída' : 'foram distribuídas'} a partir de hoje, sem apagar seu histórico.`);
  }

  async function duplicatePlan(plan: WeeklyPlan) {
    const duplicate = { ...plan, id: `plan-${Date.now()}`, status: 'Planejado', completed_at: undefined, rescheduled_from_id: undefined };
    setWeeklyPlans((plans) => [duplicate, ...plans]);
    setMessage('🔁 Bloco duplicado. Boa estratégia: repetição inteligente constrói domínio.');
    try {
      await postJson('/api/weekly-plan', duplicate);
      await refresh();
    } catch {
      setMessage('A duplicação foi preservada neste dispositivo, mas ainda não foi confirmada na nuvem. Tente novamente para concluir o salvamento.');
    }
  }

  async function deletePlan(planId?: string) {
    if (!planId) return;
    if (!window.confirm('Excluir este bloco de estudo?')) return;
    const previousPlans = weeklyPlans;
    setWeeklyPlans((plans) => plans.filter((plan) => plan.id !== planId));
    setMessage('Bloco excluído. Ajustar o plano também é parte da evolução.');
    try {
      await deleteJson(`/api/weekly-plan?id=${encodeURIComponent(planId)}`);
      await refresh();
    } catch {
      setWeeklyPlans(previousPlans);
      setMessage('Não foi possível confirmar a exclusão na nuvem. O bloco foi restaurado para evitar perda de informações.');
    }
  }

  function startStudy(block: WeeklyPlan) {
    if (!block.id) return;
    if (activeStudyBlockId === block.id) return;
    const scheduledDate = String(block.scheduled_date || todayIso).slice(0, 10);
    if (planIsExpired(block, scheduledDate, todayIso)) {
      setMessage('O horário deste bloco já terminou. Registre a execução retroativa ou reorganize sua pendência.');
      return;
    }
    const previousExecution = studyLogs.find((log) => String(log.study_block_id || '') === String(block.id));
    const previousSeconds = studyLogElapsedSeconds(previousExecution);
    autoSavingStudyRef.current = false;
    setActiveStudyBlockId(block.id);
    setStudyElapsedSeconds(previousSeconds);
    setStudyStartedAt(previousExecution?.started_at || new Date().toISOString());
    setFinishForm({
      ...defaultFinishForm,
      questions_done: Number(previousExecution?.questions_done || previousExecution?.completed_questions || 0),
      correct_answers: Number(previousExecution?.correct_answers || 0),
      perceived_difficulty: previousExecution?.perceived_difficulty || defaultFinishForm.perceived_difficulty,
      notes: previousExecution?.notes || ''
    });
    setStudyTimerRunning(true);
    setMessage('🚀 Missão iniciada! Foque no próximo bloco: aprovação é construída minuto a minuto.');
  }

  function finishStudy() {
    if (!activeStudyBlock) return;
    if (activityRequiresQuestions(activeStudyBlock.activity_type)) {
      const questions = Number(finishForm.questions_done || 0);
      const correct = Number(finishForm.correct_answers || 0);
      if (questions < 1) {
        setMessage('Informe a quantidade exata de questões executadas antes de concluir este bloco.');
        return;
      }
      if (correct < 0 || correct > questions) {
        setMessage('A quantidade de acertos deve ser igual ou menor que a quantidade executada.');
        return;
      }
    }
    completeStudy(activeStudyBlock);
  }

  function completeStudy(block: WeeklyPlan | null, extra = false) {
    const manualExtraMinutes = Math.round(Number(logForm.completed_hours || logForm.planned_hours || 0) * 60);
    const actualSeconds = Math.max(1, extra ? manualExtraMinutes * 60 : studyElapsedSeconds);
    const actualMinutes = Math.max(1, Math.ceil(actualSeconds / 60));
    const plannedMinutes = Number(block?.planned_minutes || Math.round(Number(block?.duration_hours || logForm.planned_hours || 0) * 60) || actualMinutes);
    const questions = Number(finishForm.questions_done || 0);
    const correct = Number(finishForm.correct_answers || 0);
    const status = extra ? 'extra' : actualSeconds >= plannedMinutes * 60 ? 'concluído' : 'parcial';
    const activityType = block?.activity_type || logForm.activity_type;
    const previousExecution = !extra && block?.id ? studyLogs.find((item) => String(item.study_block_id || '') === String(block.id)) : undefined;
    const log: StudyLog = {
      ...logForm,
      id: previousExecution?.id || `log-${Date.now()}`,
      study_block_id: extra ? null : block?.id || null,
      plan_week_id: extra ? null : block?.plan_week_id || null,
      date: extra ? todayIso : String(block?.scheduled_date || todayIso).slice(0, 10),
      subject_name: block?.subject_name || logForm.subject_name,
      content: block?.content || logForm.content,
      activity_type: activityType,
      planned_hours: plannedMinutes / 60,
      completed_hours: actualSeconds / 3600,
      actual_minutes: actualMinutes,
      actual_seconds: actualSeconds,
      planned_questions: Number(block?.planned_questions || logForm.planned_questions || 0),
      completed_questions: questions,
      questions_done: questions,
      correct_answers: correct,
      accuracy_percentage: questions ? Math.round((correct / questions) * 100) : 0,
      perceived_difficulty: finishForm.perceived_difficulty,
      is_extra: extra,
      started_at: previousExecution?.started_at || studyStartedAt || new Date().toISOString(),
      started_on_time: Boolean(block?.scheduled_date && block?.start_time && studyStartedAt && Math.abs(new Date(studyStartedAt).getTime() - new Date(`${block.scheduled_date}T${block.start_time}:00`).getTime()) <= 15 * 60 * 1000),
      finished_at: new Date().toISOString(),
      review_done: activityType === 'Memorização/Revisão' || logForm.review_done,
      simulation_done: activityType === 'Simulado' || logForm.simulation_done,
      essay_done: isWritingActivity(activityType) || logForm.essay_done,
      notes: finishForm.notes
    };
    setStudyLogs((logs) => [log, ...logs.filter((item) => item.study_block_id !== log.study_block_id || !log.study_block_id)]);
    const updatedBlock = block?.id ? { ...block, status, completed_at: log.finished_at } : null;
    if (updatedBlock) setWeeklyPlans((plans) => plans.map((plan) => plan.id === updatedBlock.id ? updatedBlock : plan));
    setActiveStudyBlockId(null);
    setStudyTimerRunning(false);
    setStudyElapsedSeconds(0);
    setStudyStartedAt(null);
    setFinishForm(defaultFinishForm);
    setMessage(status === 'concluído'
      ? '✅ Missão concluída! Progresso registrado: constância hoje, resultado amanhã.'
      : '⏱️ Execução parcial registrada. O tempo realizado foi preservado e você poderá continuar este bloco antes do horário final.');
    void Promise.all([
      postJson('/api/study-logs', log),
      ...(updatedBlock ? [postJson('/api/weekly-plan', updatedBlock)] : [])
    ]).then(() => refresh()).catch(() => undefined);
  }

  function registerRetroactiveExecution(block: WeeklyPlan) {
    if (!block.id || !block.scheduled_date) return;
    const draft = retroactiveExecutionDrafts[block.id] || { minutes: Number(block.planned_minutes || Math.round(Number(block.duration_hours || 0) * 60) || 0), questions: 0, correct: 0 };
    const actualMinutes = Math.max(0, Number(draft.minutes || 0));
    if (!actualMinutes) {
      setMessage('Informe o tempo efetivamente estudado para registrar a execução retroativa.');
      return;
    }
    const plannedMinutes = Number(block.planned_minutes || Math.round(Number(block.duration_hours || 0) * 60) || actualMinutes);
    const questions = Math.max(0, Number(draft.questions || 0));
    const correct = Math.min(questions, Math.max(0, Number(draft.correct || 0)));
    const startedAt = `${block.scheduled_date}T${block.start_time || '00:00'}:00`;
    const log: StudyLog = {
      ...defaultLogForm,
      id: `retroactive-log-${Date.now()}-${crypto.randomUUID()}`,
      study_block_id: block.id,
      plan_week_id: block.plan_week_id || null,
      date: block.scheduled_date,
      subject_name: block.subject_name,
      content: block.content || '',
      activity_type: block.activity_type,
      planned_hours: plannedMinutes / 60,
      completed_hours: actualMinutes / 60,
      actual_minutes: actualMinutes,
      actual_seconds: actualMinutes * 60,
      planned_questions: Number(block.planned_questions || 0),
      completed_questions: questions,
      questions_done: questions,
      correct_answers: correct,
      accuracy_percentage: questions ? Math.round(correct / questions * 100) : 0,
      is_extra: false,
      started_at: startedAt,
      started_on_time: false,
      finished_at: startedAt,
      review_done: block.activity_type === 'Memorização/Revisão',
      simulation_done: block.activity_type === 'Simulado',
      essay_done: isWritingActivity(block.activity_type),
      notes: 'Execução retroativa registrada manualmente.'
    };
    const status = actualMinutes >= plannedMinutes ? 'concluído' : 'parcial';
    const updatedPlan = { ...block, status, completed_at: new Date().toISOString() };
    setStudyLogs((logs) => [log, ...logs.filter((item) => item.study_block_id !== block.id)]);
    setWeeklyPlans((plans) => plans.map((plan) => plan.id === block.id ? updatedPlan : plan));
    setRetroactiveExecutionDrafts((drafts) => {
      const next = { ...drafts };
      delete next[block.id!];
      return next;
    });
    void Promise.all([postJson('/api/study-logs', log), postJson('/api/weekly-plan', updatedPlan)]).then(() => refresh()).catch(() => undefined);
    setMessage('Execução retroativa registrada. O bloco foi atualizado sem gerar pendência.');
  }

  function updateRetroactiveExecutionDraft(planId: string, defaultMinutes: number, patch: Partial<{ minutes: number; questions: number; correct: number }>) {
    setRetroactiveExecutionDrafts((drafts) => {
      const current = drafts[planId] || { minutes: defaultMinutes, questions: 0, correct: 0 };
      return { ...drafts, [planId]: { ...current, ...patch } };
    });
  }

  async function resetTestData(adminSession: string) {
    await api('/api/admin/reset-test-data', { method: 'POST', headers: { 'x-admin-session': adminSession } });
    clearTestDataStorage();
    window.location.reload();
  }

  async function downloadAdminDashboardPdf(filters: { session: string; startDate: string; endDate: string; startTime: string; endTime: string }) {
    if ((filters.startTime && !filters.startDate) || (filters.endTime && !filters.endDate)) {
      throw new Error('Informe a data correspondente ao horário inicial ou final.');
    }
    const toLocalIso = (date: string, time: string, isEnd: boolean) => {
      if (!date) return '';
      const localDate = new Date(`${date}T${time || (isEnd ? '23:59:59' : '00:00:00')}`);
      return Number.isNaN(localDate.getTime()) ? '' : localDate.toISOString();
    };
    const startAt = toLocalIso(filters.startDate, filters.startTime, false);
    const endAt = toLocalIso(filters.endDate, filters.endTime, true);
    if ((filters.startDate && !startAt) || (filters.endDate && !endAt)) {
      throw new Error('Confira as datas e os horários informados.');
    }

    const report = await api<{
        filters: { start_at: string | null; end_at: string | null; matched_users: number };
        overview: { total_hours: number; total_questions: number; total_correct: number; average_accuracy: number; total_simulations: number; total_essays: number; total_reviews: number; longest_streak_days: number; platform_level: number; average_week_completion: number; weeks_tracked: number };
        top_subjects: Array<{ name: string; hours: number }>;
        activity_hours: Array<{ name: string; hours: number }>;
        subject_accuracy: Array<{ name: string; questions: number; correct: number; accuracy: number }>;
        weekly_trend: Array<{ date: string; hours: number }>;
        ranking: Array<{ rank: number; average_completion: number; hours_invested: number; weeks_tracked: number }>;
      }>('/api/admin/dashboard-pdf', {
        method: 'POST',
        headers: { 'x-admin-session': filters.session },
        body: JSON.stringify({ start_at: startAt || undefined, end_at: endAt || undefined })
      });
      const formatFilter = (value: string | null) => value
        ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
        : 'Sem filtro';
      const filterSummary = report.filters.start_at || report.filters.end_at
        ? `Registros de usuários que realizaram o teste entre ${formatFilter(report.filters.start_at)} e ${formatFilter(report.filters.end_at)}.`
        : 'Relatório com todos os registros de usuários que realizaram o teste.';
      const hasStudyData = report.overview.total_hours > 0 || report.overview.total_questions > 0;
      const pdf = createProfessionalPdf({
        title: 'Dashboard da Comunidade',
        subtitle: 'Dados consolidados da plataforma',
        fileName: `dashboard-comunidade-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.pdf`
      }, [
        {
          title: 'Filtro do relatório',
          text: `${filterSummary} Participantes encontrados: ${report.filters.matched_users}.`
        },
        {
          title: 'Visão geral',
          text: hasStudyData ? 'Indicadores consolidados de estudo, execução e desempenho do período selecionado.' : 'Não há registros de estudo para os filtros informados.',
          metrics: [
            { label: 'Horas registradas', value: `${report.overview.total_hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h` },
            { label: 'Questões realizadas', value: String(report.overview.total_questions) },
            { label: 'Percentual de acertos', value: `${report.overview.average_accuracy}%` },
            { label: 'Cumprimento médio', value: `${report.overview.average_week_completion}%` },
            { label: 'Simulados', value: String(report.overview.total_simulations) },
            { label: 'Redações ou dissertativas', value: String(report.overview.total_essays) }
          ]
        },
        {
          title: 'Gráfico: disciplinas com maior dedicação',
          text: 'Horas consolidadas registradas por disciplina no recorte selecionado.',
          chart: {
            title: 'Horas registradas por disciplina',
            color: [52, 211, 153],
            unit: 'hours',
            data: report.top_subjects.map((item) => ({ label: item.name, value: item.hours }))
          }
        },
        {
          title: 'Gráfico: tipos de atividade realizados',
          text: 'Distribuição das horas de estudo entre as atividades registradas pelos usuários.',
          chart: {
            title: 'Horas por tipo de atividade',
            color: [167, 139, 250],
            unit: 'hours',
            data: report.activity_hours.map((item) => ({ label: item.name, value: item.hours }))
          }
        },
        {
          title: 'Gráfico: percentual de acertos por disciplina',
          text: 'Aproveitamento das questões registradas, organizado por disciplina.',
          chart: {
            title: 'Percentual de acertos',
            color: [250, 204, 21],
            unit: 'percent',
            data: report.subject_accuracy.map((item) => ({ label: item.name, value: item.accuracy }))
          }
        },
        {
          title: 'Disciplinas com mais horas registradas',
          headers: ['Disciplina', 'Horas registradas'],
          widths: [70, 30],
          rows: report.top_subjects.length ? report.top_subjects.map((item) => [item.name, `${item.hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h`]) : [['Sem registros no período', '-']]
        },
        {
          title: 'Atividades realizadas',
          headers: ['Atividade', 'Horas registradas'],
          widths: [70, 30],
          rows: report.activity_hours.length ? report.activity_hours.map((item) => [item.name, `${item.hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h`]) : [['Sem registros no período', '-']]
        },
        {
          title: 'Desempenho por disciplina',
          headers: ['Disciplina', 'Questões', 'Acertos', 'Percentual'],
          widths: [48, 17, 17, 18],
          rows: report.subject_accuracy.length ? report.subject_accuracy.map((item) => [item.name, String(item.questions), String(item.correct), `${item.accuracy}%`]) : [['Sem questões registradas', '-', '-', '-']]
        },
        {
          title: 'Evolução diária de horas',
          headers: ['Data', 'Horas registradas'],
          widths: [40, 60],
          rows: report.weekly_trend.length ? report.weekly_trend.map((item) => [formatFullDateBr(item.date), `${item.hours.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h`]) : [['Sem registros no período', '-']]
        },
        {
          title: 'Classificação por constância e cumprimento',
          headers: ['Posição', 'Cumprimento médio', 'Horas investidas', 'Semanas'],
          widths: [18, 32, 28, 22],
          rows: report.ranking.length ? report.ranking.map((item) => [`${item.rank}ª`, `${item.average_completion}%`, `${item.hours_invested.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h`, String(item.weeks_tracked)]) : [['Sem classificação no período', '-', '-', '-']]
        }
      ]);
    pdf.save(`dashboard-comunidade-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.pdf`);
  }

  async function submitEditoraContact() {
    const fullName = editoraContact.full_name.trim();
    const email = editoraContact.email.trim().toLowerCase();
    const whatsappDigits = editoraContact.whatsapp.replace(/\D/g, '');
    const contactMessage = editoraContact.message.trim();
    const signature = JSON.stringify({ fullName, email, whatsapp: whatsappDigits, contactMessage });

    setEditoraStatus({ type: '', text: '' });
    if (editoraSending) return;
    if (signature === editoraLastSignature) {
      setEditoraStatus({ type: 'error', text: 'Sua mensagem já foi enviada. Para enviar novamente, altere alguma informação.' });
      return;
    }
    if (fullName.length < 5) {
      setEditoraStatus({ type: 'error', text: 'Informe seu nome completo para que a Editora Liberato possa falar com você com carinho e segurança.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email)) {
      setEditoraStatus({ type: 'error', text: 'Informe um e-mail válido para receber o retorno da Editora Liberato.' });
      return;
    }
    if (whatsappDigits.length < 10 || whatsappDigits.length > 14) {
      setEditoraStatus({ type: 'error', text: 'Informe um WhatsApp válido com DDD. Assim o contato fica mais rápido e acolhedor.' });
      return;
    }
    if (contactMessage.length < 10) {
      setEditoraStatus({ type: 'error', text: 'Escreva uma mensagem com pelo menos 10 caracteres contando um pouco sobre sua ideia de livro.' });
      return;
    }

    setEditoraSending(true);
    try {
      await postJson('/api/editora-contact', editoraContact);
      setEditoraLastSignature(signature);
      setEditoraContact({ ...defaultEditoraContact, started_at: new Date().toISOString() });
      setEditoraStatus({ type: 'success', text: 'Mensagem enviada com sucesso. Que alegria: sua ideia deu o primeiro passo para virar livro!' });
    } catch (error) {
      setEditoraStatus({ type: 'error', text: error instanceof Error ? error.message : 'Não foi possível enviar agora. Seus dados foram preservados para você tentar novamente.' });
    } finally {
      setEditoraSending(false);
    }
  }

  async function submitFeedback() {
    const fullName = feedbackContact.full_name.trim();
    const email = feedbackContact.email.trim().toLowerCase();
    const whatsappDigits = feedbackContact.whatsapp.replace(/\D/g, '');
    const messageText = feedbackContact.message.trim();
    const signature = JSON.stringify({ fullName, email, whatsappDigits, messageText });
    setFeedbackStatus({ type: '', text: '' });
    if (feedbackSending) return;
    if (signature === feedbackLastSignature) { setFeedbackStatus({ type: 'error', text: 'Esta sugestão já foi enviada. Altere alguma informação para enviar novamente.' }); return; }
    if (fullName.length < 5 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(email) || whatsappDigits.length < 10 || whatsappDigits.length > 14 || messageText.length < 10) { setFeedbackStatus({ type: 'error', text: 'Preencha nome completo, e-mail, WhatsApp e uma sugestão com pelo menos 10 caracteres.' }); return; }
    setFeedbackSending(true);
    try {
      await postJson('/api/editora-contact', { ...feedbackContact, purpose: 'feedback' });
      setFeedbackLastSignature(signature);
      setFeedbackContact({ ...defaultFeedbackContact, started_at: new Date().toISOString() });
      setFeedbackStatus({ type: 'success', text: 'Obrigado pela contribuição. Sua sugestão foi enviada com sucesso e ajudará a aprimorar a plataforma.' });
    } catch (error) {
      setFeedbackStatus({ type: 'error', text: error instanceof Error ? error.message : 'Não foi possível enviar agora. Seus dados foram preservados para uma nova tentativa.' });
    } finally { setFeedbackSending(false); }
  }

  const visibleNavItems = navItems;
  const activeNavigationIndex = visibleNavItems.findIndex((item) => item.id === active);
  const previousNavigationItem = activeNavigationIndex > 0 ? visibleNavItems[activeNavigationIndex - 1] : null;
  const nextNavigationItem = activeNavigationIndex >= 0 && activeNavigationIndex < visibleNavItems.length - 1 ? visibleNavItems[activeNavigationIndex + 1] : null;
  const activeNavigationItem = activeNavigationIndex >= 0 ? visibleNavItems[activeNavigationIndex] : null;
  const activeNavigationGroup = activeNavigationItem ? navigationGroups.find((group) => group.id === activeNavigationItem.group) : null;
  const activeGroupItems = activeNavigationGroup ? visibleNavItems.filter((item) => item.group === activeNavigationGroup.id) : [];
  const activeGroupIndex = activeGroupItems.findIndex((item) => item.id === active);

  function navigateToMenuItem(item: (typeof navItems)[number]) {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    shouldFocusPageTitle.current = true;
    const lockedByLead = !leadSaved && !publicNavIds.has(item.id);
    const lockedByWeight = leadSaved && ['plano', 'registro'].includes(item.id) && !hasDisciplineWeightSetup;
    if (lockedByLead) {
      setActive('inicio');
      setMessage('Preencha o código de acesso e o cadastro para liberar esta área.');
      setMenuOpen(false);
      return;
    }
    if (lockedByWeight) {
      setActive('peso');
      setMessage('Preencha primeiro a aba Disciplinas e Prioridades. Depois o Plano de Estudos e o Estudar Agora serão liberados.');
      setMenuOpen(false);
      return;
    }
    setActive(item.id);
    setMenuOpen(false);
  }

  const nav = (
    <nav aria-label="Navegação principal da plataforma" className="grid min-w-0 gap-4">
      {navigationGroups.map((group, groupIndex) => {
        const groupItems = visibleNavItems.filter((item) => item.group === group.id);
        if (!groupItems.length) return null;
        return (
          <section key={group.id} aria-labelledby={`menu-group-${group.id}`} className={`grid min-w-0 gap-2 ${groupIndex > 0 ? 'border-t border-slate-700/75 pt-4' : ''}`}>
            <div className={`flex items-center gap-2 px-2 ${group.primary ? 'rounded-lg border border-cyan-300/25 bg-cyan-300/5 py-2' : 'pt-1'}`}>
              <span className={`h-5 w-1 rounded-full ${group.markerClass}`} aria-hidden="true" />
              <h2 id={`menu-group-${group.id}`} className={`text-[11px] font-black tracking-[0.14em] ${group.titleClass}`}>{group.label}</h2>
              {group.primary && <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-cyan-100">Jornada principal</span>}
            </div>
            <div className="grid min-w-0 gap-1">
              {groupItems.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                const lockedByLead = !leadSaved && !publicNavIds.has(item.id);
                const lockedByWeight = leadSaved && ['plano', 'registro'].includes(item.id) && !hasDisciplineWeightSetup;
                const locked = lockedByLead || lockedByWeight;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigateToMenuItem(item)}
                    aria-current={isActive ? 'page' : undefined}
                    aria-disabled={locked || undefined}
                    className={`focus-ring flex min-h-12 min-w-0 items-center gap-3 rounded-lg border-l-2 border-transparent px-3 text-left text-sm transition ${
                      isActive ? group.activeClass : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    } ${locked ? 'cursor-not-allowed opacity-45' : ''}`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? group.iconClass : 'text-slate-400'}`} />
                    <span className="min-w-0 flex-1 break-words font-medium">{item.label}</span>
                    {isActive && <span className="shrink-0 text-[10px] font-black uppercase tracking-wide text-current">Atual</span>}
                    {locked && <Lock className="h-3.5 w-3.5 shrink-0" aria-label="Área bloqueada" />}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen">
      <nav className="sticky top-0 z-40 border-b border-slate-800 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-2 px-3 py-3 sm:gap-4 sm:px-4">
          <button type="button" onClick={() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); setActive('inicio'); }} className="focus-ring flex min-w-0 flex-1 items-center gap-2 rounded-lg text-left sm:gap-3">
            <div className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg border border-cyan-200/60 bg-slate-950 shadow-[0_0_22px_rgba(34,211,238,0.32)]">
              <span className="absolute inset-x-0 bottom-0 h-2 bg-emerald-400" />
              <GraduationCap className="relative h-7 w-7 text-cyan-300" strokeWidth={2.5} />
              <span className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full border-2 border-slate-950 bg-yellow-300 text-slate-950">
                <Award className="h-3 w-3" strokeWidth={3} />
              </span>
            </div>
            <div className="hidden min-w-0 min-[420px]:block">
              <strong className="block truncate text-base text-white">Estudante de Alta Performance</strong>
              <span className="hidden text-xs text-slate-400 sm:block">Francisney Liberato | www.francisney.com.br</span>
            </div>
          </button>
          <div className="flex shrink-0 items-center gap-2">
            <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-950/55 p-1">
              <button type="button" title="Voltar" aria-label="Voltar" onClick={() => window.history.back()} className="focus-ring grid h-10 w-10 place-items-center rounded-md text-slate-100 transition hover:bg-slate-800 hover:text-cyan-100 sm:h-11 sm:w-11">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" title="Avançar" aria-label="Avançar" onClick={() => window.history.forward()} className="focus-ring grid h-10 w-10 place-items-center rounded-md text-slate-100 transition hover:bg-slate-800 hover:text-cyan-100 sm:h-11 sm:w-11">
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
            <button type="button" className="focus-ring grid h-12 w-12 place-items-center rounded-lg border border-slate-600 bg-slate-900/80 text-slate-100 transition hover:border-cyan-300 hover:bg-slate-800 lg:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}>
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </nav>

      <div className="mx-auto grid max-w-[1500px] gap-6 px-4 py-6 lg:grid-cols-[290px_minmax(0,1fr)]">
        <aside className={`no-print min-w-0 ${menuOpen ? 'fixed inset-x-3 bottom-3 top-[76px] z-50 block overflow-hidden lg:static lg:z-auto lg:overflow-visible' : 'hidden'} lg:block`}>
          <div className="glass h-full max-h-full min-w-0 overflow-y-auto overscroll-contain rounded-lg p-3 lg:sticky lg:top-24 lg:h-auto lg:max-h-[calc(100vh-120px)]">{nav}</div>
        </aside>

        <main className="min-w-0">
          <h1 ref={pageTitleRef} tabIndex={-1} className="sr-only">{activeNavigationItem?.label || 'Plataforma de estudos'}</h1>
          {message && (
            <div className="no-print mb-4 flex items-center gap-3 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-3 text-sm text-cyan-50">
              <ShieldCheck className="h-5 w-5 text-cyan-300" />
              {message}
            </div>
          )}

          {active === 'inicio' && (
            <section className="grid gap-6">
              <div className="glass overflow-hidden rounded-lg">
                <div className="grid gap-8 p-6 md:grid-cols-[1.15fr_.85fr] md:p-10">
                  <div className="self-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-300/30 px-3 py-2 text-sm text-cyan-100">
                      <Sparkles className="h-4 w-4" /> Diagnóstico, plano e acompanhamento de estudos
                    </div>
                    <h1 className="max-w-4xl text-4xl font-black leading-tight text-white md:text-6xl">
                      Estudante de Alta Performance
                    </h1>
                    <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                      Uma plataforma gamificada para transformar rotina, método, constância e indicadores em Missões de Estudo rumo à aprovação.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <Button onClick={() => document.getElementById('jornada-aprovacao')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                        <KeyRound className="h-5 w-5" /> Liberar acesso
                      </Button>
                      <Button tone="quiet" onClick={() => setActive('consulta')}>
                        <FileText className="h-5 w-5" /> Já tenho cadastro
                      </Button>
                    </div>
                  </div>
                  <div className="grid min-h-[390px] place-items-center rounded-lg border border-slate-700 bg-slate-950/60 p-4">
                    <div className="relative w-full max-w-[500px] overflow-hidden rounded-lg border border-slate-700 bg-slate-950/75 p-5 shadow-neon">
                      <div className="absolute inset-5 rounded-full border border-cyan-300/25" />
                      <div className="absolute inset-16 rounded-full border border-emerald-300/20" />
                      <div className="relative z-10 grid min-h-[300px] grid-cols-2 content-center gap-3 md:block">
                        <div className="col-span-2 mx-auto grid h-28 w-28 place-items-center rounded-lg border border-yellow-300 bg-yellow-300 p-3 text-center text-sm font-black text-slate-950 shadow-[0_0_28px_rgba(250,204,21,0.28)] md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2">
                          Plano de<br />Estudos<br />Estratégico
                        </div>
                        {[
                          { name: 'Direito', icon: '⚖️', color: '#22d3ee', className: 'md:left-2 md:top-7' },
                          { name: 'Português', icon: '📘', color: '#22d3ee', className: 'md:right-3 md:top-12' },
                          { name: 'Matemática', icon: '🧮', color: '#34d399', className: 'md:bottom-12 md:left-6' },
                          { name: 'Contabilidade', icon: '🏛️', color: '#34d399', className: 'md:bottom-8 md:right-4' }
                        ].map((subject) => (
                          <div
                            key={subject.name}
                            className={`z-20 min-w-0 rounded-lg border bg-slate-900/95 px-2 py-2 text-center text-xs font-bold text-white shadow-lg backdrop-blur md:absolute md:max-w-[190px] md:px-3 ${subject.className}`}
                            style={{ borderColor: subject.color, boxShadow: `0 0 18px ${subject.color}33` }}
                          >
                            <span className="mr-1">{subject.icon}</span>{subject.name}
                          </div>
                        ))}
                      </div>
                      <div className="relative z-10 mt-4 grid gap-2 sm:grid-cols-3">
                        {['Missões de Estudo diárias', 'Pontos de Evolução por execução', 'Meu Desempenho como guia'].map((item) => (
                          <div key={item} className="rounded-lg border border-slate-700 bg-slate-900/80 p-3 text-center text-xs font-bold text-slate-200">
                            ✨ {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <section id="jornada-aprovacao" className="glass overflow-hidden rounded-lg" aria-labelledby="jornada-aprovacao-title">
                <div className="grid lg:grid-cols-[minmax(280px,.82fr)_minmax(0,1.18fr)]">
                  <figure className="relative min-h-[390px] overflow-hidden bg-slate-950 sm:min-h-[460px] lg:min-h-full">
                    <img
                      src={francisneyJornadaConcursos}
                      alt="Francisney Liberato, criador da plataforma"
                      className="absolute inset-0 h-full w-full object-cover object-[center_22%]"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-950/70 to-transparent" />
                    <figcaption className="absolute bottom-5 left-5 right-5 text-sm font-bold text-white drop-shadow">
                      Francisney Liberato
                    </figcaption>
                  </figure>
                  <div className="p-6 sm:p-8 lg:p-10">
                    <div className="mb-4 inline-flex items-center gap-2 text-sm font-black uppercase text-yellow-200">
                      <Trophy className="h-4 w-4 text-yellow-300" /> {'Experi\u00eancia que se transforma em estrat\u00e9gia'}
                    </div>
                    <h2 id="jornada-aprovacao-title" className="max-w-3xl text-3xl font-black leading-tight text-white sm:text-4xl">
                      Uma plataforma criada por quem viveu a jornada
                    </h2>
                    <div className="mt-5 max-w-3xl space-y-4 text-base leading-7 text-slate-300 sm:text-lg sm:leading-8">
                      <p>{'Esta plataforma nasceu da minha pr\u00f3pria experi\u00eancia como concurseiro. Durante 20 dias \u00fateis, estudei cerca de 12 horas por dia para conquistar a aprova\u00e7\u00e3o em concursos p\u00fablicos. Nesse processo, precisei aprender n\u00e3o apenas o conte\u00fado, mas principalmente '}<strong className="font-bold text-white">{'como estudar com estrat\u00e9gia, organiza\u00e7\u00e3o e const\u00e2ncia'}</strong>.</p>
                      <p>{'Ao longo dessa jornada, desenvolvi t\u00e9cnicas, metodologias e ferramentas que deram origem aos livros '}<strong className="font-bold text-white">{'Como Passar nos Concursos \u2013 Volumes 1 e 2'}</strong>{' e '}<strong className="font-bold text-white">Manual do Concurseiro</strong>.</p>
                      <p>{'Aqui, voc\u00ea encontrar\u00e1 apoio para fortalecer os pilares da aprova\u00e7\u00e3o: '}<strong className="font-bold text-cyan-100">{'planejamento, organiza\u00e7\u00e3o, disciplina, acompanhamento, mentalidade e motiva\u00e7\u00e3o'}</strong>.</p>
                      <p className="border-l-2 border-yellow-300 pl-4 font-bold text-yellow-50">{'Mais do que estudar, \u00e9 preciso aprender a transformar esfor\u00e7o em resultado.'}</p>
                    </div>
                    <div className="mt-7 max-w-3xl rounded-lg border border-yellow-300/35 bg-yellow-300/10 p-4">
                      <label className="flex cursor-pointer items-start gap-3 text-sm leading-6 text-slate-100 sm:text-base">
                        <input
                          className="mt-1 h-5 w-5 shrink-0 accent-yellow-300"
                          type="checkbox"
                          checked={journeyRead}
                          onChange={(event) => setJourneyRead(event.target.checked)}
                        />
                        <span>Li a apresentação e compreendi como a plataforma pode apoiar minha preparação.</span>
                      </label>
                      <Button
                        className="mt-4 w-full sm:w-auto"
                        disabled={!journeyRead}
                        onClick={() => document.getElementById('cadastro-acesso')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      >
                        <ArrowRight className="h-5 w-5" /> Continuar para Dados do visitante
                      </Button>
                      {!journeyRead && <p className="mt-3 text-xs leading-5 text-yellow-100">Confirme a leitura da apresentação para continuar.</p>}
                    </div>
                  </div>
                </div>
              </section>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Diagnóstico completo" value="50 pontos" icon={Brain} />
                <StatCard label="Metas planejadas" value="12+" icon={FileText} color="#34d399" />
                <StatCard label="Evolução" value="Pontos e níveis" icon={Award} color="#f59e0b" />
                <StatCard label="Exportação" value="PDF" icon={Download} color="#fb7185" />
              </div>
              <div id="cadastro-acesso" className="grid gap-4">
                <div className="glass rounded-lg p-5">
                  <h2 className="mb-4 flex items-center gap-2 text-2xl font-black text-white"><User className="h-6 w-6 text-mint" /> Dados do visitante</h2>
                  <AutoSaveNote />
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field label="Nome completo"><input className={inputClass()} value={lead.full_name} onChange={(event) => setLead({ ...lead, full_name: event.target.value })} /></Field>
                    <Field label="WhatsApp"><input className={inputClass()} value={lead.whatsapp} onChange={(event) => setLead({ ...lead, whatsapp: event.target.value })} /></Field>
                    <Field label="E-mail" className="md:col-span-2"><input className={inputClass()} type="email" value={lead.email} onChange={(event) => setLead({ ...lead, email: event.target.value })} /></Field>
                    <Field label="Crie sua senha de acesso" className="md:col-span-2">
                      <input className={inputClass()} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={leadPassword} onChange={(event) => setLeadPassword(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 números" />
                      <p className="mt-1 text-xs text-slate-400">Use uma senha de 6 números para acessar o Histórico de Estudos em qualquer dispositivo.</p>
                    </Field>
                    <Field label="Objetivo de estudo" className="md:col-span-2">
                      <select className={inputClass()} required value={lead.study_goal} onChange={(event) => setLead({ ...lead, study_goal: event.target.value, study_goal_other: event.target.value === 'Outros' ? lead.study_goal_other : '' })}>
                        <option value="">Selecione o objetivo</option>
                        {studyGoalOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    {lead.study_goal === 'Outros' && (
                      <Field label="Informe o objetivo de estudo" className="md:col-span-2">
                        <input className={inputClass()} required value={lead.study_goal_other} onChange={(event) => setLead({ ...lead, study_goal_other: event.target.value })} placeholder="Digite seu objetivo de estudo" />
                      </Field>
                    )}
                    <Field label="Consentimento LGPD" className="md:col-span-2">
                      <label className="flex items-start gap-3 rounded-lg border border-slate-600 bg-slate-950/60 p-3 text-sm text-slate-200">
                        <input className="mt-1 h-5 w-5" type="checkbox" checked={lead.consent_lgpd} onChange={(event) => setLead({ ...lead, consent_lgpd: event.target.checked })} />
                        <span>Autorizo o tratamento dos meus dados pessoais para o envio de conteúdos, cursos, livros, palestras, mentorias, eventos, comunicações institucionais e demais contatos relacionados à gestão pública, liderança, inteligência emocional, desenvolvimento profissional e outros temas, do palestrante e professor Francisney Liberato, nos termos da legislação aplicável.</span>
                      </label>
                    </Field>
                    <Field label="Código de acesso (Digite: FrancisneyLiberato)" className="md:col-span-2">
                      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                        <input className={inputClass()} type="text" value={accessCode} onChange={(event) => setAccessCode(event.target.value)} onPaste={(event) => event.preventDefault()} onDrop={(event) => event.preventDefault()} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void validateAccess(); } }} autoCapitalize="none" autoCorrect="off" autoComplete="off" spellCheck={false} aria-describedby="codigo-acesso-ajuda" placeholder="Digite FrancisneyLiberato" />
                        <Button onClick={validateAccess}><CheckCircle2 className="h-5 w-5" /> Validar</Button>
                      </div>
                      <p id="codigo-acesso-ajuda" className="mt-2 text-xs leading-5 text-slate-400">Digite o código manualmente. A colagem não é permitida. Letras maiúsculas e minúsculas são aceitas.</p>
                    </Field>
                  </div>
                </div>
              </div>
              {leadSaved && (
                <div className="rounded-lg border border-yellow-300/50 bg-yellow-300/10 p-4 text-yellow-50">
                  Acesso liberado. Seu desempenho está pronto para acompanhar sua evolução. Para usar o Plano de Estudos e o Estudar Agora, preencha antes a aba Disciplinas e Prioridades.
                </div>
              )}
            </section>
          )}

          {active === 'teste' && (
            <section className="grid gap-6">
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="glass rounded-lg p-5">
                  <h2 className="mb-4 flex items-center gap-2 text-2xl font-black text-white"><KeyRound className="h-6 w-6 text-cyan-300" /> Código de acesso</h2>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input className={inputClass()} type="text" value={accessCode} onChange={(event) => setAccessCode(event.target.value)} onPaste={(event) => event.preventDefault()} onDrop={(event) => event.preventDefault()} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void validateAccess(); } }} autoCapitalize="none" autoCorrect="off" autoComplete="off" spellCheck={false} placeholder="Digite FrancisneyLiberato" />
                    <Button onClick={validateAccess}><CheckCircle2 className="h-5 w-5" /> Validar</Button>
                  </div>
                </div>
                <div className="glass rounded-lg p-5">
                  <h2 className="mb-4 flex items-center gap-2 text-2xl font-black text-white"><User className="h-6 w-6 text-mint" /> Dados do visitante</h2>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field label="Nome completo"><input className={inputClass()} value={lead.full_name} onChange={(event) => setLead({ ...lead, full_name: event.target.value })} /></Field>
                    <Field label="WhatsApp"><input className={inputClass()} value={lead.whatsapp} onChange={(event) => setLead({ ...lead, whatsapp: event.target.value })} /></Field>
                    <Field label="E-mail" className="md:col-span-2"><input className={inputClass()} type="email" value={lead.email} onChange={(event) => setLead({ ...lead, email: event.target.value })} /></Field>
                    <Field label="Objetivo de estudo" className="md:col-span-2">
                      <select className={inputClass()} required value={lead.study_goal} onChange={(event) => setLead({ ...lead, study_goal: event.target.value, study_goal_other: event.target.value === 'Outros' ? lead.study_goal_other : '' })}>
                        <option value="">Selecione o objetivo</option>
                        {studyGoalOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    {lead.study_goal === 'Outros' && (
                      <Field label="Informe o objetivo de estudo" className="md:col-span-2">
                        <input className={inputClass()} required value={lead.study_goal_other} onChange={(event) => setLead({ ...lead, study_goal_other: event.target.value })} placeholder="Digite seu objetivo de estudo" />
                      </Field>
                    )}
                    <Field label="Consentimento LGPD" className="md:col-span-2">
                      <label className="flex items-start gap-3 rounded-lg border border-slate-700 bg-slate-950/40 p-3 text-sm text-slate-300">
                        <input className="mt-1 h-5 w-5" type="checkbox" checked={lead.consent_lgpd} onChange={(event) => setLead({ ...lead, consent_lgpd: event.target.checked })} />
                        <span>Autorizo o tratamento dos meus dados pessoais para o envio de conteúdos, cursos, livros, palestras, mentorias, eventos, comunicações institucionais e demais contatos relacionados à gestão pública, liderança, inteligência emocional, desenvolvimento profissional e outros temas, do palestrante e professor Francisney Liberato, nos termos da legislação aplicável.</span>
                      </label>
                    </Field>
                    <Button className="md:col-span-2" disabled={!accessOk} onClick={() => saveLead()}><Save className="h-5 w-5" /> Salvar e iniciar Meu Diagnóstico</Button>
                  </div>
                </div>
              </div>

              {accessOk && leadSaved && (
                <>
                  <div className="glass rounded-lg p-5">
                    <h2 className="mb-4 text-2xl font-black text-white">Diagnóstico inicial</h2>
                    <div className="grid gap-3">
                      {diagnosisQuestions.map((question, index) => (
                        <div key={question} className="grid gap-3 rounded-lg border border-slate-700 bg-slate-950/35 p-3 md:grid-cols-[1fr_260px] md:items-center">
                          <span className="text-slate-200">{index + 1}. {question}</span>
                          <input type="range" min={0} max={5} value={answers[index]} onChange={(event) => setAnswers(answers.map((value, current) => (current === index ? Number(event.target.value) : value)))} />
                          <span className="md:col-start-2 text-sm text-cyan-100">Nota: {answers[index]} de 5</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="glass rounded-lg p-5">
                    <h2 className="mb-4 text-2xl font-black text-white">Questionário estratégico</h2>
                    <div className="grid gap-3">
                      {strategicQuestions.map((question, index) => (
                        <div key={question} className="grid gap-3 rounded-lg border border-slate-700 bg-slate-950/35 p-3 lg:grid-cols-[1fr_280px] lg:items-center">
                          <span className="text-slate-200">{index + 1}. {question}</span>
                          <div className="grid grid-cols-3 gap-2">
                            {['Sim', 'Talvez', 'Não'].map((option) => (
                              <button
                                key={option}
                                onClick={() => setStrategicAnswers(strategicAnswers.map((value, current) => (current === index ? option : value)))}
                                className={`focus-ring rounded-lg px-3 py-3 text-sm font-bold ${strategicAnswers[index] === option ? 'bg-mint text-slate-950' : 'border border-slate-700 text-slate-200'}`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div ref={resultRef} className="print-card glass rounded-lg p-5">
                    <h2 className="text-2xl font-black text-white">Resultado do diagnóstico</h2>
                    <div className="mt-4 grid gap-4 lg:grid-cols-[360px_1fr]">
                      <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-4">
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={[{ name: 'Desempenho', value: result.percentage }, { name: 'Estratégia', value: Math.round((strategicTotal / 110) * 100) }]}>
                            <CartesianGrid stroke="#1e293b" />
                            <XAxis dataKey="name" stroke="#cbd5e1" />
                            <YAxis stroke="#cbd5e1" />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(34, 211, 238, 0.10)' }} />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                              <Cell fill="#22d3ee" />
                              <Cell fill="#34d399" />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid gap-3">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <StatCard label="Pontuação" value={`${result.total}/50`} icon={Target} />
                          <StatCard label="Aproveitamento" value={`${result.percentage}%`} icon={BarChart3} color="#34d399" />
                          <StatCard label="Classificação" value={result.classification} icon={Award} color="#f59e0b" />
                        </div>
                        <div className="rounded-lg border border-slate-700 bg-slate-950/35 p-4 text-slate-200">
                          <p><strong>Pontos fortes:</strong> {result.strong.join(' ')}</p>
                          <p className="mt-2"><strong>Pontos de atenção:</strong> {result.attention.join(' ')}</p>
                          <p className="mt-2"><strong>Recomendação prática:</strong> {result.recommendation}</p>
                          <p className="mt-2"><strong>Estratégico:</strong> {strategicTotal >= 70 ? 'Está no caminho certo, com ajustes progressivos.' : 'Requer revisão de planejamento, método e fatores psicológicos.'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="no-print mt-4 flex flex-wrap gap-3">
                      <Button onClick={saveAssessment}><Save className="h-5 w-5" /> Registrar resultado</Button>
                      <Button tone="quiet" onClick={downloadPdf}><Download className="h-5 w-5" /> Baixar PDF</Button>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {active === 'dashboard' && dashboard && (
            <section className="grid gap-6">
              <div ref={progressPdfRef} className="grid gap-6">
              <div className="glass rounded-lg p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Meu Desempenho</p>
                    <h2 className="mt-1 text-3xl font-black text-white">Acompanhe sua execução real</h2>
                    <p className="mt-2 max-w-4xl text-slate-300">
                      Aqui se integram as informações de <strong className="font-semibold text-white">Disciplinas e Prioridades</strong>, <strong className="font-semibold text-white">Plano de Estudos</strong> e <strong className="font-semibold text-white">Estudar Agora</strong>. Compare o planejado com o executado, acompanhe sua evolução, celebre os avanços e escolha a próxima ação com confiança.
                    </p>
                    <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-300 sm:text-base">
                      O melhor parâmetro é você mesmo. Evite comparar sua trajetória com a de outros estudantes, pois sempre haverá pessoas em níveis inferiores, semelhantes ou superiores, o que pode gerar acomodação, ansiedade ou frustração. Observe seu próprio progresso e identifique o que ainda precisa melhorar.
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
                    <Button tone="quiet" onClick={() => setActive('feedback')}><MessageSquare className="h-5 w-5" /> Sugestões de Melhoria</Button>
                    <Button tone="quiet" onClick={() => setActive('registro')}><TimerReset className="h-5 w-5" /> Estudar Agora</Button>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap items-end gap-3">
                  <Field label="Data inicial">
                    <input className={inputClass()} type="date" value={dashboardStartDate} onChange={(event) => setDashboardStartDate(event.target.value)} />
                  </Field>
                  <Field label="Data final">
                    <input className={inputClass()} type="date" value={dashboardEndDate} onChange={(event) => setDashboardEndDate(event.target.value)} />
                  </Field>
                  <Button tone="quiet" onClick={() => { setDashboardStartDate(''); setDashboardEndDate(''); }}><RefreshCw className="h-4 w-4" /> Ver dados acumulados</Button>
                  <p className="pb-2 text-sm text-slate-300">{dashboardStartDate || dashboardEndDate ? 'Filtro aplicado ao período informado.' : 'Exibindo todos os dados registrados.'}</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <StatCard label="Planos no período" value={dashboardPlans.length} icon={CalendarDays} color="#facc15" />
                <StatCard label="Execução de tarefas / Total de tarefas planejadas" value={`${dashboardCompletedTaskPlans.length}/${dashboardTaskPlans.length}`} icon={CheckCircle2} color="#34d399" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Realizado x planejado" value={`${formatStudyElapsed(dashboardCompletedSeconds)} / ${formatHoursFromMinutes(dashboardPlannedMinutes)}`} icon={CheckCircle2} />
                <StatCard label="Questões realizadas" value={dashboardQuestionsDone} icon={BookOpen} color="#f59e0b" />
                <StatCard label="Percentual de acertos" value={`${dashboardAccuracy}%`} icon={BarChart3} color="#34d399" />
                <StatCard label="Disciplina prioritária" value={dashboardPrioritySubject} icon={Award} color="#f59e0b" />
              </div>
              <div className="rounded-lg border border-violet-300/35 bg-violet-300/10 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-black uppercase tracking-wider text-violet-100">Trilha de evolução</p>
                    <h3 className="mt-1 text-2xl font-black text-white">Nível de Preparação {currentGamificationLevel} - estudante em evolução</h3>
                    <p className="mt-1 text-sm text-slate-300">Você já acumulou {totalGamificationXp} Pontos de Evolução. Faltam {xpToNextLevel} pontos para alcançar o próximo Nível de Preparação.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm font-bold">
                    <span className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 px-3 py-2 text-yellow-100">🏆 {dashboardMedals} {dashboardMedals === 1 ? 'medalha' : 'medalhas'}</span>
                    <span className="rounded-lg border border-rose-300/40 bg-rose-300/10 px-3 py-2 text-rose-100">🔥 {dashboard.cards?.streak || 0} {Number(dashboard.cards?.streak || 0) === 1 ? 'dia' : 'dias'} de sequência</span>
                  </div>
                </div>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-900"><div className="h-full rounded-full bg-violet-400 transition-all duration-700" style={{ width: `${levelProgress}%` }} /></div>
                <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3"><span className="rounded-md border border-slate-700 bg-slate-950/40 p-2 text-slate-200">Primeiro plano: +20 pontos</span><span className="rounded-md border border-slate-700 bg-slate-950/40 p-2 text-slate-200">Bloco concluído: +10 pontos</span><span className="rounded-md border border-slate-700 bg-slate-950/40 p-2 text-slate-200">Semana com 80%: +50 pontos</span></div>
              </div>
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-lg border border-cyan-300/35 bg-cyan-300/10 p-5">
                  <p className="text-sm font-black uppercase text-cyan-100">Tempo executado</p>
                  <strong className="mt-2 block text-3xl text-white">{formatStudyElapsed(dashboardCompletedSeconds)}</strong>
                  <p className="mt-2 text-sm leading-6 text-slate-300">Horas, minutos e segundos registrados em Estudar Agora no período selecionado.</p>
                </div>
                <div className="rounded-lg border border-emerald-300/35 bg-emerald-300/10 p-5">
                  <p className="text-sm font-black uppercase text-emerald-100">Progresso de execução</p>
                  <strong className="mt-2 block text-3xl text-white">{dashboardProgress}%</strong>
                  <p className="mt-2 text-sm leading-6 text-slate-300">Horas realizadas em relação às horas planejadas no período selecionado.</p>
                </div>
                <div className="rounded-lg border border-yellow-300/35 bg-yellow-300/10 p-5">
                  <p className="text-sm font-black uppercase text-yellow-100">Desempenho acadêmico</p>
                  <strong className="mt-2 block text-3xl text-white">{dashboardAccuracy}%</strong>
                  <p className="mt-2 text-sm leading-6 text-slate-300">Percentual de acertos nas questões registradas. Use-o para orientar a próxima semana.</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Dias cumpridos" value={`${dashboardCompletedDays}/${dashboardPlannedDays || 0}`} icon={CalendarDays} color="#34d399" />
                <StatCard label="Aderência aos dias" value={`${dashboardDaysAdherence}%`} icon={CheckCircle2} color="#22d3ee" />
                <StatCard label="Simulados realizados" value={dashboardSimulations} icon={Target} color="#facc15" />
                <StatCard label="Redações ou dissertativas" value={dashboardEssays} icon={FileText} color="#fb7185" />
                <StatCard label="Carga redistribuída" value={formatHoursFromMinutes(dashboardRedistributedMinutes)} icon={RefreshCw} color="#a78bfa" />
                <StatCard label="Blocos reagendados" value={dashboardRescheduled} icon={CalendarDays} color="#f59e0b" />
              </div>
              <div className="glass rounded-lg p-5">
                <h2 className="text-xl font-black text-white">Missões de Estudo da semana</h2>
                <p className="mt-1 text-sm text-slate-300">Metas objetivas para transformar constância em avanço mensurável.</p>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {[
                    { label: 'Concluir 80% das tarefas planejadas', done: dashboardTaskProgress >= 80, progress: dashboardTaskProgress, reward: '+50 pontos' },
                    { label: 'Estudar em todos os dias escolhidos', done: dashboardDaysAdherence >= 100, progress: dashboardDaysAdherence, reward: '+25 pontos' },
                    { label: 'Concluir os blocos de memorização', done: dashboardCompletedLogs.some((log) => log.review_done), progress: dashboardCompletedLogs.some((log) => log.review_done) ? 100 : 0, reward: '+15 pontos' },
                    { label: 'Atingir a meta de questões', done: dashboardCompletedQuestions >= 120, progress: Math.min(100, Math.round(dashboardCompletedQuestions / 120 * 100)), reward: 'Domínio' },
                    { label: 'Realizar o simulado', done: dashboardSimulations > 0, progress: dashboardSimulations > 0 ? 100 : 0, reward: '+30 pontos' },
                    { label: 'Realizar redação ou dissertativa', done: dashboardEssays > 0, progress: dashboardEssays > 0 ? 100 : 0, reward: '+30 pontos' }
                  ].map((mission) => (
                    <div key={mission.label} className={`rounded-lg border p-4 ${mission.done ? 'border-emerald-300/40 bg-emerald-300/10' : 'border-slate-700 bg-slate-950/45'}`}>
                      <div className="flex items-start justify-between gap-3"><strong className="text-sm text-white">{mission.label}</strong><span className="text-xs font-black text-slate-300">{mission.done ? 'Concluída' : `${mission.progress}%`}</span></div>
                      <p className={`mt-2 text-xs font-black ${mission.done ? 'text-emerald-100' : 'text-yellow-100'}`}>{mission.done ? `Missão de Estudo concluída · ${mission.reward}` : `Recompensa: ${mission.reward}`}</p>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-800"><div className="h-full bg-emerald-400" style={{ width: `${mission.progress}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="glass rounded-lg p-5">
                <h2 className="mb-4 text-xl font-black text-white">Calendário executado</h2>
                <StudyCalendarGrid title="Meu Desempenho - executado" plans={dashboardPlans} logs={dashboardLogs} startDate={dashboardStartDate || dateToIso(startOfCurrentWeek())} />
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="glass rounded-lg p-4">
                  <h2 className="mb-4 text-xl font-black text-white">Evolução de horas</h2>
                  {dashboardHoursByDay.length ? <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dashboardHoursByDay}>
                      <CartesianGrid stroke="#1e293b" />
                      <XAxis dataKey="date" stroke="#cbd5e1" />
                      <YAxis stroke="#cbd5e1" />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(34, 211, 238, 0.10)' }} />
                      <Line type="monotone" dataKey="hours" name="Horas" stroke="#22d3ee" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer> : <p className="grid h-[300px] place-items-center rounded-lg border border-dashed border-cyan-300/35 bg-cyan-300/5 px-6 text-center text-sm leading-6 text-slate-300">Registre seu primeiro bloco em Estudar Agora. A evolução das horas aparecerá aqui para orientar seus próximos passos.</p>}
                  <ChartSummary>{hoursTrendSummary(dashboardHoursByDay)}</ChartSummary>
                  <p className="mt-3 text-sm text-slate-300">Estratégia: mantenha blocos curtos nos dias fracos e concentre simulados nos dias de maior energia.</p>
                </div>
                <div className="glass rounded-lg p-4">
                  <h2 className="mb-4 text-xl font-black text-white">Horas por disciplina e Simulado</h2>
                  {dashboardHoursBySubject.length ? <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardHoursBySubject}>
                      <CartesianGrid stroke="#1e293b" />
                      <XAxis dataKey="name" stroke="#cbd5e1" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#cbd5e1" />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(52, 211, 153, 0.10)' }} />
                      <Bar dataKey="hours" name="Horas" fill="#34d399" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer> : <p className="grid h-[300px] place-items-center rounded-lg border border-dashed border-emerald-300/35 bg-emerald-300/5 px-6 text-center text-sm leading-6 text-slate-300">Quando você concluir um estudo, esta área mostrará onde sua dedicação está crescendo. Comece pelo primeiro bloco possível.</p>}
                  <ChartSummary>{leadingHoursSummary(dashboardHoursBySubject)}</ChartSummary>
                  <p className="mt-3 text-sm text-slate-300">Estratégia: compare as disciplinas prioritárias e o Simulado. Se uma disciplina crítica recebeu poucas horas, ela deve entrar no primeiro bloco livre da semana.</p>
                </div>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="glass rounded-lg p-4">
                  <h2 className="mb-4 text-xl font-black text-white">Exercícios: planejado x executado</h2>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={subjectExecutionData}>
                      <CartesianGrid stroke="#1e293b" />
                      <XAxis dataKey="name" stroke="#cbd5e1" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#cbd5e1" />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(250, 204, 21, 0.10)' }} />
                      <Bar dataKey="planejado" fill="#facc15" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="executado" fill="#22d3ee" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <ChartSummary>{executionSummary(subjectExecutionData)}</ChartSummary>
                  <p className="mt-3 text-sm text-slate-300">Estratégia: quando a barra azul ficar menor que a amarela por dois dias, reduza a meta ou divida a bateria em blocos de 15 questões.</p>
                </div>
                <div className="glass rounded-lg p-4">
                  <h2 className="mb-4 text-xl font-black text-white">Percentual de acertos por disciplina</h2>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={subjectExecutionData}>
                      <CartesianGrid stroke="#1e293b" />
                      <XAxis dataKey="name" stroke="#cbd5e1" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#cbd5e1" domain={[0, 100]} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(52, 211, 153, 0.10)' }} />
                      <Bar dataKey="acerto" fill="#34d399" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <ChartSummary>{accuracySummary(subjectExecutionData)}</ChartSummary>
                  <p className="mt-3 text-sm text-slate-300">Estratégia: abaixo de 70%, volte para correção ativa; acima de 80%, avance para simulado cronometrado.</p>
                </div>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="glass rounded-lg p-4">
                  <h2 className="mb-4 text-xl font-black text-white">Distribuição por tipo de atividade</h2>
                  {activityData.length ? <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={activityData}>
                      <CartesianGrid stroke="#1e293b" />
                      <XAxis dataKey="name" stroke="#cbd5e1" />
                      <YAxis stroke="#cbd5e1" />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(167, 139, 250, 0.10)' }} />
                      <Bar dataKey="hours" name="Horas" fill="#a78bfa" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer> : <p className="grid h-[300px] place-items-center rounded-lg border border-dashed border-violet-300/50 bg-violet-300/5 px-6 text-center text-sm leading-6 text-slate-200">Conclua um estudo para visualizar a distribuição real por tipo de atividade.</p>}
                  <ChartSummary>{leadingHoursSummary(activityData, 'atividade')}</ChartSummary>
                  <p className="mt-3 text-sm text-slate-300">Estratégia: um ciclo equilibrado mistura teoria, exercícios, revisão e simulado. Excesso de teoria costuma atrasar resultado.</p>
                </div>
                <div className="glass rounded-lg p-4">
                  <h2 className="mb-4 text-xl font-black text-white">Foco e energia</h2>
                  {focusEnergyData.length ? <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={focusEnergyData}>
                      <CartesianGrid stroke="#1e293b" />
                      <XAxis dataKey="date" stroke="#cbd5e1" />
                      <YAxis stroke="#cbd5e1" domain={[0, 10]} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(34, 211, 238, 0.10)' }} />
                      <Line type="monotone" dataKey="foco" stroke="#22d3ee" strokeWidth={3} />
                      <Line type="monotone" dataKey="energia" stroke="#facc15" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer> : <p className="grid h-[300px] place-items-center rounded-lg border border-dashed border-cyan-300/50 bg-cyan-300/5 px-6 text-center text-sm leading-6 text-slate-200">Conclua um estudo e registre foco e energia para acompanhar seu ritmo real.</p>}
                  <ChartSummary>{focusEnergySummary(focusEnergyData)}</ChartSummary>
                  <p className="mt-3 text-sm text-slate-300">Estratégia: use os dias de foco alto para disciplinas difíceis e os dias de energia baixa para revisões leves.</p>
                </div>
              </div>
              <div className="glass rounded-lg p-5">
                <h2 className="mb-4 text-xl font-black text-white">Plano de decisão do coach</h2>
                <div className="grid gap-3 md:grid-cols-3">
                  {coachSuggestions.map((suggestion) => (
                    <div key={suggestion} className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-4 text-sm leading-6 text-yellow-50">
                      {suggestion}
                    </div>
                  ))}
                </div>
              </div>
              </div>
              <div className="no-print flex flex-wrap gap-3">
                <Button onClick={downloadProgressPdf}><Save className="h-5 w-5" /> Salvar em PDF</Button>
                <Button tone="quiet" onClick={() => sharePdfDocument(createProgressPdf(), progressPdfMeta())}><Send className="h-5 w-5" /> Compartilhar em PDF</Button>
              </div>
            </section>
          )}

          {active === 'geral' && <PlatformDashboardSection />}

          {active === 'disciplinas' && (
            <section className="grid gap-6">
              <div className="glass rounded-lg p-5">
                <h2 className="mb-4 text-2xl font-black text-white">Disciplinas</h2>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Field label="Nome"><input className={inputClass()} value={subjectForm.name} onChange={(event) => setSubjectForm({ ...subjectForm, name: event.target.value })} /></Field>
                  <Field label="Peso"><input className={inputClass()} type="number" value={subjectForm.weight} onChange={(event) => setSubjectForm({ ...subjectForm, weight: Number(event.target.value) })} /></Field>
                  <Field label="Questões"><input className={inputClass()} type="number" value={subjectForm.questions} onChange={(event) => setSubjectForm({ ...subjectForm, questions: Number(event.target.value) })} /></Field>
                  <Field label="Cor"><input className={`${inputClass()} h-12`} type="color" value={subjectForm.color} onChange={(event) => setSubjectForm({ ...subjectForm, color: event.target.value })} /></Field>
                  <Field label="Tamanho"><select className={inputClass()} value={subjectForm.size} onChange={(event) => setSubjectForm({ ...subjectForm, size: event.target.value })}><option value="baixo">Baixo</option><option value="medio">Médio</option><option value="alto">Alto</option></select></Field>
                  <Field label="Dificuldade"><select className={inputClass()} value={subjectForm.difficulty} onChange={(event) => setSubjectForm({ ...subjectForm, difficulty: event.target.value })}><option value="baixo">Baixo</option><option value="medio">Médio</option><option value="alto">Alto</option></select></Field>
                  <Field label="Domínio atual (%)"><input className={inputClass()} type="number" value={subjectForm.current_mastery} onChange={(event) => setSubjectForm({ ...subjectForm, current_mastery: Number(event.target.value) })} /></Field>
                  <Field label="Meta de domínio (%)"><input className={inputClass()} type="number" value={subjectForm.target_mastery} onChange={(event) => setSubjectForm({ ...subjectForm, target_mastery: Number(event.target.value) })} /></Field>
                  <Button className="md:col-span-2 xl:col-span-4" onClick={addSubject}><Plus className="h-5 w-5" /> Adicionar disciplina</Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-700">
                <table className="min-w-[900px] w-full text-left text-sm">
                  <thead className="bg-cyan-900/70 text-white"><tr>{['Disciplina', 'Peso', 'Questões', 'Dificuldade', 'Domínio', 'Prioridade', '% recomendado', 'Horas/ciclo'].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
                  <tbody>
                    {subjects.map((subject) => (
                      <tr key={subject.id} className="border-t border-slate-800 bg-slate-950/35">
                        <td className="p-3"><span className="mr-2 inline-block h-3 w-3 rounded-full" style={{ background: subject.color }} />{subject.name}</td>
                        <td className="p-3">{subject.weight}</td>
                        <td className="p-3">{subject.questions}</td>
                        <td className="p-3">{subject.difficulty}</td>
                        <td className="p-3">{subject.current_mastery}%</td>
                        <td className="p-3">{subject.priority ?? '-'}</td>
                        <td className="p-3">{subject.recommended_percent ?? '-'}%</td>
                        <td className="p-3">{subject.recommended_hours ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {active === 'peso' && (
            <section className="grid gap-6">
              <div className="glass rounded-lg p-5">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Disciplinas e Prioridades</p>
                <h2 className="mt-1 text-3xl font-black text-white">Transforme o edital em uma estratégia de aprovação</h2>
                <p className="mt-2 max-w-4xl text-slate-300">
                  Cadastre cada disciplina conforme o edital e a sua percepção de dificuldade. O sistema organiza as prioridades para que você direcione sua energia ao que mais importa. Depois, o Plano de Estudos ajustará as horas à sua disponibilidade real.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <StatCard className="border border-cyan-300/25 bg-cyan-300/5" label="Disciplinas cadastradas" value={disciplineWeights.length} icon={BookOpen} />
                  <StatCard className="border border-yellow-300/30 bg-yellow-300/5" label="Maior prioridade" value={disciplineWeightResults[0]?.subject_name || 'A definir'} icon={Target} color="#facc15" />
                  <StatCard className="border border-emerald-300/25 bg-emerald-300/5" label="Peso do edital" value="Você informa" icon={Award} color="#34d399" />
                  <StatCard className="border border-violet-300/25 bg-violet-300/5" label="Cálculo da prioridade" value="P + NQ + TD + ND" icon={BarChart3} color="#a78bfa" />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  {['Missão 1: cadastrar as disciplinas do edital', 'Missão 2: descobrir onde seu foco gera mais resultado', 'Recompensa: Plano de Estudos liberado com estratégia e clareza'].map((text) => (
                    <div key={text} className="rounded-lg border border-yellow-300/35 bg-yellow-300/10 p-3 text-sm font-bold text-yellow-50">{text}</div>
                  ))}
                </div>
              </div>

              <div className="glass rounded-lg p-5">
                <h3 className="mb-2 text-2xl font-black text-white">Cadastre uma disciplina e avance</h3>
                <p className="mb-4 text-slate-300">
                  Informe os dados exatamente como aparecem no edital. No Peso do edital, registre o valor atribuído à disciplina, como 1, 1,5, 2 ou outro valor previsto. Em NQ, informe a quantidade real de questões. Para TD e ND, escolha 1 baixo, 2 médio ou 3 alto; o ND representa a sua percepção de dificuldade. Cada informação torna sua estratégia mais inteligente e aproxima você da aprovação.
                </p>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                  <Field label="Disciplina">
                    <input className={inputClass()} value={disciplineWeightForm.subject_name} onChange={(event) => setDisciplineWeightForm({ ...disciplineWeightForm, subject_name: event.target.value })} placeholder="Ex.: Português" />
                  </Field>
                  <Field label="Peso do edital">
                    <input className={inputClass()} type="number" min={0.1} step="0.1" value={disciplineWeightForm.exam_weight || ''} onChange={(event) => setDisciplineWeightForm({ ...disciplineWeightForm, exam_weight: Number(event.target.value || 0) })} placeholder="Ex.: 1,5" />
                  </Field>
                  <Field label="Número de questões - NQ">
                    <input className={inputClass()} type="number" min={1} step={1} value={disciplineWeightForm.question_weight || ''} onChange={(event) => setDisciplineWeightForm({ ...disciplineWeightForm, question_weight: Number(event.target.value || 0) })} placeholder="Ex.: 10" />
                  </Field>
                  <Field label="Tamanho da disciplina - TD">
                    <select className={inputClass()} value={disciplineWeightForm.discipline_size} onChange={(event) => setDisciplineWeightForm({ ...disciplineWeightForm, discipline_size: Number(event.target.value) })}>
                      <option value={1}>1 - Baixo</option>
                      <option value={2}>2 - Médio</option>
                      <option value={3}>3 - Alto</option>
                    </select>
                  </Field>
                  <Field label="Nível de dificuldade - ND">
                    <select className={inputClass()} value={disciplineWeightForm.difficulty_level} onChange={(event) => setDisciplineWeightForm({ ...disciplineWeightForm, difficulty_level: Number(event.target.value) })}>
                      <option value={1}>1 - Baixo</option>
                      <option value={2}>2 - Médio</option>
                      <option value={3}>3 - Alto</option>
                    </select>
                  </Field>
                  <div className="grid gap-3 md:col-span-2 sm:grid-cols-2 xl:col-span-5">
                    <Button className="min-h-14 w-full whitespace-normal text-center leading-tight" onClick={saveDisciplineWeight}><Save className="h-5 w-5 shrink-0" /> Salvar e calcular peso</Button>
                    <Button className="min-h-14 w-full whitespace-normal text-center leading-tight" tone="quiet" disabled={!disciplineWeightResults.length} onClick={() => setActive('plano')}><ArrowRight className="h-5 w-5 shrink-0" /> Ir para Plano de Estudos</Button>
                  </div>
                </div>
              </div>

              <div className="grid gap-3">
                {disciplineWeightResults.length ? disciplineWeightResults.map((item, index) => (
                  <div key={item.id} className="rounded-lg border border-slate-700 bg-slate-950/45 p-4 text-slate-100">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-black uppercase tracking-wider text-yellow-100">{index + 1}ª prioridade</p>
                        <h3 className="mt-1 text-xl font-black text-white">{item.subject_name}</h3>
                      </div>
                      <span className="rounded-lg bg-yellow-300 px-3 py-2 text-sm font-black text-slate-950">{item.percent}% do ciclo</span>
                    </div>
                    <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      {[
                        ['P edital', item.exam_weight],
                        ['NQ edital', item.question_weight],
                        ['P calc.', item.normalizedExamWeight],
                        ['NQ calc.', item.normalizedQuestionWeight],
                        ['TD', item.discipline_size],
                        ['ND', item.difficulty_level],
                        ['Peso total', item.total],
                        ['Distribuição no plano', `${item.percent}%`]
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
                          <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
                          <p className="mt-1 font-black text-white">{value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button tone="quiet" onClick={() => setDisciplineWeightForm(item)}><Pencil className="h-4 w-4" /> Editar</Button>
                      <Button tone="danger" onClick={() => deleteDisciplineWeight(item.id)}><Trash2 className="h-4 w-4" /> Excluir</Button>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-lg border border-yellow-300/35 bg-yellow-300/10 p-5 text-yellow-50">
                    Cadastre pelo menos uma disciplina para liberar o Plano de Estudos e o Estudar Agora. Comece por uma: cada decisão bem definida fortalece o seu caminho até a aprovação.
                  </div>
                )}
              </div>
              {disciplineWeightResults.length > 0 && (
                <div className="glass flex flex-wrap items-center justify-between gap-4 rounded-lg p-5">
                  <div>
                    <h3 className="text-xl font-black text-white">Disciplinas conferidas</h3>
                    <p className="mt-1 text-slate-300">Excelente avanço. Suas prioridades já podem orientar um plano de estudos equilibrado, possível e consistente.</p>
                  </div>
                  <Button onClick={() => setActive('plano')}><ArrowRight className="h-5 w-5" /> Ir para Plano de Estudos</Button>
                </div>
              )}
            </section>
          )}

          {active === 'plano' && (
            <section className="grid gap-5">
              <PlanWizard
                subjects={planAssistantSubjects}
                performance={planPreviousPerformance}
                existingBlocks={planAssistantExistingBlocks}
                onDraft={savePlanAssistantDraft}
                onConfirm={confirmPlanAssistant}
                onMessage={setMessage}
              />
              <div className="no-print flex flex-wrap gap-3">
                <Button onClick={downloadPlanPdf}><Save className="h-5 w-5" /> Salvar em PDF</Button>
                <Button tone="quiet" onClick={() => sharePdfDocument(createPlanPdf(), planPdfMeta())}><Send className="h-5 w-5" /> Compartilhar em PDF</Button>
                <Button tone="quiet" onClick={() => setActive('registro')}><TimerReset className="h-5 w-5" /> Estudar Agora</Button>
              </div>
            </section>
          )}

          {false && active === 'plano' && (
            <section className="grid gap-6">
              <div ref={planPdfRef} className="grid gap-6">
              <div className="glass rounded-lg p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Plano de Estudos</p>
                    <h2 className="mt-1 text-3xl font-black text-white">Organize sua semana de estudo</h2>
                    <p className="mt-2 max-w-4xl text-slate-300">Monte uma semana possível, animadora e estratégica. O Estudar Agora e o Meu Desempenho transformam cada bloco em avanço real.</p>
                  </div>
                  <AutoSaveNote />
                </div>
                <div className="mt-5 grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  <StatCard label="Período" value={weekLabel} icon={CalendarDays} />
                  <Field label="Data inicial">
                    <input
                      className={inputClass()}
                      type="date"
                      value={weekStartDate}
                      onChange={(event) => {
                        const nextStart = event.target.value;
                        setWeekStartDate(nextStart);
                        if (!weekEndDate || new Date(`${weekEndDate}T12:00:00`) < new Date(`${nextStart}T12:00:00`)) {
                          setWeekEndDate(dateToIso(addDays(new Date(`${nextStart}T12:00:00`), 6)));
                        }
                      }}
                    />
                  </Field>
                  <Field label="Data final">
                    <input
                      className={inputClass()}
                      type="date"
                      value={weekEndDate}
                      onChange={(event) => setWeekEndDate(event.target.value)}
                    />
                  </Field>
                  <StatCard label="Meta semanal de horas" value={automaticWeekGoalLabel} icon={Target} color="#34d399" />
                  <Field label="Meta de semanas de estudo">
                    <input className={inputClass()} type="number" min={1} value={studyWeeksTarget} onChange={(event) => setStudyWeeksTarget(Number(event.target.value || 1))} />
                  </Field>
                  <StatCard label="Sugestão trimestral" value="12 semanas" icon={Target} color="#a78bfa" />
                  <StatCard label="Progresso da meta" value={`${selectedWeekNumber}/${studyWeeksTarget || 1}`} icon={Award} color="#facc15" />
                  <StatCard label="Blocos na semana" value={`${normalizedPlansInPeriod.length}`} icon={TimerReset} color="#34d399" />
                  <StatCard label="Atualização automática" value="Ao salvar" icon={Sparkles} color="#facc15" />
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  A meta semanal de horas é calculada automaticamente pela soma dos blocos adicionados neste período. Sugestão: divida o ano em 4 trimestres de estudo; cada trimestre pode ter aproximadamente 12 semanas, com revisão e simulados ao final de cada ciclo.
                </p>
                <div className="mt-4 rounded-lg border border-emerald-300/30 bg-emerald-300/10 p-4 text-sm leading-6 text-emerald-50">
                  🎮 Missão de Estudo da semana: monte blocos possíveis, execute um por vez e mantenha sua sequência viva. Cada bloco salvo é uma etapa pronta para gerar Pontos de Evolução no Estudar Agora.
                </div>
                <div className="mt-5 flex min-w-0 flex-wrap gap-3">
                  <Button tone="quiet" onClick={() => {
                    setWeeklyPlans((plans) => [...normalizedPlansInPeriod.map((plan, index) => ({ ...plan, id: `copy-${Date.now()}-${index}`, scheduled_date: dateToIso(addDays(new Date(`${plan.scheduled_date || todayIso}T12:00:00`), 7)), status: 'Planejado' })), ...plans]);
                    setMessage('Semana copiada para ajuste.');
                  }}><Copy className="h-5 w-5" /> Copiar semana anterior</Button>
                  <Button tone="quiet" onClick={() => document.getElementById('form-meu-plano')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}><Plus className="h-5 w-5" /> Adicionar estudo</Button>
                  <div className="inline-flex min-w-0 rounded-lg border border-slate-700 bg-slate-950/50 p-1">
                    <button className={`rounded-md px-4 py-2 text-sm font-bold ${planView === 'list' ? 'bg-yellow-300 text-slate-950' : 'text-slate-300'}`} onClick={() => setPlanView('list')}>Lista</button>
                    <button className={`rounded-md px-4 py-2 text-sm font-bold ${planView === 'calendar' ? 'bg-yellow-300 text-slate-950' : 'text-slate-300'}`} onClick={() => setPlanView('calendar')}>Calendário</button>
                  </div>
                </div>
              </div>

              <SabbathNote />

              <div id="form-meu-plano" className="glass rounded-lg p-5">
                <h3 className="mb-1 text-2xl font-black text-white">Bloco de Estudo</h3>
                <p className="mb-4 text-slate-300">Escolha o tipo de atividade e preencha somente o necessário para aquele bloco. Menos campos, mais clareza: o plano fica leve, objetivo e fácil de cumprir.</p>
                <AutoSaveNote />
                <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="Tipo de atividade"><select className={inputClass()} value={planForm.activity_type} onChange={(event) => setPlanForm({ ...planForm, activity_type: event.target.value, recurrence: event.target.value === 'Outros' ? 'Não repetir' : planForm.recurrence })}>{activities.map((activity) => <option key={activity}>{activity}</option>)}</select></Field>
                  {planForm.activity_type === 'Estudo' && (
                    <Field label="Disciplina"><select className={inputClass()} value={planForm.subject_name} onChange={(event) => {
                      const selected = disciplineWeightResults.find((item) => item.subject_name === event.target.value);
                      setPlanForm({
                        ...planForm,
                        subject_name: event.target.value,
                        planned_minutes: selected ? Math.max(30, Math.round(selected.cycle_hours * 60)) : planForm.planned_minutes,
                        duration_hours: selected ? Math.max(0.5, selected.cycle_hours) : planForm.duration_hours
                      });
                    }}>
                      <option value="">Selecione a disciplina</option>
                      {disciplineWeightResults.map((item) => <option key={item.id} value={item.subject_name}>{item.subject_name}</option>)}
                    </select></Field>
                  )}
                  <Field label="Dia"><input className={inputClass()} type="date" value={planForm.scheduled_date || todayIso} onChange={(event) => setPlanForm({ ...planForm, scheduled_date: event.target.value, day: dayNameFromDate(event.target.value) })} /></Field>
                  <Field label="Horário"><input className={inputClass()} type="time" value={planForm.start_time} onChange={(event) => setPlanForm({ ...planForm, start_time: event.target.value })} /></Field>
                  <Field label={planForm.activity_type === 'Estudo' ? 'Tempo de Estudo sugerido (min)' : 'Duração planejada (min)'}><input className={inputClass()} type="number" min={5} step={5} value={planForm.planned_minutes || 60} onChange={(event) => setPlanForm({ ...planForm, planned_minutes: Number(event.target.value), duration_hours: Number(event.target.value) / 60 })} /></Field>
                  {['Exercício', 'Simulado'].includes(planForm.activity_type) && (
                    <Field label={planForm.activity_type === 'Exercício' ? 'Meta de exercícios' : 'Meta de questões'}><input className={inputClass()} type="number" min={0} value={planForm.planned_questions || 0} onChange={(event) => setPlanForm({ ...planForm, planned_questions: Number(event.target.value) })} /></Field>
                  )}
                  {planForm.activity_type !== 'Outros' && (
                    <div className="grid gap-2 rounded-lg border border-slate-700 bg-slate-950/45 p-3 text-sm text-slate-200 md:col-span-2 xl:col-span-1">
                      <label className="flex items-center gap-2 font-bold text-yellow-100">
                        <input type="checkbox" checked={(planForm.recurrence || 'Não repetir') !== 'Não repetir'} onChange={(event) => setPlanForm({ ...planForm, recurrence: event.target.checked ? 'Repetir semanalmente' : 'Não repetir' })} />
                        REPETIR
                      </label>
                      {(planForm.recurrence || 'Não repetir') !== 'Não repetir' && (
                        <select className={inputClass()} value={planForm.recurrence || 'Repetir semanalmente'} onChange={(event) => setPlanForm({ ...planForm, recurrence: event.target.value })}>
                          <option>Repetir nesta semana</option>
                          <option>Repetir semanalmente</option>
                          <option>Repetir quinzenalmente</option>
                        </select>
                      )}
                    </div>
                  )}
                </div>
                <Button className="mt-4 w-full" onClick={addPlan}><Plus className="h-5 w-5" /> Salvar bloco no Plano de Estudos</Button>
              </div>

              <div className="glass rounded-lg p-5">
                <h3 className="mb-2 text-2xl font-black text-white">Gerar semana planejada</h3>
                <p className="mb-4 text-slate-300">Depois de lançar ou conferir seus blocos, gere a semana automaticamente com base nas prioridades calculadas na aba Disciplinas e Prioridades.</p>
                <Button className="w-full sm:w-auto" onClick={generateWeekPlan}><Sparkles className="h-5 w-5" /> Gerar minha semana</Button>
              </div>

              <div className="glass rounded-lg p-5">
                <h3 className="mb-4 text-2xl font-black text-white">Semana planejada</h3>
                {!normalizedPlans.length && (
                  <div className="rounded-lg border border-yellow-300/30 bg-yellow-300/10 p-5 text-yellow-50">
                    Sua semana ainda está em branco, e isso é uma oportunidade excelente para começar com clareza. Gere uma sugestão automática ou adicione seu primeiro estudo.
                    <Button className="mt-4" onClick={() => document.getElementById('form-meu-plano')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}><Plus className="h-5 w-5" /> Criar Bloco de Estudo</Button>
                  </div>
                )}
                {planView === 'list' && (
                  <div className="grid gap-4">
                    {plansByDate.map(({ day, date, blocks }) => (
                      <div key={date} className="rounded-lg border border-slate-700 bg-slate-950/35 p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <strong className="text-white">{day} | {formatDateBr(new Date(`${date}T00:00:00`))}</strong>
                          <span className="text-sm text-slate-300">{formatHoursFromMinutes(blocks.reduce((sum, plan) => sum + Number(plan.planned_minutes || 0), 0))}</span>
                        </div>
                        <div className="grid min-w-0 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                          {blocks.map((plan) => (
                            <div key={plan.id} className="rounded-lg border border-slate-700 bg-slate-900/65 p-4">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <strong className="text-yellow-100">{plan.subject_name}</strong>
                                  <p className="text-sm text-slate-300">{plan.activity_type} | {plan.start_time || 'horário livre'} | {formatHoursFromMinutes(Number(plan.planned_minutes || 0))}</p>
                                  {plan.content && <p className="mt-1 text-sm text-slate-400">{plan.content}</p>}
                                </div>
                                <span className="rounded-md bg-slate-950 px-2 py-1 text-xs text-slate-300">{plan.status}</span>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Button tone="quiet" onClick={() => setPlanForm(plan)}><Pencil className="h-4 w-4" /> Editar</Button>
                                <Button tone="quiet" onClick={() => duplicatePlan(plan)}><Copy className="h-4 w-4" /> Duplicar</Button>
                                <Button tone="quiet" onClick={() => deletePlan(plan.id)}><Trash2 className="h-4 w-4" /> Excluir</Button>
                              </div>
                            </div>
                          ))}
                          {!blocks.length && <p className="text-sm text-slate-500">Sem blocos neste dia.</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {planView === 'calendar' && (
                  <div className="grid gap-3 lg:grid-cols-7">
                    {plansByDate.map(({ day, date, blocks }) => (
                      <div key={date} className="min-h-[180px] rounded-lg border border-slate-700 bg-slate-950/35 p-3">
                        <strong className="text-sm text-white">{day}</strong>
                        <p className="mb-3 text-xs text-slate-400">{formatDateBr(new Date(`${date}T00:00:00`))}</p>
                        <div className="grid gap-2">
                          {blocks.map((plan) => <button key={plan.id} className="rounded-md border border-yellow-300/30 bg-yellow-300/10 p-2 text-left text-xs text-yellow-50" onClick={() => setPlanForm(plan)}>{plan.start_time || '--:--'} | {plan.subject_name}</button>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="glass rounded-lg p-5">
                <h3 className="mb-4 text-2xl font-black text-white">Calendário planejado x executado</h3>
                <p className="mb-4 text-slate-300">Depois de montar sua semana, acompanhe aqui o combinado e o realizado. Esse painel mostra se o plano está ajudando você a avançar com equilíbrio.</p>
                <StudyCalendarGrid title={`Plano de Estudos - ${weekLabel}`} plans={normalizedPlansInPeriod} logs={studyLogsInSelectedPeriod} startDate={weekStartDate} />
              </div>
              </div>
              <div className="no-print flex flex-wrap gap-3">
                <Button onClick={downloadPlanPdf}><Save className="h-5 w-5" /> Salvar em PDF</Button>
                <Button tone="quiet" onClick={() => sharePdfDocument(createPlanPdf(), planPdfMeta())}><Send className="h-5 w-5" /> Compartilhar em PDF</Button>
              </div>
            </section>
          )}

          {active === 'registro' && (
            <section className="grid gap-6">
              <div className="glass rounded-lg p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Estudar Agora</p>
                    <h2 className="mt-1 text-3xl font-black text-white">Transforme intenção em progresso</h2>
                    <p className="mt-2 max-w-4xl text-slate-300">Hoje é dia de transformar intenção em progresso. Avance um bloco de cada vez: comece com coragem, mantenha o foco e valorize cada hora de dedicação. Lembre-se de que as horas de execução são horas líquidas, isto é, correspondem ao tempo efetivo de estudo, sem contar intervalos, pausas ou outras interrupções. É a constância, construída diariamente, que aproxima você da aprovação.</p>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto">
                    <Button tone="quiet" onClick={() => setActive('plano')}><ArrowLeft className="h-5 w-5" /> Voltar ao Plano de Estudos</Button>
                    <Button tone="quiet" onClick={() => setActive('dashboard')}><BarChart3 className="h-5 w-5" /> Ir para Meu Desempenho</Button>
                    <Button tone="quiet" onClick={() => setActive('tempo')}><TimerReset className="h-5 w-5" /> Abrir Gestão do Tempo</Button>
                    <AutoSaveNote />
                  </div>
                </div>
                <div className="mt-5 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4">
                  <div>
                    <div>
                      <p className="text-sm font-black uppercase tracking-wider text-cyan-100">Agenda de execução</p>
                      <h3 className="mt-1 text-xl font-black text-white">Sua semana planejada e executada</h3>
                      <p className="mt-1 text-sm text-slate-200">{hasStudyScheduleFilter ? `Período consultado: ${formatFullDateBr(studyScheduleStart)} a ${formatFullDateBr(safeStudyScheduleEnd)}.` : `Semana atual: ${formatFullDateBr(defaultStudyScheduleStart)} a ${formatFullDateBr(defaultStudyScheduleEnd)}.`} Selecione um cartão para ver a programação correspondente abaixo.</p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 rounded-lg border border-slate-700 bg-slate-950/35 p-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] xl:items-end">
                    <Field label="Data inicial">
                      <input className={inputClass()} type="date" value={studyScheduleStartDate} onChange={(event) => setStudyScheduleStartDate(event.target.value)} />
                    </Field>
                    <Field label="Data final">
                      <input className={inputClass()} type="date" min={studyScheduleStartDate || undefined} value={studyScheduleEndDate} onChange={(event) => setStudyScheduleEndDate(event.target.value)} />
                    </Field>
                    <Button tone="quiet" onClick={() => {
                      if (studyScheduleEndDate && studyScheduleStartDate && studyScheduleEndDate < studyScheduleStartDate) {
                        setMessage('A data final deve ser igual ou posterior à data inicial.');
                        return;
                      }
                      setStudyViewDate(studyScheduleStart);
                    }}><Search className="h-4 w-4" /> Pesquisar período</Button>
                    <Button tone="quiet" onClick={() => {
                      setStudyScheduleStartDate('');
                      setStudyScheduleEndDate('');
                      setStudyViewDate(todayIso);
                    }}><CalendarDays className="h-4 w-4" /> Semana atual</Button>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-7">
                    {studyWeekOverview.map((day) => (
                      <button key={day.date} type="button" aria-pressed={day.date === studyViewDate} onClick={() => selectStudyDay(day.date)} className={`focus-ring rounded-lg border p-3 text-left transition hover:-translate-y-0.5 ${day.date === studyViewDate ? 'border-yellow-300 bg-yellow-300/15 shadow-[0_0_22px_rgba(250,204,21,0.12)]' : 'border-slate-700 bg-slate-950/45 hover:border-cyan-300/70'}`}>
                        <p className="text-xs font-black text-slate-300">{dayNameFromDate(day.date)}</p>
                        <p className="mt-1 text-sm font-bold text-white">{formatFullDateBr(day.date)}</p>
                        <p className="mt-3 text-xs text-slate-300">{day.plans} bloco{day.plans === 1 ? '' : 's'}</p>
                        <p className="mt-1 text-xs text-slate-300">Planejado: <strong className="text-white">{formatHoursFromMinutes(day.plannedMinutes)}</strong></p>
                        <p className="mt-1 text-xs text-emerald-100">Executado: <strong>{formatHoursFromMinutes(day.executedMinutes)}</strong></p>
                        <p className="mt-2 text-xs font-black text-emerald-100">{day.percent}% executado</p>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-900"><div className="h-full rounded-full bg-emerald-400 transition-all duration-700" style={{ width: `${day.percent}%` }} /></div>
                      </button>
                    ))}
                  </div>
                </div>
                {pendingStudyPlans.length > 0 && (
                  <div className="mt-5 rounded-lg border border-rose-300/45 border-l-4 border-l-rose-300 bg-gradient-to-br from-rose-950/75 via-rose-950/45 to-slate-950/75 p-4 shadow-[0_0_32px_rgba(251,113,133,0.16)]">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-sm font-black uppercase tracking-wider text-rose-100">Pendências para recuperar</p>
                        <h3 className="mt-1 text-xl font-black text-rose-50">{pendingStudyPlans.length} bloco{pendingStudyPlans.length === 1 ? '' : 's'} pendente{pendingStudyPlans.length === 1 ? '' : 's'} · {formatHoursFromMinutes(pendingStudyMinutes)}</h3>
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-rose-100">Recomendação de coach: recupere primeiro a pendência de ontem, hoje. Assim você avança sem deixar as metas se acumularem.</p>
                      </div>
                      <Button className="whitespace-nowrap" onClick={recalculatePlanWithPendingStudies}><RefreshCw className="h-5 w-5" /> Recalcular Plano de Estudos</Button>
                    </div>
                    <div className="mt-4 grid gap-3">
                      {pendingStudyPlans.map((plan) => {
                        const planId = String(plan.id || '');
                        const scheduledDate = String(plan.scheduled_date || todayIso).slice(0, 10);
                        const targetSlot = pendingRescheduleSlots[planId] || { date: todayIso, time: plan.start_time || '08:00' };
                        return (
                          <div key={planId} className="grid gap-3 rounded-lg border border-rose-200/35 bg-rose-950/25 p-3 lg:grid-cols-[minmax(0,1fr)_180px_auto] lg:items-end">
                            <div>
                              <p className="text-sm font-black text-rose-50">{formatDateBr(scheduledDate)} · {dayNameFromDate(scheduledDate)}</p>
                              <p className="mt-1 text-sm text-rose-100"><strong>{plan.subject_name || 'Atividade geral'}</strong> · {plan.activity_type || 'Estudo'} · {formatHoursFromMinutes(Number(plan.planned_minutes || Math.round(Number(plan.duration_hours || 0) * 60) || 0))}</p>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                              <Field label="Recuperar em: data">
                                <input className={inputClass()} type="date" min={todayIso} value={targetSlot.date} onChange={(event) => setPendingRescheduleSlots((slots) => ({ ...slots, [planId]: { ...targetSlot, date: event.target.value } }))} />
                              </Field>
                              <Field label="Recuperar em: horário">
                                <input className={inputClass()} type="time" value={targetSlot.time} onChange={(event) => setPendingRescheduleSlots((slots) => ({ ...slots, [planId]: { ...targetSlot, time: event.target.value } }))} />
                              </Field>
                            </div>
                            <Button tone="quiet" className="whitespace-nowrap" onClick={() => schedulePendingStudy(plan, targetSlot.date, targetSlot.time)}><CalendarDays className="h-4 w-4" /> Agendar</Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div ref={studyScheduleRef} className="mt-5 rounded-lg border border-slate-700 bg-slate-950/35 p-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <p className="text-sm font-black uppercase tracking-wider text-slate-300">Programação e execução</p>
                      <h3 className="mt-1 text-xl font-black text-white">Blocos de {formatFullDateBr(studyViewDate)} · {dayNameFromDate(studyViewDate)}</h3>
                    </div>
                    <span className="rounded-lg border border-slate-600 bg-slate-900/70 px-3 py-2 text-sm font-black text-slate-100">{selectedStudySchedule.length} agendado{selectedStudySchedule.length === 1 ? '' : 's'}</span>
                  </div>
                  {selectedStudySchedule.length ? (
                    <div className="mt-4 grid gap-3">
                      {selectedStudySchedule.map(({ plan, execution, plannedMinutes, actualSeconds, executionPercent, completed, partial, expired }) => (
                        <div key={plan.id} className={`rounded-lg border p-4 ${completed ? 'border-emerald-300/35 bg-emerald-300/10' : expired ? 'border-rose-300/45 bg-rose-300/10' : 'border-cyan-300/30 bg-cyan-300/5'}`}>
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs font-black uppercase tracking-wider text-cyan-100">{formatFullDateBr(String(plan.scheduled_date || studyViewDate).slice(0, 10))} · {dayNameFromDate(String(plan.scheduled_date || studyViewDate).slice(0, 10))}</p>
                              <p className="text-sm font-black uppercase tracking-wider text-slate-200">{plan.start_time || 'Horário livre'} às {planEndTime(plan)} · {plan.activity_type}</p>
                              <h4 className="mt-1 text-xl font-black text-white">{plan.subject_name || 'Atividade geral'}</h4>
                              <p className="mt-1 text-sm text-slate-300">{plan.content || 'Conteúdo livre'} · {formatHoursFromMinutes(plannedMinutes)} planejados</p>
                            </div>
                            <span className={`rounded-lg px-3 py-2 text-sm font-black ${completed ? 'bg-emerald-300 text-slate-950' : partial ? 'bg-amber-300 text-slate-950' : expired ? 'bg-rose-300 text-slate-950' : 'bg-cyan-300 text-slate-950'}`}>{completed ? 'Executado' : partial ? 'Execução parcial' : expired ? 'Tempo expirado' : 'Agendado'}</span>
                          </div>
                          <div className={`mt-4 grid gap-3 ${activityRequiresQuestions(plan.activity_type) ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                            <div className="rounded-lg border border-slate-700 bg-slate-950/45 p-3"><p className="text-xs text-slate-400">Tempo executado</p><strong className="mt-1 block text-white">{formatStudyElapsed(actualSeconds)}</strong></div>
                            <div className="rounded-lg border border-slate-700 bg-slate-950/45 p-3"><p className="text-xs text-slate-400">Execução do bloco</p><strong className="mt-1 block text-white">{executionPercent}%</strong></div>
                            {activityRequiresQuestions(plan.activity_type) && <div className="rounded-lg border border-slate-700 bg-slate-950/45 p-3"><p className="text-xs text-slate-400">Questões executadas e acertos</p><strong className="mt-1 block text-white">{execution ? `${Number(execution.questions_done || execution.completed_questions || 0)} · ${Number(execution.correct_answers || 0)}` : 'Ainda não registrado'}</strong></div>}
                          </div>
                          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-900"><div className={`h-full transition-all ${completed ? 'bg-emerald-400' : expired ? 'bg-rose-400' : 'bg-cyan-400'}`} style={{ width: `${executionPercent}%` }} /></div>
                          {!expired && (
                            <div className="mt-4 border-t border-slate-700/80 pt-4">
                              {activeStudyBlockId === plan.id && activityRequiresQuestions(plan.activity_type) && (
                                <div className="mb-3 grid gap-3 rounded-lg border border-cyan-300/35 bg-cyan-300/10 p-3 sm:grid-cols-3">
                                  <div><p className="text-xs font-bold uppercase tracking-wider text-cyan-100">Meta de questões</p><strong className="mt-1 block text-xl text-white">{Number(plan.planned_questions || 0)}</strong></div>
                                  <Field label="Quantidade executada"><input className={inputClass()} type="number" min={1} value={finishForm.questions_done || ''} onChange={(event) => setFinishForm({ ...finishForm, questions_done: Number(event.target.value || 0) })} /></Field>
                                  <Field label="Quantidade de acertos"><input className={inputClass()} type="number" min={0} max={finishForm.questions_done || undefined} value={finishForm.correct_answers || ''} onChange={(event) => setFinishForm({ ...finishForm, correct_answers: Number(event.target.value || 0) })} /></Field>
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                              <Button className="min-w-[132px]" disabled={!canStartScheduledBlock(plan, todayIso) || Boolean(activeStudyBlock && activeStudyBlock.id !== plan.id) || (activeStudyBlockId === plan.id && studyTimerRunning)} onClick={() => activeStudyBlockId === plan.id ? setStudyTimerRunning(true) : startStudy(plan)}><TimerReset className="h-4 w-4" /> Iniciar</Button>
                              <Button className="min-w-[132px]" tone="quiet" disabled={activeStudyBlockId !== plan.id || !studyTimerRunning} onClick={() => setStudyTimerRunning(false)}><ShieldCheck className="h-4 w-4" /> Pausar</Button>
                              <Button className="min-w-[132px]" tone="quiet" disabled={activeStudyBlockId !== plan.id} onClick={finishStudy}><CheckCircle2 className="h-4 w-4" /> Concluir</Button>
                              {activeStudyBlockId === plan.id && <div className="inline-flex min-h-12 items-center gap-2 rounded-lg border border-cyan-300/45 bg-cyan-300/10 px-4 py-3 text-sm font-black text-cyan-50"><Clock3 className="h-5 w-5" /> Tempo em andamento: {formatTimer(studyElapsedSeconds)}</div>}
                              </div>
                            </div>
                          )}
                          {expired && !completed && plan.id && (
                            <div className="mt-4 rounded-lg border border-amber-300/35 bg-amber-300/10 p-3">
                              <p className="text-sm font-black text-amber-100">Registro excepcional de execução retroativa</p>
                              <p className="mt-1 text-xs leading-5 text-slate-300">Estudou antes de usar o sistema? Informe a execução real deste bloco para que ele não fique como pendência.</p>
                              <div className={`mt-3 grid gap-3 ${activityRequiresQuestions(plan.activity_type) ? 'sm:grid-cols-3' : 'sm:grid-cols-1'}`}>
                                <Field label="Tempo realizado (min)"><input className={inputClass()} type="number" min={1} value={retroactiveExecutionDrafts[plan.id]?.minutes ?? plannedMinutes} onChange={(event) => updateRetroactiveExecutionDraft(plan.id!, plannedMinutes, { minutes: Number(event.target.value || 0) })} /></Field>
                                {activityRequiresQuestions(plan.activity_type) && <>
                                  <Field label="Quantidade executada"><input className={inputClass()} type="number" min={0} value={retroactiveExecutionDrafts[plan.id]?.questions ?? 0} onChange={(event) => updateRetroactiveExecutionDraft(plan.id!, plannedMinutes, { questions: Number(event.target.value || 0) })} /></Field>
                                  <Field label="Quantidade de acertos"><input className={inputClass()} type="number" min={0} value={retroactiveExecutionDrafts[plan.id]?.correct ?? 0} onChange={(event) => updateRetroactiveExecutionDraft(plan.id!, plannedMinutes, { correct: Number(event.target.value || 0) })} /></Field>
                                </>}
                              </div>
                              <div className="mt-3"><Button tone="quiet" onClick={() => registerRetroactiveExecution(plan)}><Save className="h-4 w-4" /> Registrar execução realizada</Button></div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 rounded-lg border border-slate-700 bg-slate-950/45 p-4 text-sm leading-6 text-slate-300">Não há blocos agendados para esta data. Use o Plano de Estudos para organizar uma rotina possível ou aproveite para recuperar uma pendência.</p>
                  )}
                  {selectedExtraStudyLogs.length > 0 && <p className="mt-3 text-sm text-emerald-100">Há {pluralize(selectedExtraStudyLogs.length, 'execução extra', 'execuções extras')} {selectedExtraStudyLogs.length === 1 ? 'registrada' : 'registradas'} nesta data, somando {formatStudyElapsed(selectedExtraStudyLogs.reduce((sum, log) => sum + studyLogElapsedSeconds(log), 0))}.</p>}
                </div>
              </div>

              <div className="glass rounded-lg p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-2xl font-black text-white">Estudo extra</h3>
                    <p className="text-slate-300">Use quando estudar algo fora do plano. Todo esforço inteligente conta, inclusive os estudos extras.</p>
                  </div>
                  <Button tone="quiet" onClick={() => setShowExtraStudy(!showExtraStudy)}><Plus className="h-5 w-5" /> Registrar extra</Button>
                </div>
                {showExtraStudy && (
                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Disciplina"><input className={inputClass()} value={logForm.subject_name} onChange={(event) => setLogForm({ ...logForm, subject_name: event.target.value })} /></Field>
                    <Field label="Tipo de atividade"><select className={inputClass()} value={logForm.activity_type} onChange={(event) => setLogForm({ ...logForm, activity_type: event.target.value })}>{activities.map((activity) => <option key={activity}>{activity}</option>)}</select></Field>
                    <Field label="Duração realizada (h)"><input className={inputClass()} type="number" min={0.1} step="0.25" value={logForm.completed_hours} onChange={(event) => setLogForm({ ...logForm, completed_hours: Number(event.target.value), planned_hours: Number(event.target.value) })} /></Field>
                    <Field label="Conteúdo"><input className={inputClass()} value={logForm.content} onChange={(event) => setLogForm({ ...logForm, content: event.target.value })} /></Field>
                    {extraStudyNeedsQuestions && (
                      <>
                        <Field label="Questões realizadas"><input className={inputClass()} type="number" min={0} value={finishForm.questions_done} onChange={(event) => setFinishForm({ ...finishForm, questions_done: Number(event.target.value) })} /></Field>
                        <Field label="Acertos"><input className={inputClass()} type="number" min={0} value={finishForm.correct_answers} onChange={(event) => setFinishForm({ ...finishForm, correct_answers: Number(event.target.value) })} /></Field>
                      </>
                    )}
                    <Button className="md:col-span-2 xl:col-span-4" onClick={() => { completeStudy(null, true); setShowExtraStudy(false); }}><Save className="h-5 w-5" /> Salvar estudo extra</Button>
                  </div>
                )}
              </div>

              <div className="no-print flex flex-wrap justify-end gap-3">
                <Button tone="quiet" onClick={() => setActive('dashboard')}><BarChart3 className="h-5 w-5" /> Ir para Meu Desempenho</Button>
              </div>
            </section>
          )}

          {active === 'autoavaliacao' && (
            <section className="grid gap-6">
              <div className="glass rounded-lg p-5">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Meu Diagnóstico Inicial</p>
                <h2 className="mt-1 text-3xl font-black text-white">Avalie seu desempenho como estudante</h2>
                <p className="mt-2 max-w-4xl text-slate-300">
                  Responda com sinceridade. Esta etapa ajuda concurseiros, estudantes do ENEM, vestibular, OAB, CFC e certificações a entenderem potencial, método, disciplina, ansiedade, revisão e estratégia.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <StatCard label="Potencial atual" value={`${result.percentage}%`} icon={Brain} />
                  <StatCard label="Pontuação" value={`${result.total}/50`} icon={Target} color="#facc15" />
                  <StatCard label="Estratégia" value={`${strategicTotal} pts`} icon={BarChart3} color="#34d399" />
                  <StatCard label="Classificação" value={result.classification} icon={Award} color="#fb7185" />
                </div>
                <div className="mt-4 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4 text-sm leading-6 text-cyan-50">
                  🌟 Missão inicial: responda com sinceridade, descubra seu ponto de partida e transforme autoconhecimento em avanço real.
                </div>
              </div>

              <div ref={resultRef} className="print-card glass rounded-lg p-5">
                <h3 className="mb-2 text-2xl font-black text-white">Avaliação inicial de desempenho</h3>
                <p className="mb-4 text-slate-300">Clique na resposta que melhor representa sua realidade atual. Cada escolha é um passo para transformar consciência em estratégia.</p>
                <div className="grid gap-3">
                  {diagnosisQuestions.map((question, index) => (
                    <div key={question} className="grid gap-3 rounded-lg border border-slate-700 bg-slate-950/35 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-bold text-slate-100">{index + 1}. {question}</span>
                        <span className="rounded-lg bg-yellow-300 px-3 py-1 text-xs font-black text-slate-950">Nota {answers[index]}/5</span>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
                        {selfAssessmentScale.map((option) => (
                          <button
                            key={`${question}-${option.value}`}
                            onClick={() => setAnswers(answers.map((value, current) => (current === index ? option.value : value)))}
                            className={`focus-ring rounded-lg border p-3 text-left transition ${answers[index] === option.value ? 'border-yellow-300 bg-yellow-300 text-slate-950 shadow-[0_0_18px_rgba(250,204,21,0.25)]' : 'border-slate-700 bg-slate-900/70 text-slate-200 hover:border-cyan-300'}`}
                          >
                            <strong className="block text-lg">{option.badge}</strong>
                            <span className="text-xs font-bold">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <h3 className="mb-2 mt-6 text-2xl font-black text-white">Questionário estratégico</h3>
                <p className="mb-4 text-slate-300">Escolha a resposta mais honesta. O sistema calcula a pontuação automaticamente e transforma o resultado em orientação prática.</p>
                <div className="grid gap-3">
                  {strategicQuestions.map((question, index) => (
                    <div key={question} className="grid gap-3 rounded-lg border border-slate-700 bg-slate-950/35 p-3 lg:grid-cols-[1fr_280px] lg:items-center">
                      <span className="text-slate-200">{index + 1}. {question}</span>
                      <div className="grid grid-cols-3 gap-2">
                        {['Sim', 'Talvez', 'Não'].map((option) => (
                          <button
                            key={option}
                            onClick={() => setStrategicAnswers(strategicAnswers.map((value, current) => (current === index ? option : value)))}
                            className={`focus-ring rounded-lg px-3 py-3 text-sm font-bold ${strategicAnswers[index] === option ? 'bg-yellow-300 text-slate-950' : 'border border-slate-700 text-slate-200'}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4 text-slate-100">
                  <p><strong>Resultado:</strong> {result.percentage}% de uso do potencial como estudante.</p>
                  <p className="mt-2"><strong>Leitura estratégica:</strong> {strategicTotal < 70 ? 'Reveja conceitos, planejamento, método e fatores psicológicos antes de aumentar a carga.' : 'Você está no caminho certo. Continue ajustando o método e protegendo a constância.'}</p>
                </div>
                <div className="mt-5 rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-sm font-black uppercase tracking-wider text-yellow-100">Plano de ação</p>
                      <h3 className="mt-1 text-xl font-black text-white">Transforme a avaliação em próximos passos</h3>
                      <p className="mt-1 text-sm leading-6 text-slate-200">Defina tarefas simples, atividades práticas e um prazo realista. Este plano também será incluído no PDF.</p>
                    </div>
                    <Button tone="quiet" onClick={() => setSelfActionPlan((items) => [...items, { id: crypto.randomUUID(), task: 'Nova tarefa de melhoria', activity: 'Descreva a atividade prática que será realizada.', deadline: dateToIso(addDays(new Date(), 7)), completed: false }])}><Plus className="h-4 w-4" /> Adicionar tarefa</Button>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {selfActionPlan.map((item, index) => (
                      <div key={item.id} className={`grid gap-3 rounded-lg border p-4 ${item.completed ? 'border-emerald-300/40 bg-emerald-300/10' : 'border-slate-700 bg-slate-950/45'}`}>
                        <div className="flex items-center justify-between gap-3"><span className="text-sm font-black text-yellow-100">Ação {index + 1}</span><label className="flex items-center gap-2 text-sm font-bold text-slate-200"><input type="checkbox" checked={item.completed} onChange={(event) => setSelfActionPlan((items) => items.map((current) => current.id === item.id ? { ...current, completed: event.target.checked } : current))} /> Concluída</label></div>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1.4fr_180px_auto]">
                          <Field label="Tarefa"><input className={inputClass()} value={item.task} onChange={(event) => setSelfActionPlan((items) => items.map((current) => current.id === item.id ? { ...current, task: event.target.value } : current))} /></Field>
                          <Field label="Atividade prática"><input className={inputClass()} value={item.activity} onChange={(event) => setSelfActionPlan((items) => items.map((current) => current.id === item.id ? { ...current, activity: event.target.value } : current))} /></Field>
                          <Field label="Prazo"><input className={inputClass()} type="date" value={item.deadline} onChange={(event) => setSelfActionPlan((items) => items.map((current) => current.id === item.id ? { ...current, deadline: event.target.value } : current))} /></Field>
                          <button type="button" aria-label="Excluir tarefa" onClick={() => setSelfActionPlan((items) => items.filter((current) => current.id !== item.id))} className="self-end rounded-lg border border-rose-300/50 p-3 text-rose-100"><Trash2 className="h-5 w-5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button onClick={saveAssessment}><Save className="h-5 w-5" /> Resultado</Button>
                <Button tone="quiet" onClick={downloadPdf}><Download className="h-5 w-5" /> Baixar PDF</Button>
                <Button tone="quiet" onClick={() => sharePdfDocument(createAssessmentPdf(), assessmentPdfMeta())}><Send className="h-5 w-5" /> Compartilhar PDF</Button>
                <Button tone="quiet" onClick={() => setActive('vida')}><Radar className="h-5 w-5" /> Ir para Roda da Vida</Button>
              </div>
            </section>
          )}

          {active === 'vida' && (
            <section className="glass rounded-lg p-5">
              <h2 className="mb-4 text-2xl font-black text-white">Roda da Vida do Estudante</h2>
              <ToolIntro
                title="Como usar a Roda da Vida"
                text="Antes de marcar as notas, pense na sua vida de estudante como um conjunto de áreas que sustentam a aprovação. A ideia não é buscar perfeição, e sim enxergar onde um pequeno ajuste pode melhorar seu rendimento."
                examples={[
                  'Concurseiro: se Saúde e disposição estiver baixa, reduza sessões longas e inclua pausas planejadas.',
                  'ENEM ou vestibular: se Equilíbrio emocional estiver baixo, intercale simulados com revisões leves.',
                  'OAB, CFC ou certificações: se Recursos ou Família pesarem, planeje horários realistas e protegidos.'
                ]}
              />
              <AutoSaveNote />
              <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_620px]">
                <div className="grid gap-3 sm:grid-cols-2">
                  {lifeWheelAreas.map((area) => (
                    <Field key={area} label={area}>
                      <input type="range" min={0} max={10} value={lifeValues[area]} onChange={(event) => setLifeValues({ ...lifeValues, [area]: Number(event.target.value) })} />
                      <span className="text-sm text-cyan-100">Nota {lifeValues[area]}</span>
                    </Field>
                  ))}
                </div>
                <div className="grid min-w-0 gap-3">
                  <div ref={lifeRef} className="min-w-0 rounded-lg border border-slate-700 bg-slate-950/55 p-3 sm:p-4">
                    <h3 className="mb-3 text-lg font-black text-white">Resultado visual</h3>
                    <div className="w-full max-w-full">
                      <div className="mx-auto h-[330px] w-full max-w-[390px] sm:h-[430px] sm:max-w-[520px] xl:h-[460px] xl:max-w-[560px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={lifeData} margin={{ top: 30, right: 44, bottom: 30, left: 44 }}>
                          <PolarGrid stroke="#334155" />
                          <PolarAngleAxis dataKey="label" tick={{ fill: '#cbd5e1', fontSize: 10 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 10]} stroke="#94a3b8" tick={{ fill: '#cbd5e1', fontSize: 11 }} />
                          <RadarSeries dataKey="nota" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.32} />
                        </RadarChart>
                      </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
                      {lifeData.map((item) => <span key={item.area}>{item.area}: <strong className="text-cyan-100">{item.nota}/10</strong></span>)}
                    </div>
                  </div>
                </div>
              </div>
              <ActionPlanEditor title="Plano de ação da Roda da Vida" items={lifeActionPlan} onChange={setLifeActionPlan} areaOptions={lifePriorityAreas} />
              <div className="mt-5 flex flex-wrap justify-end gap-3 border-t border-slate-700 pt-5">
                <Button tone="quiet" onClick={downloadLifePdf}><Download className="h-5 w-5" /> Baixar PDF</Button>
                <Button tone="quiet" onClick={() => sharePdfDocument(createLifePdf(), lifePdfMeta())}><Send className="h-5 w-5" /> Compartilhar PDF</Button>
              </div>
            </section>
          )}

          {active === 'swot' && (
            <MatrixSection
              title="SWOT / FOFA"
              fields={['Forças - fatores internos', 'Fraquezas - fatores internos', 'Oportunidades - fatores externos', 'Ameaças - fatores externos']}
              footer="Síntese: Forças e Fraquezas são fatores internos, ligados ao estudante, método, rotina, disciplina, energia e comportamento. Oportunidades e Ameaças são fatores externos, ligados ao edital, calendário, concorrência, ambiente, recursos e condições ao redor."
              nextLabel="Ir para Matriz de Perdas e Ganhos"
              onNext={() => setActive('ganhos')}
            />
          )}

          {active === 'ganhos' && (
            <MatrixSection
              title="Matriz de Perdas e Ganhos"
              fields={['O que ganho se obtiver aprovação?', 'O que perco se obtiver aprovação?', 'O que ganho se não obtiver aprovação?', 'O que perco se não obtiver aprovação?']}
              footer="Compromisso: eu escolho estudar com constância, consciência e método para construir minha aprovação."
              nextLabel="Ir para Livros"
              onNext={() => setActive('livros')}
            />
          )}

          {active === 'tempo' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">⏱️ Foco, descanso e constância</p>
                <h2 className="mt-1 text-4xl font-black text-white">Gestão do Tempo</h2>
                <ToolIntro
                  title="Como usar a Gestão do Tempo"
                  text="Use os ciclos para estudar com foco e descansar quando fizer sentido para você. A sugestão inicial é 25 minutos de estudo e 5 de descanso, mas você pode informar 0 minuto para pular a pausa de qualquer ciclo. Após o quarto ciclo, o sistema reinicia automaticamente no primeiro e continua até você pausar ou reiniciar o timer."
                />
                <div className="mt-5 grid gap-4 lg:grid-cols-3">
                  <div className="rounded-lg border border-yellow-300/35 bg-yellow-300/10 p-4">
                    <p className="text-sm font-black uppercase tracking-wider text-yellow-100">1. Ajuste e inicie</p>
                    <p className="mt-2 leading-7 text-slate-200">Escolha os tempos de estudo e descanso, depois aperte Iniciar. Concentre-se apenas no próximo ciclo: o progresso nasce de blocos pequenos, repetidos com alegria e constância.</p>
                  </div>
                  <div className="rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4">
                    <p className="text-sm font-black uppercase tracking-wider text-cyan-100">2. Acompanhe o aviso</p>
                    <p className="mt-2 leading-7 text-slate-200">Quando uma etapa termina, a próxima começa automaticamente. O som continua até você clicar em OK, sem interromper a contagem do próximo tempo.</p>
                  </div>
                  <div className="rounded-lg border border-emerald-300/30 bg-emerald-300/10 p-4">
                    <p className="text-sm font-black uppercase tracking-wider text-emerald-100">3. Registre sua evolução</p>
                    <p className="mt-2 leading-7 text-slate-200">Ao concluir o estudo, vá para Estudar Agora e registre o que realizou. Cada ciclo cumprido fortalece seu foco, sua disciplina e seus Pontos de Evolução.</p>
                  </div>
                </div>
                <div className="mt-4 rounded-lg border border-slate-700 bg-slate-950/45 p-4">
                  <p className="font-bold text-white">Funcionamento do timer</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">
                    O timer continua funcionando enquanto esta página estiver aberta, inclusive se você estiver em outra aba, no Word, Excel, PDF ou com o navegador minimizado. Se a página for fechada, o navegador não consegue manter o gerenciamento do tempo ativo.
                  </p>
                </div>
              </div>

              <div className="glass rounded-lg p-5">
                <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
                  <div>
                    <AutoSaveNote />
                    <div className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold uppercase tracking-wider text-yellow-100">
                            {currentMode === 'study' ? '📚 Estudo em andamento' : '☕ Descanso em andamento'}
                          </p>
                          <h3 className="mt-1 text-2xl font-black text-white">
                            {currentCycleIndex + 1}º Ciclo - {currentMode === 'study' ? 'Estudo' : 'Descanso'}
                          </h3>
                        </div>
                        <span className="rounded-lg border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
                          ⭐ {timeCompleted}/{timeCycles.length * 2} etapas
                        </span>
                      </div>
                      <div className="mt-6 text-center text-7xl font-black text-white sm:text-8xl">{formatTimer(timeRemaining)}</div>
                      <div className="mt-5 h-4 overflow-hidden rounded-full bg-slate-900">
                        <div className="h-full rounded-full bg-yellow-300 transition-all" style={{ width: `${timerProgress}%` }} />
                      </div>
                      <div className="mt-5 rounded-lg border border-slate-700 bg-slate-950/45 p-3 shadow-inner shadow-slate-950/40">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
                          <p className="text-xs font-black uppercase tracking-wider text-slate-300">Controles do ciclo</p>
                          <p className="text-xs text-slate-400">Pausar conserva o tempo; Parar encerra o ciclo atual.</p>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                          <Button className="min-h-14 whitespace-nowrap shadow-[0_8px_20px_rgba(250,204,21,0.18)] lg:px-3" onClick={() => {
                            if (timeRemaining <= 0) resetTimer(timeStep);
                            startTimeManagement();
                          }}><TimerReset className="h-5 w-5" /> Iniciar ciclo</Button>
                          <Button className="min-h-14 whitespace-nowrap lg:px-3" tone="quiet" onClick={pauseTimeManagement}><ShieldCheck className="h-5 w-5" /> Pausar</Button>
                          <Button className="min-h-14 whitespace-nowrap border-rose-400/50 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20 lg:px-3" tone="quiet" onClick={stopTimeManagement}><X className="h-5 w-5" /> Parar</Button>
                          <Button className="min-h-14 whitespace-nowrap lg:px-3" tone="quiet" onClick={() => resetTimer(0)}><Trash2 className="h-5 w-5" /> Reiniciar</Button>
                          <Button className="min-h-14 whitespace-nowrap border-cyan-300/50 bg-cyan-300/10 text-cyan-50 hover:bg-cyan-300/20 lg:px-3" tone="quiet" onClick={() => setActive('registro')}><BookOpen className="h-5 w-5" /> Estudar Agora</Button>
                        </div>
                      </div>
                      <div className="mt-5 rounded-lg border border-slate-700 bg-slate-950/45 p-4">
                        <p className="text-sm font-bold uppercase tracking-wider text-yellow-100">Escolha o som do aviso</p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          {timerSoundOptions.map((option) => (
                            <button
                              key={option.id}
                              className={`rounded-lg border p-3 text-left transition ${timerSound === option.id ? 'border-yellow-300 bg-yellow-300 text-slate-950' : 'border-slate-700 bg-slate-900/70 text-slate-200 hover:border-yellow-300'}`}
                              onClick={() => {
                                setTimerSound(option.id);
                                setMessage(`🔔 Som "${option.label}" selecionado. Cada aviso vai te chamar de volta para a sua Missão de Estudo.`);
                              }}
                            >
                              <strong>{option.label}</strong>
                              <span className={`mt-1 block text-xs ${timerSound === option.id ? 'text-slate-800' : 'text-slate-400'}`}>{option.description}</span>
                            </button>
                          ))}
                        </div>
                        <Button className="mt-3 w-full" tone="quiet" onClick={() => {
                          playTimerSound();
                          setMessage('🔔 Teste de som realizado. Ótimo: agora o timer pode te acompanhar no ritmo certo.');
                        }}><TimerReset className="h-5 w-5" /> Testar som</Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3">
                    {timeCycles.map((cycle, index) => {
                      const activeCycle = index === currentCycleIndex;
                      return (
                        <div key={index} className={`rounded-lg border p-4 ${activeCycle ? 'border-yellow-300 bg-yellow-300/10' : 'border-slate-700 bg-slate-950/45'}`}>
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <strong className="text-white">{index + 1}º Ciclo {activeCycle ? '🔥' : '✨'}</strong>
                            <span className="text-xs text-slate-400">{cycle.study}m estudo {cycle.rest > 0 ? `+ ${cycle.rest}m descanso` : '· sem descanso'}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Field label="Estudo">
                              <input
                                className={inputClass()}
                                type="number"
                                min={1}
                                value={cycle.study}
                                onChange={(event) => {
                                  const value = Math.max(1, Number(event.target.value || 1));
                                  setTimeCycles(timeCycles.map((item, current) => current === index ? { ...item, study: value } : item));
                                  if (!timeRunning && index === currentCycleIndex && currentMode === 'study') setTimeRemaining(value * 60);
                                }}
                              />
                            </Field>
                            <Field label="Descanso (0 para pular)">
                              <input
                                className={inputClass()}
                                type="number"
                                min={0}
                                value={cycle.rest}
                                onChange={(event) => {
                                  const value = Math.max(0, Number(event.target.value || 0));
                                  setTimeCycles(timeCycles.map((item, current) => current === index ? { ...item, rest: value } : item));
                                  if (!timeRunning && index === currentCycleIndex && currentMode === 'rest') setTimeRemaining(value * 60);
                                }}
                              />
                            </Field>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {timeCycles.map((cycle, index) => (
                  <div key={index} className="glass rounded-lg p-4">
                    <p className="text-sm text-slate-400">Missão de Estudo {index + 1} 🎯</p>
                    <strong className="mt-2 block text-xl text-white">{cycle.study + cycle.rest} min</strong>
                    <p className="mt-2 text-sm text-slate-300">{cycle.rest > 0 ? 'Foco com coragem, pausa com consciência e retorno ainda mais forte.' : 'Foco contínuo: este ciclo segue direto para o próximo estudo.'}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {active === 'banca' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-5 sm:p-6">
                <p className="text-sm font-black uppercase tracking-wider text-yellow-200">Estratégia guiada pela banca</p>
                <h2 className="mt-1 text-3xl font-black text-white sm:text-4xl">Banca Examinadora</h2>
                <p className="mt-3 max-w-4xl text-base leading-7 text-slate-300 sm:text-lg sm:leading-8">
                  Conheça o padrão de cobrança, as etapas e as estratégias de preparação da sua banca. Use este painel como bússola de estudo e confirme no edital as regras do seu certame.
                </p>
              </div>

              <div className="glass rounded-lg p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="text-xl font-black text-white">Qual é a sua banca?</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-300">Pesquise por nome, sigla, tipo de prova, redação, discursiva, legislação ou outro termo. Um clique em uma banca abre diretamente o conteúdo selecionado.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button tone="quiet" onClick={() => setActive('tecnicas')}><BookOpen className="h-5 w-5" /> Ir para Técnicas de Estudo</Button>
                    <span className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 px-3 py-2 text-xs font-bold text-yellow-100">{examBoards.length} perfis disponíveis</span>
                  </div>
                </div>
                <label className="relative mt-4 block">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cyan-200" />
                  <input
                    className={`${inputClass()} pl-12`}
                    value={examBoardSearch}
                    onChange={(event) => setExamBoardSearch(event.target.value)}
                    placeholder="Ex.: Cebraspe, prova discursiva, redação, certo ou errado"
                    aria-label="Pesquisar banca examinadora"
                  />
                </label>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {examBoards
                    .filter((board) => normalizeSearchText(`${board.name} ${board.acronym} ${board.institution} ${board.scope} ${board.focus} ${board.search_text || ''}`).includes(normalizeSearchText(examBoardSearch.trim())))
                    .map((board) => (
                      <button
                        key={board.id}
                        type="button"
                        onClick={() => void loadExamBoardProfile(board.id)}
                        className={`focus-ring min-h-0 rounded-lg border p-4 text-left transition ${selectedExamBoard?.id === board.id ? 'border-yellow-300 bg-yellow-300 text-slate-950 shadow-[0_0_24px_rgba(253,224,71,0.18)]' : 'border-slate-700 bg-slate-950/45 text-slate-100 hover:border-cyan-300/70 hover:bg-slate-900'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0"><strong className="block text-base">{board.name}</strong><span className={`mt-1 block text-xs ${selectedExamBoard?.id === board.id ? 'text-slate-700' : 'text-slate-400'}`}>{board.acronym} · {board.scope}</span></div>
                          <Search className="h-5 w-5 shrink-0" />
                        </div>
                        <p className={`mt-2 text-xs leading-5 ${selectedExamBoard?.id === board.id ? 'text-slate-800' : 'text-slate-300'}`}>{board.focus}</p>
                      </button>
                    ))}
                </div>
                {!examBoardLoading && examBoardSearch && !examBoards.some((board) => `${board.name} ${board.acronym} ${board.institution}`.toLocaleLowerCase('pt-BR').includes(examBoardSearch.trim().toLocaleLowerCase('pt-BR'))) && (
                  <p className="mt-4 rounded-lg border border-slate-700 bg-slate-950/45 p-3 text-sm text-slate-300">Ainda não há um perfil nesta lista. Pesquise pela sigla ou use o edital e o portal oficial da organizadora para orientar seu plano.</p>
                )}
              </div>

              {examBoardLoading && <div className="glass rounded-lg p-6 text-center text-slate-300">Carregando informações da banca...</div>}
              {examBoardError && <div className="rounded-lg border border-rose-300/45 bg-rose-950/40 p-4 text-rose-50">{examBoardError}</div>}

              {selectedExamBoard && !examBoardLoading && (
                <div id="perfil-banca-detalhes" className="grid gap-5">
                  <div className="rounded-lg border border-yellow-300/45 bg-gradient-to-br from-yellow-300/15 via-slate-900 to-cyan-300/10 p-5 sm:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-4xl">
                        <p className="text-sm font-black uppercase tracking-wider text-yellow-100">Perfil selecionado</p>
                        <h3 className="mt-1 text-3xl font-black text-white">{selectedExamBoard.name}</h3>
                        <p className="mt-3 leading-7 text-slate-200">{selectedExamBoard.overview}</p>
                      </div>
                      <a href={selectedExamBoard.officialSite} target="_blank" rel="noopener noreferrer" className="button-like focus-ring inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-cyan-300/60 bg-slate-950/60 px-4 py-3 font-bold text-cyan-50 transition hover:bg-cyan-300/15"><ExternalLink className="h-5 w-5" /> Abrir site oficial</a>
                    </div>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <StatCard label="Sigla" value={selectedExamBoard.acronym} icon={FileText} color="#facc15" />
                      <StatCard label="Abrangência" value={selectedExamBoard.scope} icon={Target} color="#22d3ee" />
                      <StatCard label="Experiência" value={selectedExamBoard.operatesSince} icon={Award} color="#34d399" />
                      <StatCard label="Atuação" value={selectedExamBoard.focus} icon={GraduationCap} color="#a78bfa" />
                    </div>
                    {selectedExamBoard.officialSource?.page_description && (
                      <div className="mt-5 rounded-lg border border-cyan-300/35 bg-cyan-300/10 p-4 text-cyan-50">
                        <p className="text-xs font-black uppercase tracking-wider text-cyan-100">Atualização da fonte oficial</p>
                        {selectedExamBoard.officialSource.page_title && <p className="mt-2 font-bold text-white">{selectedExamBoard.officialSource.page_title}</p>}
                        <p className="mt-1 text-sm leading-6">{selectedExamBoard.officialSource.page_description}</p>
                        {selectedExamBoard.officialSource.last_success_at && <p className="mt-2 text-xs text-cyan-100">Consulta realizada em {formatDateBr(selectedExamBoard.officialSource.last_success_at.slice(0, 10))}.</p>}
                      </div>
                    )}
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    {[
                      { title: 'Como a banca costuma avaliar', icon: FileText, items: selectedExamBoard.evaluation, color: 'yellow' },
                      { title: 'Formato das questões', icon: CheckCircle2, items: selectedExamBoard.questionFormat, color: 'cyan' },
                      { title: 'Pontuação e classificação', icon: Award, items: selectedExamBoard.scoring, color: 'emerald' },
                      { title: 'Estilo de cobrança', icon: Brain, items: selectedExamBoard.style, color: 'violet' },
                      { title: 'Pegadinhas e pontos de atenção', icon: Target, items: selectedExamBoard.attentionPoints, color: 'rose' },
                      { title: 'Gestão do tempo na prova', icon: Clock3, items: selectedExamBoard.timeStrategy, color: 'amber' }
                    ].map((section) => {
                      const Icon = section.icon;
                      const accents: Record<string, string> = { yellow: 'border-yellow-300/35 bg-yellow-300/5 text-yellow-100', cyan: 'border-cyan-300/35 bg-cyan-300/5 text-cyan-100', emerald: 'border-emerald-300/35 bg-emerald-300/5 text-emerald-100', violet: 'border-violet-300/35 bg-violet-300/5 text-violet-100', rose: 'border-rose-300/35 bg-rose-300/5 text-rose-100', amber: 'border-amber-300/35 bg-amber-300/5 text-amber-100' };
                      return <div key={section.title} className={`rounded-lg border p-5 ${accents[section.color]}`}><div className="flex items-center gap-3"><Icon className="h-5 w-5" /><h4 className="text-lg font-black text-white">{section.title}</h4></div><ul className="mt-4 grid gap-3 text-sm leading-6 text-slate-200">{section.items.map((item) => <li key={item} className="flex gap-2"><span aria-hidden="true">•</span><span>{item}</span></li>)}</ul></div>;
                    })}
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="glass rounded-lg p-5"><div className="flex items-center gap-3"><CalendarDays className="h-5 w-5 text-yellow-200" /><h4 className="text-xl font-black text-white">Estratégia de preparação</h4></div><ol className="mt-4 grid gap-3 text-sm leading-6 text-slate-200">{selectedExamBoard.studyStrategy.map((item, index) => <li key={item} className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-yellow-300 font-black text-slate-950">{index + 1}</span><span>{item}</span></li>)}</ol></div>
                    <div className="glass rounded-lg p-5"><div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-cyan-200" /><h4 className="text-xl font-black text-white">Estratégia para o dia da prova</h4></div><ol className="mt-4 grid gap-3 text-sm leading-6 text-slate-200">{selectedExamBoard.examDayStrategy.map((item, index) => <li key={item} className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-cyan-300 font-black text-slate-950">{index + 1}</span><span>{item}</span></li>)}</ol></div>
                  </div>

                  <div className="rounded-lg border border-slate-700 bg-slate-950/45 p-5">
                    <h4 className="text-xl font-black text-white">Fontes e conferência do edital</h4>
                    <p className="mt-2 text-sm leading-6 text-slate-300">Antes de transformar estas orientações em meta de estudo, confira as publicações oficiais do seu concurso: edital, retificações, cronograma, provas, gabaritos, espelhos e comunicados.</p>
                    <ul className="mt-4 grid gap-2 text-sm text-cyan-100">{selectedExamBoard.sources.map((source) => <li key={source} className="flex gap-2"><ExternalLink className="mt-0.5 h-4 w-4 shrink-0" />{source}</li>)}</ul>
                    <div className="mt-4 rounded-lg border border-rose-300/40 bg-rose-950/35 p-3 text-sm leading-6 text-rose-50"><strong>Atenção:</strong> {selectedExamBoard.disclaimer}</div>
                  </div>
                </div>
              )}
            </section>
          )}

          {active === 'tecnicas' && <StudyTechniquesSection onGoToDiagnosis={() => setActive('autoavaliacao')} />}

          {active === 'edital' && <EditalSection />}

          {active === 'consulta' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Histórico de Estudos</p>
                <h2 className="mt-1 text-4xl font-black text-white">Retome suas informações</h2>
              </div>

              <div className="glass rounded-lg p-5">
                <h3 className="mb-2 text-2xl font-black text-white">Pesquisar por WhatsApp</h3>
                <p className="mb-4 text-sm leading-6 text-slate-300">Informe o WhatsApp e a senha de 6 números criados no primeiro cadastro para acessar seus registros com segurança.</p>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                  <input className={inputClass()} inputMode="tel" value={consultWhatsapp} onChange={(event) => { setConsultWhatsapp(event.target.value); setConsultFound(false); }} placeholder="WhatsApp usado no cadastro" />
                  <input className={inputClass()} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={consultPassword} onChange={(event) => { setConsultPassword(event.target.value.replace(/\D/g, '').slice(0, 6)); setConsultFound(false); }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void resumeStudiesByWhatsapp(); } }} placeholder="Senha de 6 números" />
                  <Button disabled={consulting} onClick={resumeStudiesByWhatsapp}><FileText className="h-5 w-5" /> {consulting ? 'Entrando...' : 'Continuar os estudos'}</Button>
                </div>
              </div>

              {historyReady && (
                <section className="rounded-lg border border-cyan-300/40 bg-cyan-300/10 p-5 sm:p-6" aria-labelledby="retomada-estudos">
                  <p className="text-sm font-black uppercase tracking-wider text-cyan-100">Sua retomada de estudos</p>
                  <h3 id="retomada-estudos" className="mt-1 text-2xl font-black text-white sm:text-3xl">{greeting}, {participantFirstName}.</h3>
                  {nextStudyMission ? (
                    <p className="mt-3 max-w-4xl text-base leading-7 text-slate-100 sm:text-lg">Sua próxima Missão de Estudo é <strong className="text-yellow-100">{nextStudyMission.subject_name || 'uma atividade planejada'}</strong>, às <strong className="text-yellow-100">{nextStudyMission.start_time || 'horário livre'}</strong>, durante <strong className="text-yellow-100">{formatHoursFromMinutes(Number(nextStudyMission.planned_minutes || Math.round(Number(nextStudyMission.duration_hours || 0) * 60) || 0))}</strong>. Cada início fortalece a sua constância.</p>
                  ) : (
                    <p className="mt-3 max-w-4xl text-base leading-7 text-slate-100 sm:text-lg">Sua próxima Missão de Estudo será definida assim que você confirmar um plano. Organize uma semana possível e avance com segurança.</p>
                  )}
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Percentual semanal cumprido" value={`${historyWeeklyCompletion}%`} icon={CheckCircle2} color="#34d399" />
                    <StatCard label="Sequência atual" value={`${dashboard?.cards?.streak || 0} ${Number(dashboard?.cards?.streak || 0) === 1 ? 'dia' : 'dias'}`} icon={Flame} color="#fb7185" />
                    <StatCard label="Horas realizadas" value={formatHoursFromMinutes(historyWeeklyExecutedMinutes)} icon={Clock3} color="#22d3ee" />
                    <StatCard label="Pendências" value={`${pendingStudyPlans.length} bloco${pendingStudyPlans.length === 1 ? '' : 's'}`} icon={AlertTriangle} color="#fb7185" />
                  </div>
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <Button onClick={() => { setActive('registro'); window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }}><TimerReset className="h-5 w-5" /> Estudar Agora</Button>
                    <Button tone="quiet" onClick={() => { setActive('plano'); window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }}><CalendarDays className="h-5 w-5" /> Continuar plano</Button>
                  </div>
                </section>
              )}

              {consultFound && savedPlanVersions.length > 0 && (
                <div className="glass rounded-lg p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-wider text-emerald-200">Versões salvas</p>
                      <h3 className="mt-1 text-2xl font-black text-white">Histórico dos planos de estudos</h3>
                      <p className="mt-1 text-sm leading-6 text-slate-300">A versão mais recente registra a última alteração salva. As semanas confirmadas do plano continuam disponíveis em Estudar Agora e alimentam o Meu Desempenho.</p>
                    </div>
                    <span className="rounded-lg border border-emerald-300/35 bg-emerald-300/10 px-3 py-2 text-sm font-black text-emerald-50">{pluralize(savedPlanVersions.length, 'versão', 'versões')}</span>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {savedPlanVersions.map((version, index) => (
                      <div key={version.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${index === 0 ? 'border-yellow-300/50 bg-yellow-300/10' : 'border-slate-700 bg-slate-950/45'}`}>
                        <div>
                          <p className="font-black text-white">{version.fileName}</p>
                          <p className="mt-1 text-sm text-slate-300">Versão {version.versionNumber} · {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(version.savedAt))} · {pluralize(version.blockCount, 'bloco')}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-md px-3 py-1 text-xs font-black ${index === 0 ? 'bg-yellow-300 text-slate-950' : 'bg-slate-800 text-slate-100'}`}>{index === 0 ? 'VERSÃO ATUAL' : 'HISTÓRICO'}</span>
                          {version.snapshot?.blocks && <Button tone="quiet" onClick={() => downloadSavedPlanVersionPdf(version)}><Download className="h-4 w-4" />Baixar PDF</Button>}
                          {index > 0 && <Button tone="quiet" onClick={() => void deleteSavedPlanVersion(version)} className="border-rose-300/45 text-rose-100 hover:bg-rose-400/10"><Trash2 className="h-4 w-4" />Apagar</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {consultFound && (
                <div className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-5">
                  <p className="text-sm font-bold uppercase tracking-wider text-yellow-100">Próximo passo</p>
                  <h3 className="mt-1 text-2xl font-black text-white">Como você deseja continuar?</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-200">Seus registros foram localizados. Escolha a ação que melhor ajuda você neste momento.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <Button onClick={() => { setActive('registro'); window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }}><TimerReset className="h-5 w-5" />Estudar Agora</Button>
                    <Button tone="quiet" onClick={() => { setActive('dashboard'); window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }}><BarChart3 className="h-5 w-5" />Meu Desempenho</Button>
                    <Button tone="quiet" onClick={() => { setActive('plano'); window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }}><CalendarDays className="h-5 w-5" />Plano de Estudos</Button>
                  </div>
                </div>
              )}

              {consultFound && (
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="glass rounded-lg p-5">
                    <p className="text-sm text-slate-400">Visitante</p>
                    <h3 className="mt-1 text-2xl font-black text-white">{lead.full_name || 'Cadastro em andamento'}</h3>
                    <p className="mt-2 text-slate-300">{lead.whatsapp || 'WhatsApp não informado'}</p>
                    <p className="text-slate-300">{lead.email || 'E-mail não informado'}</p>
                    <p className="mt-2 text-yellow-100">Objetivo: {lead.study_goal === 'Outros' ? lead.study_goal_other || 'Outros' : lead.study_goal || 'Não informado'}</p>
                  </div>
                  <StatCard label="Áreas da Roda alteradas" value={`${savedSummary.lifeFilled}/12`} icon={Radar} />
                  <StatCard label="Registros salvos" value={`${weeklyPlans.length} planos | ${studyLogs.length} execuções`} icon={Save} color="#34d399" />
                </div>
              )}

              {consultFound && (
                <div className="glass rounded-lg p-5">
                  <h3 className="mb-4 text-2xl font-black text-white">Continuar preenchimento</h3>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {[
                      { label: 'Roda da Vida', id: 'vida', icon: Radar },
                      { label: 'SWOT / FOFA', id: 'swot', icon: Network },
                      { label: 'Perdas e Ganhos', id: 'ganhos', icon: Sparkles },
                      { label: 'Gestão do Tempo', id: 'tempo', icon: TimerReset },
                      { label: 'Plano de Estudos', id: 'plano', icon: CalendarDays },
                      { label: 'Estudar Agora', id: 'registro', icon: TimerReset },
                      { label: 'Meu Desempenho', id: 'dashboard', icon: BarChart3 },
                      { label: 'Painel da Comunidade', id: 'geral', icon: Trophy },
                      { label: 'Ecos da Graça', id: 'ecos', icon: Music },
                      { label: 'Editora Liberato', id: 'editora', icon: Library },
                      { label: 'Sugestões de Melhoria', id: 'feedback', icon: MessageSquare }
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <Button key={item.id} tone="quiet" onClick={() => {
                          setLeadSaved(true);
                          setAccessOk(true);
                          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                          setActive(item.id);
                        }}>
                          <Icon className="h-5 w-5" /> {item.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

          {active === 'livros' && (
            <Institutional
              icon={Library}
              title="Livros"
              text="Conheça os livros de Francisney Liberato e aprofunde sua jornada de desenvolvimento, liderança, fé, estudos e crescimento pessoal."
              button="Ver livros"
              href="https://francisney.com.br/livros/"
            />
          )}

          {active === 'curriculo' && (
            <Institutional
              icon={User}
              title="Currículo"
              text="Conheça a trajetória profissional, acadêmica, literária e institucional de Francisney Liberato."
              button="Ver currículo"
              href="https://francisney.com.br/sobre/"
            />
          )}

          {active === 'redes' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Conecte-se depois do diagnóstico</p>
                <h2 className="mt-1 text-4xl font-black text-white">Redes sociais</h2>
                <p className="mt-3 max-w-3xl text-lg leading-8 text-slate-300">
                  Continue aprendendo por meio de conteúdos, vídeos, mensagens, aulas, palestras, livros e novidades de Francisney Liberato.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button-like focus-ring flex items-center justify-between rounded-lg border border-slate-700 bg-slate-950/40 p-4 text-white transition hover:border-yellow-300/60 hover:bg-slate-900"
                  >
                    <span>Francisney Liberato</span>
                    <strong style={{ color: social.color }}>{social.name}</strong>
                  </a>
                ))}
              </div>
            </section>
          )}

          {active === 'ecos' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Música, fé e esperança</p>
                <h2 className="mt-1 text-4xl font-black text-white">Ecos da Graça</h2>
                <p className="mt-3 max-w-4xl text-lg leading-8 text-slate-300">
                  Ouça as composições musicais de Francisney Liberato e permita que mensagens de fé, esperança e graça façam parte dos seus momentos.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {[
                  { label: 'Ouvir no YouTube', href: 'https://youtube.com/@ecosdagraca7?si=XS4aX-XSCSjNdRao', icon: Youtube, color: '#ef4444', text: 'Canções para inspirar sua caminhada com fé.' },
                  { label: 'Ouvir no Spotify', href: 'https://open.spotify.com/playlist/61hayfialh92Rra9LnRWBg?si=afMIs9_vSAyM3r1Opw76rw&pi=VUcn820rTV2zu', icon: Music, color: '#22c55e', text: 'Uma playlist para acompanhar seus momentos.' },
                  { label: 'Seguir no Instagram', href: 'https://www.instagram.com/ecosdagraca7', icon: Instagram, color: '#fb7185', text: 'Acompanhe novidades e mensagens especiais.' }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-like focus-ring rounded-lg border border-slate-700 bg-slate-950/45 p-5 text-white transition hover:border-yellow-300/70 hover:bg-slate-900"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <Icon className="h-7 w-7" style={{ color: item.color }} />
                        <span className="rounded-lg bg-yellow-300/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-yellow-100">Abrir</span>
                      </div>
                      <strong className="mt-4 block text-xl">{item.label}</strong>
                      <p className="mt-2 text-sm leading-6 text-slate-300">{item.text}</p>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {active === 'editora' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Ideias que viram legado</p>
                <h2 className="mt-1 text-4xl font-black text-white">Publique seu livro com a Editora Liberato</h2>
                <p className="mt-3 max-w-4xl text-lg leading-8 text-slate-300">
                  Transforme suas ideias, conhecimentos e histórias em um livro publicado. Conte com a Editora Liberato para dar vida à sua obra com profissionalismo, cuidado editorial e acompanhamento em cada etapa da publicação.
                </p>
                <a
                  href="https://www.instagram.com/editoraliberato/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button-like focus-ring mt-5 inline-flex items-center gap-2 rounded-lg border border-yellow-300/60 bg-yellow-300 px-5 py-3 font-black text-slate-950 transition hover:bg-yellow-200"
                >
                  <Instagram className="h-5 w-5" /> Seguir a Editora Liberato no Instagram
                </a>
              </div>

              <div className="glass rounded-lg p-5">
                <h3 className="mb-2 flex items-center gap-2 text-2xl font-black text-white"><Mail className="h-6 w-6 text-cyan-300" /> Quero publicar meu livro</h3>
                <p className="mb-4 text-slate-300">Preencha seus dados e conte um pouco sobre sua obra. Sua mensagem será enviada para Francisney Liberato com segurança.</p>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Nome completo">
                    <input className={inputClass()} required value={editoraContact.full_name} onChange={(event) => setEditoraContact({ ...editoraContact, full_name: event.target.value })} placeholder="Seu nome completo" />
                  </Field>
                  <Field label="WhatsApp">
                    <input className={inputClass()} required value={editoraContact.whatsapp} onChange={(event) => setEditoraContact({ ...editoraContact, whatsapp: event.target.value })} placeholder="(65) 99999-9999" />
                  </Field>
                  <Field label="E-mail" className="md:col-span-2">
                    <input className={inputClass()} required type="email" value={editoraContact.email} onChange={(event) => setEditoraContact({ ...editoraContact, email: event.target.value })} placeholder="seuemail@exemplo.com" />
                  </Field>
                  <Field label="Mensagem" className="md:col-span-2">
                    <textarea className={`${inputClass()} min-h-[150px] resize-y`} required value={editoraContact.message} onChange={(event) => setEditoraContact({ ...editoraContact, message: event.target.value })} placeholder="Conte sobre seu livro, ideia, tema ou etapa atual do projeto." />
                  </Field>
                  <input
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    value={editoraContact.company}
                    onChange={(event) => setEditoraContact({ ...editoraContact, company: event.target.value })}
                    aria-hidden="true"
                  />
                  <Button className="md:col-span-2" disabled={editoraSending} onClick={submitEditoraContact}>
                    {editoraSending ? <TimerReset className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    {editoraSending ? 'Enviando sua mensagem...' : 'Quero publicar meu livro'}
                  </Button>
                  {editoraStatus.text && (
                    <div className={`md:col-span-2 rounded-lg border p-4 text-sm leading-6 ${editoraStatus.type === 'success' ? 'border-emerald-300/50 bg-emerald-300/10 text-emerald-50' : 'border-rose-300/50 bg-rose-300/10 text-rose-50'}`}>
                      {editoraStatus.text}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {active === 'feedback' && (
            <section className="grid gap-5">
              <div className="glass rounded-lg p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Sua experiência importa</p>
                <h2 className="mt-1 text-4xl font-black text-white">Sugestões de Melhoria</h2>
                <p className="mt-3 max-w-4xl text-lg leading-8 text-slate-300">Conte o que funcionou bem, o que pode ficar mais simples e quais ideias podem tornar a sua jornada de estudos ainda mais produtiva.</p>
              </div>
              <div className="glass rounded-lg p-5">
                <h3 className="mb-2 flex items-center gap-2 text-2xl font-black text-white"><MessageSquare className="h-6 w-6 text-cyan-300" /> Envie sua sugestão</h3>
                <p className="mb-4 text-slate-300">Sua mensagem será enviada com segurança para Francisney Liberato.</p>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Nome completo"><input className={inputClass()} value={feedbackContact.full_name} onChange={(event) => setFeedbackContact({ ...feedbackContact, full_name: event.target.value })} placeholder="Seu nome completo" /></Field>
                  <Field label="WhatsApp"><input className={inputClass()} value={feedbackContact.whatsapp} onChange={(event) => setFeedbackContact({ ...feedbackContact, whatsapp: event.target.value })} placeholder="(65) 99999-9999" /></Field>
                  <Field label="E-mail" className="md:col-span-2"><input className={inputClass()} type="email" value={feedbackContact.email} onChange={(event) => setFeedbackContact({ ...feedbackContact, email: event.target.value })} placeholder="seuemail@exemplo.com" /></Field>
                  <Field label="Sugestão de melhoria" className="md:col-span-2"><textarea className={`${inputClass()} min-h-[150px] resize-y`} value={feedbackContact.message} onChange={(event) => setFeedbackContact({ ...feedbackContact, message: event.target.value })} placeholder="Descreva sua sugestão, dificuldade ou ideia de melhoria." /></Field>
                  <input className="hidden" tabIndex={-1} autoComplete="off" value={feedbackContact.company} onChange={(event) => setFeedbackContact({ ...feedbackContact, company: event.target.value })} aria-hidden="true" />
                  <Button className="md:col-span-2" disabled={feedbackSending} onClick={submitFeedback}>{feedbackSending ? <TimerReset className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}{feedbackSending ? 'Enviando sua sugestão...' : 'Enviar sugestão'}</Button>
                  {feedbackStatus.text && <div className={`md:col-span-2 rounded-lg border p-4 text-sm leading-6 ${feedbackStatus.type === 'success' ? 'border-emerald-300/50 bg-emerald-300/10 text-emerald-50' : 'border-rose-300/50 bg-rose-300/10 text-rose-50'}`}>{feedbackStatus.text}</div>}
                </div>
              </div>
            </section>
          )}

          {active === 'admin' && (
            <Suspense fallback={<div className="glass rounded-lg p-6 text-center text-slate-200">Carregando a área administrativa...</div>}>
              <AdminPanel onGenerateDashboardPdf={downloadAdminDashboardPdf} onResetTestData={resetTestData} onNotice={setMessage} />
            </Suspense>
          )}

          {activeNavigationIndex >= 0 && activeNavigationGroup && activeGroupIndex >= 0 && (
            <nav aria-label="Navegação sequencial entre abas" className="no-print mt-8 border-t border-slate-700/80 pt-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-400">
                  <span className="font-semibold text-slate-200">{activeNavigationGroup.label}</span>
                  <span className="ml-2">Etapa {activeGroupIndex + 1} de {activeGroupItems.length}</span>
                </p>
                <div className="grid gap-2 sm:flex sm:flex-wrap sm:justify-end">
                  {previousNavigationItem && (
                    <Button tone="quiet" onClick={() => navigateToMenuItem(previousNavigationItem)}>
                      <ArrowLeft className="h-4 w-4" />
                      Voltar: {previousNavigationItem.label}
                    </Button>
                  )}
                  {nextNavigationItem && (
                    <Button onClick={() => navigateToMenuItem(nextNavigationItem)}>
                      Próximo: {nextNavigationItem.label}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </nav>
          )}

          <footer className="mt-8 border-t border-slate-800 py-6 text-center text-sm text-slate-400">
            Francisney Liberato | <a className="text-cyan-200" href="https://www.francisney.com.br">www.francisney.com.br</a> | francisneyliberato10@gmail.com
          </footer>
        </main>
      </div>
      {timerNotice && (
        <div className="no-print fixed inset-0 z-[70] grid place-items-center bg-slate-950/70 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg border border-yellow-300/60 bg-slate-950 p-5 text-white shadow-[0_0_40px_rgba(250,204,21,0.25)]">
            <div className="mb-3 inline-flex rounded-lg bg-yellow-300 px-3 py-2 text-sm font-black text-slate-950">
              Gestão do Tempo
            </div>
            <h2 className="text-2xl font-black">{timerNotice.title}</h2>
            <p className="mt-3 leading-7 text-slate-200">{timerNotice.detail}</p>
            <p className="mt-3 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-3 text-sm leading-6 text-cyan-50">
              O próximo tempo já começou automaticamente. Clique em OK quando perceber o aviso; o timer continua funcionando.
            </p>
            <Button className="mt-4 w-full" onClick={() => {
              stopTimerSound();
              setTimerNotice(null);
            }}>
              <CheckCircle2 className="h-5 w-5" /> OK
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionPlanEditor({
  title = 'Plano de ação',
  items,
  onChange,
  areaOptions
}: {
  title?: string;
  items: SelfActionPlanItem[];
  onChange: (items: SelfActionPlanItem[]) => void;
  areaOptions?: Array<{ area: string; nota: number }>;
}) {
  const areaFor = (item: SelfActionPlanItem, index: number) => item.focus_area || areaOptions?.[index % (areaOptions.length || 1)]?.area || '';
  const addItem = () => {
    const index = items.length;
    onChange([...items, {
      id: crypto.randomUUID(),
      task: 'Nova tarefa de melhoria',
      activity: 'Descreva a atividade prática.',
      deadline: dateToIso(addDays(new Date(), 7)),
      completed: false,
      ...(areaOptions?.length ? { focus_area: areaOptions[index % areaOptions.length].area } : {})
    }]);
  };
  return <div className="mt-5 rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-4 sm:p-5">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"><div><p className="text-sm font-black uppercase tracking-wider text-yellow-100">{title}</p><p className="mt-1 text-sm leading-6 text-slate-200">Defina tarefas simples, atividades práticas e um prazo realista para transformar a reflexão em melhoria contínua.</p></div><Button tone="quiet" onClick={addItem}><Plus className="h-4 w-4" /> Adicionar tarefa</Button></div>
    <div className="mt-4 grid gap-3">{items.map((item, index) => <div key={item.id} className={`grid gap-3 rounded-lg border p-4 ${item.completed ? 'border-emerald-300/40 bg-emerald-300/10' : 'border-slate-700 bg-slate-950/45'}`}><div className="flex flex-wrap items-center justify-between gap-3"><div className="flex flex-wrap items-center gap-2"><span className="text-sm font-black text-yellow-100">Ação {index + 1}</span>{areaOptions?.length ? <label className="flex items-center gap-2 text-sm font-bold text-slate-100"><span className="sr-only">Área prioritária da ação {index + 1}</span><select className="max-w-full rounded-md border border-cyan-300/45 bg-slate-950 px-3 py-2 text-sm font-bold text-cyan-50" value={areaFor(item, index)} onChange={(event) => onChange(items.map((current) => current.id === item.id ? { ...current, focus_area: event.target.value } : current))}>{areaOptions.map((area) => <option key={area.area} value={area.area}>{area.area} ({area.nota}/10)</option>)}</select></label> : null}</div><label className="flex items-center gap-2 text-sm font-bold text-slate-200"><input type="checkbox" checked={item.completed} onChange={(event) => onChange(items.map((current) => current.id === item.id ? { ...current, completed: event.target.checked } : current))} /> Concluída</label></div><div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1.4fr_180px_auto]"><Field label="Tarefa"><input className={inputClass()} value={item.task} onChange={(event) => onChange(items.map((current) => current.id === item.id ? { ...current, task: event.target.value } : current))} /></Field><Field label="Atividade prática"><input className={inputClass()} value={item.activity} onChange={(event) => onChange(items.map((current) => current.id === item.id ? { ...current, activity: event.target.value } : current))} /></Field><Field label="Prazo"><input className={inputClass()} type="date" value={item.deadline} onChange={(event) => onChange(items.map((current) => current.id === item.id ? { ...current, deadline: event.target.value } : current))} /></Field><button type="button" aria-label="Excluir tarefa" onClick={() => onChange(items.filter((current) => current.id !== item.id))} className="self-end rounded-lg border border-rose-300/50 p-3 text-rose-100"><Trash2 className="h-5 w-5" /></button></div></div>)}</div>
  </div>;
}

function MatrixSection({
  title,
  fields,
  footer,
  nextLabel,
  onNext
}: {
  title: string;
  fields: string[];
  footer: string;
  nextLabel?: string;
  onNext?: () => void;
}) {
  const storageKey = title.toLowerCase().includes('swot') ? 'matrixSwot' : 'matrixLossGain';
  const [values, setValues] = useState<Record<string, string>>(() => readStoredValue(storageKey, Object.fromEntries(fields.map((field) => [field, '']))));
  const [showResult, setShowResult] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const isSwot = title.toLowerCase().includes('swot');
  const filledCount = Object.values(values).filter((value) => value.trim()).length;
  const reportTitle = isSwot ? 'Planejamento estratégico de estudo' : 'Plano de compromisso e decisão';
  const actionPlan = isSwot
    ? [
        { title: 'Como usar suas forças', text: 'Transforme cada força em uma regra de rotina. Se você tem disciplina pela manhã, coloque a disciplina crítica no primeiro bloco do dia.' },
        { title: 'Como reduzir fraquezas', text: 'Escolha uma fraqueza por semana, defina uma ação mínima e acompanhe no Estudar Agora. Fraqueza sem métrica vira sensação; com métrica vira plano.' },
        { title: 'Como aproveitar oportunidades', text: 'Converta oportunidades em blocos concretos: prova, mentorias, simulados, revisão e materiais devem entrar no Plano de Estudos.' },
        { title: 'Como se proteger das ameaças', text: 'Crie barreiras antes do problema aparecer: horários protegidos, revisão espaçada, sono, atividade física e limite para distrações.' }
      ]
    : [
        { title: 'Ganhos que puxam você', text: 'Releia os ganhos antes de estudar. Eles funcionam como combustível emocional para manter constância quando a vontade oscila.' },
        { title: 'Perdas que exigem maturidade', text: 'Toda aprovação cobra preço: tempo, renúncias e disciplina. Planeje essas perdas para que elas não virem sabotagem.' },
        { title: 'Custo de não agir', text: 'Use o que você perde se não for aprovado como alerta, sem culpa. O objetivo é clareza, não pressão excessiva.' },
        { title: 'Compromisso prático', text: 'Escolha uma meta semanal simples, mensurável e possível. Depois registre no Estudar Agora para fechar o ciclo de responsabilidade.' }
      ];
  const actionStorageKey = isSwot ? 'swotActionPlan' : 'lossGainActionPlan';
  const [improvementPlan, setImprovementPlan] = useState<SelfActionPlanItem[]>(() => readStoredValue(actionStorageKey, actionPlan.slice(0, 3).map((item, index) => ({ id: crypto.randomUUID(), task: item.title, activity: item.text, deadline: dateToIso(addDays(new Date(), (index + 1) * 7)), completed: false }))));

  useEffect(() => {
    writeStored(storageKey, values);
  }, [storageKey, values]);
  useEffect(() => {
    writeStored(actionStorageKey, improvementPlan);
  }, [actionStorageKey, improvementPlan]);

  function createMatrixPdf() {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 16;
    const topY = 39;
    const bottomLimit = pageHeight - 24;
    let pageNumber = 1;
    let y = topY;
    const sections = fields.map((field) => ({
      title: field,
      text: values[field]?.trim() || 'Campo ainda não preenchido.'
    }));
    const meta = {
      title: isSwot ? 'SWOT / FOFA' : 'Matriz de Perdas e Ganhos',
      subtitle: reportTitle,
      fileName: `${isSwot ? 'swot' : 'perdas-ganhos'}-francisneyliberato-estudante.pdf`
    };

    const ensureSpace = (needed: number) => {
      if (y + needed <= bottomLimit) return;
      drawPdfBranding(pdf, pageNumber, pageNumber, meta);
      pdf.addPage();
      pageNumber += 1;
      y = topY;
    };

    const writeParagraph = (text: string, x: number, width: number, fontSize = 9, lineHeight = 4.6) => {
      pdf.setFontSize(fontSize);
      const lines = pdf.splitTextToSize(text, width);
      lines.forEach((line: string) => {
        ensureSpace(lineHeight + 2);
        pdf.text(line, x, y);
        y += lineHeight;
      });
    };

    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(17);
    pdf.text(reportTitle, marginX, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(203, 213, 225);
    writeParagraph(`Relatório profissional para transformar reflexão em decisão prática. Campos preenchidos: ${filledCount} de ${fields.length}.`, marginX, pageWidth - marginX * 2, 9);
    y += 4;

    sections.forEach((section) => {
      const lines = pdf.splitTextToSize(section.text, pageWidth - marginX * 2 - 8);
      const boxHeight = Math.max(24, 12 + lines.length * 4.2);
      ensureSpace(boxHeight + 5);
      pdf.setFillColor(15, 23, 42);
      pdf.setDrawColor(51, 65, 85);
      pdf.roundedRect(marginX, y, pageWidth - marginX * 2, boxHeight, 2, 2, 'FD');
      pdf.setTextColor(250, 204, 21);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text(section.title, marginX + 4, y + 7);
      pdf.setTextColor(226, 232, 240);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.8);
      pdf.text(lines, marginX + 4, y + 13);
      y += boxHeight + 5;
    });

    ensureSpace(12);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('Plano de melhoria sugerido', marginX, y);
    y += 7;

    actionPlan.forEach((item, index) => {
      ensureSpace(24);
      pdf.setTextColor(56, 189, 248);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9.5);
      pdf.text(`${index + 1}. ${item.title}`, marginX, y);
      y += 5;
      pdf.setTextColor(203, 213, 225);
      pdf.setFont('helvetica', 'normal');
      writeParagraph(item.text, marginX, pageWidth - marginX * 2, 8.7, 4.3);
      y += 2;
    });

    ensureSpace(20);
    pdf.setFillColor(8, 47, 73);
    pdf.setDrawColor(56, 189, 248);
    pdf.roundedRect(marginX, y, pageWidth - marginX * 2, 19, 2, 2, 'FD');
    pdf.setTextColor(240, 249, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Próxima ação sugerida', marginX + 4, y + 7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Registre no Plano de Estudos um bloco ligado ao ponto mais importante e confirme a execução ao final do dia.', marginX + 4, y + 13);
    y += 25;

    ensureSpace(14);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('Plano de ação', marginX, y);
    y += 7;
    improvementPlan.forEach((item, index) => {
      const content = `Tarefa: ${item.task || '-'}\nAtividade: ${item.activity || '-'}\nPrazo: ${item.deadline ? formatDateBr(item.deadline) : '-'} | Status: ${item.completed ? 'Concluída' : 'Em andamento'}`;
      const lines = pdf.splitTextToSize(content, pageWidth - marginX * 2 - 8) as string[];
      const boxHeight = Math.max(23, 10 + lines.length * 4.2);
      ensureSpace(boxHeight + 4);
      pdf.setFillColor(item.completed ? 6 : 15, item.completed ? 78 : 23, item.completed ? 59 : 42);
      pdf.setDrawColor(item.completed ? 52 : 250, item.completed ? 211 : 204, item.completed ? 153 : 21);
      pdf.roundedRect(marginX, y, pageWidth - marginX * 2, boxHeight, 2, 2, 'FD');
      pdf.setTextColor(250, 204, 21);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text(`Ação ${index + 1}`, marginX + 4, y + 6);
      pdf.setTextColor(226, 232, 240);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.text(lines, marginX + 4, y + 12);
      y += boxHeight + 4;
    });

    const totalPages = pageNumber;
    for (let page = 1; page <= totalPages; page += 1) {
      pdf.setPage(page);
      drawPdfBranding(pdf, page, totalPages, meta);
    }
    return { pdf, meta };
  }

  function downloadMatrixPdf() {
    const { pdf, meta } = createMatrixPdf();
    pdf.save(meta.fileName);
  }

  async function shareMatrixPdf() {
    try {
      const { pdf, meta } = createMatrixPdf();
      const file = new File([pdf.output('blob')], meta.fileName, { type: 'application/pdf' });
      const text = `Segue o PDF em anexo e o link da plataforma: ${window.location.href}`;
      const nav = navigator as Navigator & {
        canShare?: (data: { files?: File[] }) => boolean;
        share?: (data: { title?: string; text?: string; url?: string; files?: File[] }) => Promise<void>;
      };
      if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
        await nav.share({ title: meta.title, text, url: window.location.href, files: [file] });
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      pdf.save(meta.fileName);
    } catch {
      window.alert('Não foi possível abrir o compartilhamento agora. Baixe o PDF e envie pelo WhatsApp, e-mail ou aplicativo desejado.');
    }
  }

  return (
    <section className="glass rounded-lg p-5">
      <h2 className="mb-4 text-2xl font-black text-white">{title}</h2>
      <ToolIntro
        title={isSwot ? 'Como preencher a SWOT / FOFA' : 'Como preencher a Matriz de Perdas e Ganhos'}
        text={
          isSwot
            ? 'Use esta ferramenta para transformar autoconhecimento em estratégia de estudo. Forças e Fraquezas são fatores internos: dependem de você, da sua rotina, método, disciplina, energia e comportamento. Oportunidades e Ameaças são fatores externos: envolvem edital, calendário, concorrência, ambiente, recursos e condições que precisam ser aproveitadas ou controladas.'
            : 'Use esta matriz para tomar consciência das consequências da aprovação e da não aprovação. Ela ajuda a sustentar a disciplina quando a motivação oscila.'
        }
        examples={
          isSwot
            ? [
                'Força interna: resolvo questões com boa velocidade.',
                'Fraqueza interna: adio redação, revisão ou simulados.',
                'Oportunidade/Ameaça externa: edital próximo, concorrência alta, curso disponível ou tempo reduzido.'
              ]
            : [
                'Ganho com aprovação: estabilidade, carreira, bolsa, vaga ou certificação.',
                'Perda com aprovação: menos lazer temporário e mais responsabilidade.',
                'Perda sem aprovação: adiar metas, repetir preparação ou perder prazo importante.'
              ]
        }
      />
      <div className="grid gap-4 md:grid-cols-2">
        {fields.map((field) => (
          <Field key={field} label={field}>
            <textarea
              className={`${inputClass()} min-h-40`}
              value={values[field] || ''}
              onChange={(event) => setValues({ ...values, [field]: event.target.value })}
            />
          </Field>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4 text-slate-200">{footer}</div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button onClick={() => setShowResult(true)}><Sparkles className="h-5 w-5" /> Resultado</Button>
        {showResult && <Button tone="quiet" onClick={downloadMatrixPdf}><Download className="h-5 w-5" /> Baixar PDF</Button>}
        {showResult && <Button tone="quiet" onClick={shareMatrixPdf}><Send className="h-5 w-5" /> Compartilhar PDF</Button>}
        {onNext && nextLabel && <Button tone="quiet" onClick={onNext}><ArrowRight className="h-5 w-5" /> {nextLabel}</Button>}
      </div>
      {showResult && (
        <div ref={reportRef} className="print-card mt-5 rounded-lg border border-slate-700 bg-slate-950/70 p-5">
          <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">{title}</p>
          <h3 className="mt-1 text-3xl font-black text-white">{reportTitle}</h3>
          <p className="mt-3 text-slate-300">
            Foram preenchidos {filledCount} de {fields.length} campos. O objetivo deste relatório é transformar reflexão em decisões práticas que fortaleçam seu desempenho nos estudos.
          </p>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {fields.map((field) => (
              <div key={field} className="rounded-lg border border-slate-700 bg-slate-900/70 p-4">
                <h4 className="font-black text-white">{field}</h4>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-300">{values[field]?.trim() || 'Campo ainda não preenchido.'}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {actionPlan.map((item) => (
              <div key={item.title} className="rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-4">
                <h4 className="font-black text-yellow-100">{item.title}</h4>
                <p className="mt-2 text-sm leading-6 text-yellow-50">{item.text}</p>
              </div>
            ))}
          </div>
          <ActionPlanEditor items={improvementPlan} onChange={setImprovementPlan} />
          <div className="mt-5 rounded-lg border border-cyan-300/30 bg-cyan-300/10 p-4 text-slate-100">
            <strong>Próxima ação sugerida:</strong> registre no Plano de Estudos um bloco de estudo ligado ao ponto mais importante deste relatório e confirme a execução ao final do dia.
          </div>
        </div>
      )}
    </section>
  );
}

function Institutional({ icon: Icon, title, text, button, href }: { icon: typeof Home; title: string; text: string; button: string; href: string }) {
  const isBooks = title === 'Livros';
  const cards = isBooks
    ? [
        'Desenvolvimento pessoal',
        'Liderança e comunicação',
        'Concursos e alta performance',
        'Fé, propósito e crescimento'
      ]
    : [
        'Auditor do TCE-MT',
        'Professor e palestrante',
        'Escritor e mentor',
        'Formação multidisciplinar'
      ];
  return (
    <section className="grid gap-5">
      <div className="glass rounded-lg p-6">
        <div className="grid gap-5 md:grid-cols-[80px_1fr] md:items-center">
          <div className="grid h-20 w-20 place-items-center rounded-lg bg-yellow-300 text-slate-950">
            <Icon className="h-10 w-10" />
          </div>
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-yellow-200">Francisney Liberato</p>
            <h2 className="mt-1 text-4xl font-black text-white">{title}</h2>
            <p className="mt-3 max-w-3xl text-lg leading-8 text-slate-300">{text}</p>
            <a className="button-like focus-ring mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-yellow-300 px-4 py-3 font-bold text-slate-950" href={href} target="_blank" rel="noreferrer">
              <BookOpen className="h-5 w-5" /> {button}
            </a>
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card} className="glass rounded-lg p-4">
            <h3 className="font-black text-white">{card}</h3>
            <p className="mt-2 text-sm text-slate-400">Conteúdo para continuar a jornada depois do diagnóstico.</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function normalizeSearchText(value: string) {
  return value
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function StudyTechniquesSection({ onGoToDiagnosis }: { onGoToDiagnosis: () => void }) {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const firstResultRef = useRef<HTMLDivElement | null>(null);
  const lastSearchRef = useRef('');

  const normalizedQuery = normalizeSearchText(query.trim());

  const filteredTechniques = useMemo(() => studyTechniques.filter((technique) => {
    const matchesCategory = !activeCategory || technique.category === activeCategory;
    if (!matchesCategory) return false;
    if (!normalizedQuery) return true;
    const haystack = normalizeSearchText(
      [technique.title, technique.category, technique.summary, technique.howTo.join(' '), technique.source, technique.tags.join(' ')].join(' ')
    );
    return haystack.includes(normalizedQuery);
  }), [normalizedQuery, activeCategory]);

  useEffect(() => {
    const firstResult = filteredTechniques[0];
    const searchKey = `${normalizedQuery}:${activeCategory ?? 'all'}:${firstResult?.id ?? ''}`;

    if (normalizedQuery.length < 2 || !firstResult) {
      lastSearchRef.current = '';
      return;
    }

    const timeout = window.setTimeout(() => {
      if (lastSearchRef.current === searchKey) return;
      lastSearchRef.current = searchKey;
      firstResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [activeCategory, filteredTechniques, normalizedQuery]);

  return (
    <section className="grid gap-5">
      <div className="glass rounded-lg p-5 sm:p-6">
        <p className="text-sm font-black uppercase tracking-wider text-yellow-200">Base científica de estudo</p>
        <h2 className="mt-1 text-3xl font-black text-white sm:text-4xl">Técnicas de Estudo</h2>
        <p className="mt-3 max-w-4xl text-base leading-7 text-slate-300 sm:text-lg sm:leading-8">
          Pesquise entre técnicas de estudo baseadas em neurociência, psicologia cognitiva, coaching e nos livros e pesquisas de Francisney Liberato. Cada item traz o resumo e o passo a passo prático.
        </p>
      </div>

      <div className="glass rounded-lg p-4 sm:p-5">
        <div className="mb-3 flex justify-end">
          <Button tone="quiet" onClick={onGoToDiagnosis}><Brain className="h-5 w-5" /> Ir para Meu Diagnóstico</Button>
        </div>
        <label className="relative block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cyan-200" />
          <input
            className={`${inputClass()} pl-12 pr-12`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Pesquise por técnica, palavra, método ou forma de estudar: ex. recuperação ativa, sono, pomodoro"
            aria-label="Pesquisar técnica de estudo"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Limpar pesquisa"
              className="focus-ring absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </label>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setActiveCategory(null); setQuery(''); }}
            className={`focus-ring rounded-lg border px-3 py-2 text-xs font-bold transition ${!activeCategory ? 'border-yellow-300 bg-yellow-300 text-slate-950' : 'border-slate-700 bg-slate-950/45 text-slate-200 hover:border-cyan-300/70'}`}
          >
            Todas ({studyTechniques.length})
          </button>
          {studyTechniqueCategories.map((category) => {
            const count = studyTechniques.filter((technique) => technique.category === category).length;
            return (
              <button
                key={category}
                type="button"
                onClick={() => { setActiveCategory(activeCategory === category ? null : category); setQuery(''); window.setTimeout(() => document.getElementById('tecnicas-conteudo')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); }}
                className={`focus-ring rounded-lg border px-3 py-2 text-xs font-bold transition ${activeCategory === category ? 'border-yellow-300 bg-yellow-300 text-slate-950' : 'border-slate-700 bg-slate-950/45 text-slate-200 hover:border-cyan-300/70'}`}
              >
                {category} ({count})
              </button>
            );
          })}
        </div>
      </div>

      <div id="tecnicas-conteudo" className="grid gap-4">
        {filteredTechniques.length === 0 && (
          <div className="glass rounded-lg p-6 text-center text-slate-300">Nenhuma técnica encontrada para esta pesquisa. Tente outra palavra-chave.</div>
        )}
        {filteredTechniques.map((technique, index) => (
          <div key={technique.id} ref={index === 0 ? firstResultRef : undefined} className="glass rounded-lg p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="rounded-lg border border-cyan-300/40 bg-cyan-300/10 px-2 py-1 text-xs font-bold uppercase tracking-wide text-cyan-100">{technique.category}</span>
            </div>
            <h3 className="mt-2 text-xl font-black text-white">{technique.title}</h3>
            <p className="mt-2 leading-6 text-slate-300">{technique.summary}</p>
            <div className="mt-3 rounded-lg border border-slate-700 bg-slate-950/45 p-4">
              <p className="text-sm font-bold uppercase tracking-wide text-yellow-200">Como aplicar</p>
              <ul className="mt-2 grid gap-1.5 text-sm leading-6 text-slate-200">
                {technique.howTo.map((step, index) => (
                  <li key={index} className="flex gap-2"><span className="text-cyan-300">•</span><span>{step}</span></li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

interface PlatformDashboardData {
  period: { key: '7' | '30' | '90' | 'all'; label: string; start_date: string | null; participant_count: number };
  overview: {
    total_hours: number;
    total_questions: number;
    total_correct: number;
    average_accuracy: number;
    total_simulations: number;
    total_essays: number;
    total_reviews: number;
    longest_streak_days: number;
    average_week_completion: number;
    weeks_tracked: number;
  };
  top_subjects: Array<{ name: string; hours: number }>;
  activity_hours: Array<{ name: string; hours: number }>;
  subject_accuracy: Array<{ name: string; accuracy: number; questions: number }>;
  weekly_trend: Array<{ date: string; hours: number }>;
  ranking: Array<{ rank: number; score: number; average_completion: number; hours_invested: number; weeks_tracked: number }>;
}

function PlatformDashboardSection() {
  const [data, setData] = useState<PlatformDashboardData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [period, setPeriod] = useState<'7' | '30' | '90' | 'all'>('30');

  useEffect(() => {
    setLoadError(false);
    api<PlatformDashboardData>(`/api/platform-dashboard?period=${period}`)
      .then(setData)
      .catch(() => setLoadError(true));
  }, [period]);

  const overview = data?.overview;

  return (
    <section className="grid gap-5">
      <div className="glass rounded-lg p-5 sm:p-6">
        <p className="text-sm font-black uppercase tracking-wider text-yellow-200">Visão consolidada da plataforma</p>
        <h2 className="mt-1 text-3xl font-black text-white sm:text-4xl">Painel da Comunidade</h2>
        <p className="mt-3 max-w-4xl text-slate-300">Este painel reúne dados e informações consolidadas de quem utiliza a plataforma, apresentando tendências de estudo, execução e desempenho.</p>
        <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-400">Os indicadores seguem a mesma lógica do Meu Desempenho: planejamento confirmado, horas efetivamente registradas, questões, acertos, revisões, simulados e redações ou provas dissertativas, sempre sem identificar participantes.</p>
        <div className="mt-5 flex flex-wrap gap-2" aria-label="Período analisado">
          {([
            ['7', '7 dias'], ['30', '30 dias'], ['90', '90 dias'], ['all', 'Todo o período']
          ] as const).map(([value, label]) => (
            <Button key={value} type="button" tone={period === value ? 'primary' : 'quiet'} onClick={() => setPeriod(value)} aria-pressed={period === value}>{label}</Button>
          ))}
        </div>
        {data && <p className="mt-4 rounded-lg border border-cyan-300/35 bg-cyan-300/10 px-4 py-3 text-sm font-semibold leading-6 text-cyan-50">Dados consolidados de <strong>{data.period.participant_count} participante{data.period.participant_count === 1 ? '' : 's'}</strong> com atividade registrada em <strong>{data.period.label.toLowerCase()}</strong>.</p>}
      </div>

      {loadError && (
        <div className="glass rounded-lg p-6 text-center text-slate-300">Não foi possível carregar o dashboard geral agora. Tente novamente em instantes.</div>
      )}

      {overview && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Horas de estudo registradas" value={`${overview.total_hours}h`} icon={Clock3} color="#22d3ee" />
            <StatCard label="Questões resolvidas" value={overview.total_questions} icon={CheckCircle2} color="#34d399" />
            <StatCard label="Percentual médio de acertos" value={`${overview.average_accuracy}%`} icon={Award} color="#facc15" />
            <StatCard label="Maior sequência de estudo" value={pluralize(Number(overview.longest_streak_days || 0), 'dia')} icon={Flame} color="#fb7185" />
            <StatCard label="Simulados realizados" value={overview.total_simulations} icon={FileText} color="#a78bfa" />
            <StatCard label="Redações produzidas" value={overview.total_essays} icon={BookOpen} color="#38bdf8" />
            <StatCard label="Revisões ativas concluídas" value={overview.total_reviews} icon={RefreshCw} color="#10b981" />
            <StatCard label="Cumprimento médio da semana" value={`${overview.average_week_completion}%`} icon={BarChart3} color="#f59e0b" />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="glass rounded-lg p-4">
              <h3 className="mb-4 text-xl font-black text-white">Evolução de horas estudadas</h3>
              <p className="-mt-2 mb-4 text-sm text-slate-400">Período: {data.period.label}</p>
              {data.weekly_trend.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.weekly_trend.map((item) => ({ ...item, date: formatFullDateBr(item.date) }))}>
                    <CartesianGrid stroke="#1e293b" />
                    <XAxis dataKey="date" stroke="#cbd5e1" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#cbd5e1" />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(34, 211, 238, 0.10)' }} />
                    <Line type="monotone" dataKey="hours" stroke="#22d3ee" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="p-6 text-center text-slate-300">Ainda não há execuções suficientes para exibir a evolução.</p>
              )}
              <ChartSummary>{hoursTrendSummary(data.weekly_trend.map((item) => ({ ...item, date: formatFullDateBr(item.date) })))}</ChartSummary>
            </div>
            <div className="glass rounded-lg p-4">
              <h3 className="mb-4 text-xl font-black text-white">Disciplinas e Simulado mais estudados</h3>
              <p className="-mt-2 mb-4 text-sm text-slate-400">Período: {data.period.label}</p>
              {data.top_subjects.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.top_subjects}>
                    <CartesianGrid stroke="#1e293b" />
                    <XAxis dataKey="name" stroke="#cbd5e1" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#cbd5e1" />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(52, 211, 153, 0.10)' }} />
                    <Bar dataKey="hours" fill="#34d399" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="p-6 text-center text-slate-300">Ainda não há horas suficientes registradas por disciplina.</p>
              )}
              <ChartSummary>{leadingHoursSummary(data.top_subjects)}</ChartSummary>
            </div>
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            <div className="glass rounded-lg p-4"><h3 className="mb-1 text-xl font-black text-white">Horas por tipo de atividade</h3><p className="mb-4 text-sm text-slate-300">Período: {data.period.label}</p>{data.activity_hours.length ? <ResponsiveContainer width="100%" height={280}><BarChart data={data.activity_hours}><CartesianGrid stroke="#1e293b" /><XAxis dataKey="name" stroke="#e2e8f0" tick={{ fontSize: 12 }} /><YAxis stroke="#e2e8f0" /><Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(167, 139, 250, 0.10)' }} /><Bar dataKey="hours" fill="#a78bfa" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer> : <p className="p-6 text-center text-slate-300">Os tipos de atividade aparecerão quando os estudantes registrarem execuções.</p>}<ChartSummary>{leadingHoursSummary(data.activity_hours, 'atividade')}</ChartSummary></div>
            <div className="glass rounded-lg p-4"><h3 className="mb-1 text-xl font-black text-white">Acertos por disciplina</h3><p className="mb-4 text-sm text-slate-300">Período: {data.period.label}</p>{data.subject_accuracy.length ? <ResponsiveContainer width="100%" height={280}><BarChart data={data.subject_accuracy}><CartesianGrid stroke="#1e293b" /><XAxis dataKey="name" stroke="#e2e8f0" tick={{ fontSize: 12 }} /><YAxis stroke="#e2e8f0" domain={[0, 100]} /><Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(250, 204, 21, 0.10)' }} /><Bar dataKey="accuracy" fill="#facc15" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer> : <p className="p-6 text-center text-slate-300">O percentual de acertos surgirá após o registro de questões e acertos.</p>}<ChartSummary>{accuracySummary(data.subject_accuracy.map((item) => ({ name: item.name, acerto: item.accuracy })))}</ChartSummary></div>
          </div>

          <div className="glass rounded-lg p-5">
            <div className="flex items-center gap-3">
              <Trophy className="h-6 w-6 text-yellow-300" />
              <h3 className="text-xl font-black text-white">Ranking de melhores desempenhos</h3>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              O ranking considera cumprimento do plano, regularidade semanal, evolução individual, questões e revisões, além de horas efetivamente registradas. Os nomes não são exibidos.
            </p>
            <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2 xl:grid-cols-5" aria-label="Metodologia do ranking">
              {[
                ['Cumprimento do plano', '35%'], ['Consistência', '25%'], ['Evolução individual', '20%'], ['Questões e revisões', '10%'], ['Horas efetivas', '10%']
              ].map(([label, weight]) => <div key={label} className="rounded-lg border border-slate-700 bg-slate-950/45 px-3 py-2"><span className="block text-slate-400">{label}</span><strong className="text-cyan-100">{weight}</strong></div>)}
            </div>
            <p className="mt-3 text-sm text-slate-400">Período: {data.period.label}.</p>
            {data.ranking.length ? (
              <div className="mt-4 grid gap-2">
                {data.ranking.map((entry) => (
                  <div key={entry.rank} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-950/45 p-4">
                    <div className="flex items-center gap-3">
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full font-black ${entry.rank === 1 ? 'bg-yellow-300 text-slate-950' : entry.rank === 2 ? 'bg-slate-300 text-slate-950' : entry.rank === 3 ? 'bg-amber-600 text-slate-950' : 'bg-slate-800 text-slate-200'}`}>
                        {entry.rank}º
                      </span>
                      <div>
                        <p className="font-black text-white">Desempenho #{entry.rank}</p>
                        <p className="text-sm text-slate-400">{pluralize(Number(entry.weeks_tracked || 0), 'semana')} {Number(entry.weeks_tracked || 0) === 1 ? 'acompanhada' : 'acompanhadas'}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div><p className="text-slate-400">Cumprimento médio</p><p className="font-black text-cyan-100">{entry.average_completion}%</p></div>
                      <div><p className="text-slate-400">Horas investidas</p><p className="font-black text-emerald-200">{entry.hours_invested}h</p></div>
                      <div><p className="text-slate-400">Índice composto</p><p className="font-black text-yellow-100">{entry.score} pontos</p></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-lg border border-slate-700 bg-slate-950/45 p-4 text-sm text-slate-300">O ranking é formado à medida que as semanas de estudo são confirmadas e executadas no Plano de Estudos. Continue registrando sua rotina para aparecer aqui.</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function EditalSection() {
  const [items, setItems] = useState<Array<{ id: string; subject_name: string; content: string; status: string; completion_percent: number }>>([]);
  const [form, setForm] = useState({ subject_name: 'Direito Constitucional', content: '', subcontent: '', status: 'em andamento' });

  useEffect(() => {
    api<{ results: Array<{ id: string; subject_name: string; content: string; status: string; completion_percent: number }> }>('/api/edital')
      .then((payload) => setItems(payload.results || []))
      .catch(() => setItems([]));
  }, []);

  async function addItem() {
    try {
      const response = await postJson<{ id: string; completion_percent: number }>('/api/edital', form);
      setItems([{ id: response.id, ...form, completion_percent: response.completion_percent }, ...items]);
      setForm({ ...form, content: '', subcontent: '' });
    } catch {
      setItems([{ id: `local-${Date.now()}`, ...form, completion_percent: 0 }, ...items]);
    }
  }

  return (
    <section className="grid gap-6">
      <div className="glass rounded-lg p-5">
        <h2 className="mb-4 text-2xl font-black text-white">Edital esquematizado</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Disciplina"><input className={inputClass()} value={form.subject_name} onChange={(event) => setForm({ ...form, subject_name: event.target.value })} /></Field>
          <Field label="Status"><select className={inputClass()} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option>não iniciado</option><option>em andamento</option><option>concluído</option><option>revisar</option></select></Field>
          <Field label="Conteúdo" className="md:col-span-2"><input className={inputClass()} value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} /></Field>
          <Field label="Subconteúdo" className="md:col-span-2"><input className={inputClass()} value={form.subcontent} onChange={(event) => setForm({ ...form, subcontent: event.target.value })} /></Field>
          <Button className="md:col-span-2" onClick={addItem}><Plus className="h-5 w-5" /> Adicionar item</Button>
        </div>
      </div>
      <div className="grid gap-3">
        {items.map((item) => (
          <div key={item.id} className="glass flex items-center justify-between gap-4 rounded-lg p-4">
            <div>
              <strong className="text-white">{item.subject_name}</strong>
              <p className="text-sm text-slate-300">{item.content}</p>
              <p className="text-sm text-cyan-100">{item.status} | {item.completion_percent}%</p>
            </div>
            <Trash2 className="h-5 w-5 text-slate-500" />
          </div>
        ))}
      </div>
    </section>
  );
}

